import React, { useState, useEffect, useMemo, useRef } from 'react';
import { api } from '../utils/api';
import { exportToCSV, prepareFinancialDataForExport, prepareChildrenDataForExport, prepareBookingsDataForExport } from '../utils/export';
import { useMobileNav } from '../hooks/useMobileNav';
import RowActions from '../components/RowActions';
import WorkspaceLinkCard from '../components/WorkspaceLinkCard';
import { InviteUsersCard } from '../components/InviteUsersCard';
import { PayrollCenter } from '../components/PayrollCenter';
import { EventsPage } from '../components/EventsPage';
import TimesheetView from '../components/TimesheetView';
import { WelcomeHeroCard, GettingStartedChecklist, GetStartedSidebarPill } from '../components/GetStarted';
import AvailabilityEditor from '../components/AvailabilityEditor';
import LeaveManager from '../components/LeaveManager';
import ShiftRequestsInbox from '../components/ShiftRequestsInbox';
import { ChildCareFlags } from '../components/ChildCareFlags';
import { ComplianceAdminPanel } from '../components/ComplianceAdminPanel';
import { ComplianceSummaryCard } from '../components/ComplianceSummaryCard';
import { UserDetailDrawer } from '../components/UserDetailDrawer';
import { ChildProfileDrawer } from '../components/ChildProfileDrawer';
import { BillingTab } from '../components/BillingTab';
import { StaffIDCardModal } from '../components/StaffIDCardModal';
import { StaffProfileExtras } from '../components/StaffProfileExtras';
import { UsageBanner } from '../components/UsageBanner';
import { TrialCountdownBanner } from '../components/TrialCountdownBanner';
import { UpgradeModal, extractPlanLimitDetail } from '../components/UpgradeModal';
import { DailyRoster } from '../components/DailyRoster';
import { EnrollmentPlanManager } from '../components/EnrollmentPlanManager';
import { MealsManager } from '../components/MealsManager';
import { MessageComposer } from '../components/MessageComposer';
import { MessagesInbox } from '../components/MessagesInbox';
import { DeleteChildDialog } from '../components/DeleteChildDialog';
import { RoleTierModal } from '../components/RoleTierModal';
import { DriverAssignmentsDrawer } from '../components/DriverAssignmentsDrawer';
import { AccountCleanupCard } from '../components/AccountCleanupCard';
import { PricingDefaultsCard } from '../components/PricingDefaultsCard';
import { EmailSettingsCard } from '../components/EmailSettingsCard';
import { PayrollSettingsCard } from '../components/PayrollSettingsCard';
import { BankDetailsCard } from '../components/BankDetailsCard';
import { SiblingDiscountCard } from '../components/SiblingDiscountCard';
import { PendingPaymentsSection } from '../components/PendingPaymentsSection';
import { PaymentIntentsSection } from '../components/PaymentIntentsSection';
import { InvoicesSection } from '../components/InvoicesSection';
import { RecurringSchedulesSection, BulkInvoiceRunButton, InvoiceBrandingStudio } from '../components/InvoiceRecurringAndTools';
import { ChaseListCard } from '../components/ChaseListCard';

// Small inline component: grace-period auto-lock toggle for Org Settings.
// Kept inline (not a separate file) because it's ~40 lines and tightly
// coupled to the `organization` prop we already have in this page.
function GracePeriodCard({ organization, onSaved }) {
  const s0 = organization?.settings || {};
  const [enabled, setEnabled] = React.useState(!!s0.grace_period_enabled);
  const [days, setDays] = React.useState(Number(s0.grace_period_days || 14));
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => {
    setEnabled(!!(organization?.settings || {}).grace_period_enabled);
    setDays(Number((organization?.settings || {}).grace_period_days || 14));
  }, [organization]);
  const save = async () => {
    setSaving(true);
    try {
      const updated = await (await import('../utils/api')).api.updateMyOrganization({
        settings: { ...(organization?.settings || {}), grace_period_enabled: enabled, grace_period_days: Math.max(1, Math.min(90, Number(days) || 14)) },
      });
      onSaved && onSaved(updated);
      const toast = (await import('sonner')).toast;
      toast.success(enabled ? `Grace period ON — auto-lock at ${days} days.` : 'Grace period disabled.');
    } catch (e) {
      const toast = (await import('sonner')).toast;
      toast.error(e?.message || 'Save failed');
    } finally { setSaving(false); }
  };
  return (
    <div className="card" data-testid="grace-period-card" style={{ marginTop: '1rem' }}>
      <h3 style={{ margin: '0 0 0.15rem' }}>🔒 Grace period auto-lock</h3>
      <p style={{ margin: '0 0 0.75rem', color: 'hsl(var(--muted-foreground))', fontSize: '0.85rem' }}>
        When ON, any parent with an invoice more than N days overdue is temporarily blocked from creating new bookings until the invoice is settled. Admins can still book on-behalf-of.
      </p>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem' }}>
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="grace-enabled-toggle" style={{ transform: 'scale(1.15)' }} />
        <strong>Enable auto-lock</strong>
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <label htmlFor="grace-days" style={{ fontSize: '0.85rem' }}>Lock after</label>
        <input id="grace-days" type="number" min="1" max="90" value={days} onChange={(e) => setDays(e.target.value)} disabled={!enabled} data-testid="grace-days-input" style={{ width: 80, padding: '0.3rem 0.5rem', textAlign: 'right' }} />
        <span style={{ fontSize: '0.85rem' }}>days overdue</span>
      </div>
      <button type="button" className="btn btn-primary" onClick={save} disabled={saving} data-testid="grace-save">{saving ? 'Saving…' : '💾 Save grace period'}</button>
    </div>
  );
}

import { WelcomePageEditor } from '../components/WelcomePageEditor';
import { AGE_GROUP_OPTIONS, formatAgeGroupLabel } from '../constants/ageGroups';
import { PoliciesLibrary } from '../components/PoliciesLibrary';
import { EmailPreferencesCard } from '../components/EmailPreferencesCard';
import { MomentComments } from '../components/MomentComments';
import {
  BookingsFilterBar,
  DEFAULT_BOOKING_FILTERS,
  applyBookingFilters,
  readBookingFiltersFromURL,
  writeBookingFiltersToURL,
} from '../components/BookingsFilterBar';
import { ClockInOutFAB } from '../components/ClockInOutFAB';
import { PaymentDetailsCard } from '../components/PaymentDetailsCard';
import { PayslipDialog } from '../components/PayslipDialog';
import { RepriceBookingsDialog } from '../components/RepriceBookingsDialog';
import TermCalendarManager from '../components/TermCalendarManager';
import { toast } from 'sonner';
import { ageAndYearLabel, ukYearGroup, ageLabel } from '../utils/childAge';

// ---------------------------------------------------------------------------
// Admin "book on behalf of parent" — payment choice helpers
// ---------------------------------------------------------------------------
// Extracted to module scope so both single/batch and bulk submit paths use
// the exact same payload shape and validation rules. Server-side re-
// validation is authoritative; the client-side check just spares a
// round-trip on the obvious "reference empty" case.
function _buildAdminPaymentChoice(bookingFlow) {
  const method = bookingFlow.paymentMethod || 'send_link';
  const choice = { method };
  if (method === 'mark_paid') {
    choice.paid_method = bookingFlow.paidMethod || 'cash';
    choice.paid_reference = (bookingFlow.paidReference || '').trim();
    choice.paid_note = (bookingFlow.paidNote || '').trim();
  }
  return choice;
}

function _validateMarkPaidChoice(choice) {
  if (choice.method !== 'mark_paid') return null;
  if (!choice.paid_reference) {
    return 'Please enter a reference (receipt number, terminal txn id, etc.) before marking as paid.';
  }
  if ((choice.paid_note || '').length < 5) {
    return 'Please add a short note (at least 5 characters) explaining how the parent paid.';
  }
  return null;
}

/**
 * Per-manifest driver + escort assignment widget.
 *
 * Renders inline on each Transport Manifest card. Allows admins /
 * deputies to:
 *   - assign or change the driver on a single manifest
 *   - optionally promote that assignment to the org-wide default for
 *     that route type (persisted via `route_driver_preferences`)
 *   - add or remove escorts (second adults / safeguarding crew)
 *
 * The driver dropdown lists every user in the org with either
 * `role=driver` OR the `can_do_school_runs` capability flag set —
 * mirroring real centre operations where qualified staff cover the
 * school-run rota alongside primary drivers. Non-primary drivers are
 * badged "Staff (rota)" so admins know who's who.
 *
 * Manifests already `in_progress` or `completed` render the assigned
 * driver (if any) read-only — the backend rejects mutations in those
 * states anyway, so we mirror that constraint in the UI.
 */
function ManifestDriverAssignment({ manifest, eligibleDrivers, allStaff, onUpdated }) {
  const [selectedDriverId, setSelectedDriverId] = useState(manifest.driver_id || '');
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  // Escort picker is a controlled multi-select that mirrors the
  // current escort_ids on the manifest. Add/remove pills below.
  const initialEscortIds = useMemo(
    () => Array.isArray(manifest.escort_ids) ? manifest.escort_ids : [],
    [manifest.escort_ids]
  );
  const [escortIds, setEscortIds] = useState(initialEscortIds);
  const [escortToAdd, setEscortToAdd] = useState('');
  const [savingEscorts, setSavingEscorts] = useState(false);

  useEffect(() => {
    setSelectedDriverId(manifest.driver_id || '');
  }, [manifest.driver_id]);

  useEffect(() => {
    setEscortIds(initialEscortIds);
  }, [initialEscortIds]);

  const manifestId = manifest._id || manifest.id;
  const locked = manifest.status === 'in_progress' || manifest.status === 'completed';
  const drivers = eligibleDrivers || [];
  const currentDriverName = manifest.driver_name
    || (drivers.find(d => d._id === manifest.driver_id)?.full_name)
    || null;
  const currentDriverIsRota = (() => {
    const d = drivers.find(x => x._id === manifest.driver_id);
    return d ? !d.is_primary_driver : false;
  })();

  // Eligible escorts = any non-parent in the org (excluding the
  // currently-selected driver and anyone already in escortIds).
  const escortCandidates = (allStaff || []).filter(u => {
    const role = (u.role || '').toLowerCase();
    if (role === 'parent') return false;
    if (u._id === selectedDriverId) return false;
    if (u._id === manifest.driver_id) return false;
    if (escortIds.includes(u._id)) return false;
    return true;
  });

  // Hydrate names for current escorts using either the inline
  // `escort_details` from the backend (preferred) or falling back to
  // the all-staff list.
  const resolvedEscorts = escortIds.map(eid => {
    const fromBackend = (manifest.escort_details || []).find(e => e._id === eid);
    if (fromBackend) return fromBackend;
    const fromStaff = (allStaff || []).find(u => u._id === eid);
    if (fromStaff) {
      return {
        _id: eid,
        full_name: fromStaff.full_name || fromStaff.email,
        role: fromStaff.role,
      };
    }
    return { _id: eid, full_name: eid, role: null };
  });

  const handleAssign = async () => {
    if (!selectedDriverId) {
      toast.error('Pick a driver first.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/manifests/${manifestId}/assign-driver`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            driver_id: selectedDriverId,
            set_as_default: setAsDefault,
          }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Assignment failed (${res.status})`);
      }
      const result = await res.json();
      toast.success(
        `Driver assigned: ${result.driver_name}` +
        (result.default_updated ? ' (also set as default for this route)' : '')
      );
      setSetAsDefault(false);
      if (onUpdated) await onUpdated();
    } catch (err) {
      toast.error('Could not assign driver: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Remove driver assignment from this manifest?')) return;
    setSaving(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/manifests/${manifestId}/assign-driver`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Clear failed (${res.status})`);
      }
      toast.success('Driver assignment cleared.');
      setSelectedDriverId('');
      if (onUpdated) await onUpdated();
    } catch (err) {
      toast.error('Could not clear driver: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const persistEscorts = async (nextIds) => {
    setSavingEscorts(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/manifests/${manifestId}/escorts`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ escort_ids: nextIds }),
        }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Escort update failed (${res.status})`);
      }
      const result = await res.json();
      setEscortIds(result.escort_ids || []);
      toast.success(nextIds.length > 0 ? 'Escorts updated.' : 'All escorts removed.');
      if (onUpdated) await onUpdated();
    } catch (err) {
      toast.error('Could not update escorts: ' + err.message);
    } finally {
      setSavingEscorts(false);
    }
  };

  const handleAddEscort = () => {
    if (!escortToAdd) return;
    if (escortIds.includes(escortToAdd)) return;
    const next = [...escortIds, escortToAdd];
    setEscortToAdd('');
    persistEscorts(next);
  };

  const handleRemoveEscort = (eid) => {
    const next = escortIds.filter(x => x !== eid);
    persistEscorts(next);
  };

  // Read-only display for locked manifests.
  if (locked) {
    return (
      <div
        style={{
          marginTop: '0.75rem',
          padding: '0.5rem 0.75rem',
          background: 'hsl(var(--muted))',
          borderRadius: '6px',
          fontSize: '0.875rem',
        }}
        data-testid={`manifest-driver-readonly-${manifestId}`}
      >
        <div>
          <strong>Driver:</strong>{' '}
          {currentDriverName || <span style={{ color: 'hsl(var(--muted-foreground))' }}>🚫 No driver assigned</span>}
          {currentDriverIsRota && currentDriverName && (
            <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', padding: '0.1rem 0.4rem', background: 'hsl(var(--accent))', borderRadius: '4px' }}>
              Staff (rota)
            </span>
          )}
          <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
            (locked — manifest is {manifest.status.replace('_', ' ')})
          </span>
        </div>
        {resolvedEscorts.length > 0 && (
          <div style={{ marginTop: '0.4rem' }}>
            <strong>Escorts:</strong>{' '}
            {resolvedEscorts.map(e => e.full_name).join(', ')}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: '0.75rem',
        padding: '0.75rem',
        background: 'hsl(var(--muted))',
        borderRadius: '6px',
      }}
      data-testid={`manifest-driver-assignment-${manifestId}`}
    >
      {/* DRIVER ROW */}
      <div style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>
        <strong>Driver:</strong>{' '}
        {currentDriverName ? (
          <>
            <span data-testid={`manifest-driver-name-${manifestId}`}>{currentDriverName}</span>
            {currentDriverIsRota && (
              <span
                style={{
                  marginLeft: '0.5rem',
                  fontSize: '0.7rem',
                  padding: '0.1rem 0.4rem',
                  background: 'hsl(var(--accent))',
                  borderRadius: '4px',
                }}
                title="This user is staff who is on the school-run rota."
              >
                Staff (rota)
              </span>
            )}
          </>
        ) : (
          <span
            style={{ color: 'hsl(var(--muted-foreground))' }}
            data-testid={`manifest-no-driver-${manifestId}`}
          >
            🚫 No driver assigned
          </span>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <select
          value={selectedDriverId}
          onChange={(e) => setSelectedDriverId(e.target.value)}
          disabled={saving || drivers.length === 0}
          data-testid={`manifest-driver-select-${manifestId}`}
          style={{
            padding: '0.4rem 0.6rem',
            fontSize: '0.875rem',
            borderRadius: '6px',
            border: '1px solid hsl(var(--border))',
            background: 'hsl(var(--background))',
            minWidth: '220px',
          }}
        >
          <option value="">
            {drivers.length === 0 ? 'No eligible drivers' : 'Assign driver ▾'}
          </option>
          {drivers.map((d) => (
            <option key={d._id} value={d._id}>
              {d.full_name || d.email}
              {d.is_primary_driver ? '' : ' — Staff (rota)'}
            </option>
          ))}
        </select>

        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.35rem',
            fontSize: '0.8125rem',
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          <input
            type="checkbox"
            checked={setAsDefault}
            onChange={(e) => setSetAsDefault(e.target.checked)}
            disabled={saving}
            data-testid={`manifest-default-toggle-${manifestId}`}
          />
          Default driver for this route
        </label>

        <button
          className="btn btn-primary"
          onClick={handleAssign}
          disabled={saving || !selectedDriverId || selectedDriverId === manifest.driver_id}
          data-testid={`manifest-driver-save-${manifestId}`}
          style={{ fontSize: '0.8125rem', padding: '0.4rem 0.85rem' }}
        >
          {saving ? 'Saving...' : (manifest.driver_id ? 'Reassign' : 'Assign')}
        </button>

        {manifest.driver_id && (
          <button
            className="btn btn-secondary"
            onClick={handleClear}
            disabled={saving}
            data-testid={`manifest-driver-clear-${manifestId}`}
            style={{ fontSize: '0.8125rem', padding: '0.4rem 0.85rem' }}
          >
            Clear
          </button>
        )}
      </div>

      {drivers.length === 0 && (
        <div
          style={{
            marginTop: '0.5rem',
            fontSize: '0.75rem',
            color: 'hsl(var(--muted-foreground))',
          }}
        >
          No eligible drivers. Add a Driver via Users, or tick "🚐 Can do school runs" on a
          staff member's profile to add them to the rota.
        </div>
      )}

      {/* ESCORTS ROW */}
      <div
        style={{
          marginTop: '0.85rem',
          paddingTop: '0.75rem',
          borderTop: '1px dashed hsl(var(--border))',
        }}
        data-testid={`manifest-escorts-${manifestId}`}
      >
        <div style={{ fontSize: '0.875rem', marginBottom: '0.4rem' }}>
          <strong>Escorts</strong>{' '}
          <span style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
            (second adults / safeguarding crew — optional)
          </span>
        </div>

        {resolvedEscorts.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '0.5rem' }}>
            {resolvedEscorts.map(e => (
              <span
                key={e._id}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  padding: '0.2rem 0.5rem 0.2rem 0.65rem',
                  fontSize: '0.8125rem',
                  background: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '999px',
                }}
                data-testid={`manifest-escort-pill-${manifestId}-${e._id}`}
              >
                {e.full_name}
                <button
                  type="button"
                  onClick={() => handleRemoveEscort(e._id)}
                  disabled={savingEscorts}
                  title="Remove escort"
                  style={{
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    lineHeight: 1,
                    color: 'hsl(var(--muted-foreground))',
                  }}
                  data-testid={`manifest-escort-remove-${manifestId}-${e._id}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.5rem' }}>
            No escorts on this run.
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={escortToAdd}
            onChange={(e) => setEscortToAdd(e.target.value)}
            disabled={savingEscorts || escortCandidates.length === 0}
            data-testid={`manifest-escort-select-${manifestId}`}
            style={{
              padding: '0.35rem 0.55rem',
              fontSize: '0.8125rem',
              borderRadius: '6px',
              border: '1px solid hsl(var(--border))',
              background: 'hsl(var(--background))',
              minWidth: '200px',
            }}
          >
            <option value="">
              {escortCandidates.length === 0 ? 'No more available' : 'Add escort ▾'}
            </option>
            {escortCandidates.map(u => (
              <option key={u._id} value={u._id}>
                {u.full_name || u.email}
                {u.role ? ` — ${u.role.replace('_', ' ')}` : ''}
              </option>
            ))}
          </select>

          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleAddEscort}
            disabled={savingEscorts || !escortToAdd}
            data-testid={`manifest-escort-add-${manifestId}`}
            style={{ fontSize: '0.8125rem', padding: '0.35rem 0.75rem' }}
          >
            {savingEscorts ? 'Saving...' : '+ Add'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Master Daily Transport Schedule — rota-style aggregated view across
 * every manifest for a given date.
 *
 * Hydrated from `GET /api/transport/daily-schedule` which already does
 * the join work (driver names, escort names, school stops flattened
 * into a single child list) and emits soft conflict warnings:
 *   - same driver assigned to two overlapping time windows
 *   - same child appearing on more than one route
 *
 * Renders as a sortable/filterable table with quick CSV export for the
 * morning briefing printout. Per the user's requirement, warnings are
 * advisory only ("Option A — soft warnings") — they never block the
 * assignment, just surface a ⚠️ pill so admins can verify the rota.
 */
const ROUTE_TYPE_LABEL = {
  home_to_school: '🌅 Home → School',
  club_to_school: '🌅 Centre → School',
  school_to_club: '🏫 School → Centre',
  school_to_home: '🏫 School → Home',
  club_to_home: '🌆 Centre → Home',
};

function DailyTransportSchedule({ schedule, loading, selectedDate }) {
  const [routeTypeFilter, setRouteTypeFilter] = useState('all');
  const [driverFilter, setDriverFilter] = useState('all');
  const [schoolFilter, setSchoolFilter] = useState('all');
  const [groupBy, setGroupBy] = useState('time');

  const rows = (schedule?.rows) || [];

  // Derive filter dropdown options.
  const allDrivers = useMemo(() => {
    const set = new Map();
    rows.forEach(r => {
      if (r.driver_id) set.set(r.driver_id, r.driver_name || r.driver_id);
    });
    return Array.from(set.entries()).map(([id, name]) => ({ id, name }));
  }, [rows]);
  const allSchools = useMemo(() => {
    const set = new Set();
    rows.forEach(r => (r.schools || []).forEach(s => s && set.add(s)));
    return Array.from(set).sort();
  }, [rows]);
  const allRouteTypes = useMemo(() => {
    const set = new Set();
    rows.forEach(r => r.route_type && set.add(r.route_type));
    return Array.from(set);
  }, [rows]);

  const filtered = rows.filter(r => {
    if (routeTypeFilter !== 'all' && r.route_type !== routeTypeFilter) return false;
    if (driverFilter !== 'all') {
      if (driverFilter === '__none__' && r.driver_id) return false;
      if (driverFilter !== '__none__' && r.driver_id !== driverFilter) return false;
    }
    if (schoolFilter !== 'all' && !(r.schools || []).includes(schoolFilter)) return false;
    return true;
  });

  // Group rows for display.
  const grouped = useMemo(() => {
    if (groupBy === 'time') return { 'All routes': filtered };
    const out = {};
    filtered.forEach(r => {
      let key;
      if (groupBy === 'driver') {
        key = r.driver_name ? r.driver_name : '🚫 Unassigned';
      } else if (groupBy === 'school') {
        if (!r.schools || r.schools.length === 0) {
          key = '(no school stops)';
        } else {
          key = r.schools.join(' + ');
        }
      } else if (groupBy === 'route_type') {
        key = ROUTE_TYPE_LABEL[r.route_type] || r.route_type || 'Other';
      } else {
        key = 'All routes';
      }
      (out[key] = out[key] || []).push(r);
    });
    return out;
  }, [filtered, groupBy]);

  const exportCsv = () => {
    const headers = [
      'Time', 'Route Type', 'From', 'To', 'Driver', 'Escorts',
      'School(s)', 'Children', 'Status', 'Warnings',
    ];
    const lines = [headers.join(',')];
    filtered.forEach(r => {
      const escortNames = (r.escorts || []).map(e => e.full_name).join('; ');
      const childNames = (r.children || []).map(c => c.child_name).join('; ');
      const schools = (r.schools || []).join('; ');
      const warnings = (r.warnings || []).join(' | ');
      const cells = [
        r.departure_time || '',
        ROUTE_TYPE_LABEL[r.route_type] || r.route_type || '',
        r.start_point || '',
        r.end_point || '',
        r.driver_name || 'UNASSIGNED',
        escortNames,
        schools,
        childNames,
        r.status || '',
        warnings,
      ].map(v => `"${String(v).replace(/"/g, '""')}"`);
      lines.push(cells.join(','));
    });
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transport-rota-${selectedDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading && !schedule) {
    return <div className="card"><div className="loading">Loading daily schedule…</div></div>;
  }

  return (
    <div className="card" data-testid="daily-transport-schedule">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <div>
          <h3 style={{ margin: 0 }}>
            🗓 Daily Transport Schedule — {new Date(selectedDate).toLocaleDateString()}
          </h3>
          <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
            One row per route. {filtered.length} of {rows.length} routes shown.
          </p>
        </div>
        <button
          className="btn btn-secondary"
          onClick={exportCsv}
          disabled={filtered.length === 0}
          data-testid="schedule-export-csv"
          style={{ fontSize: '0.875rem' }}
        >
          ⬇ Export CSV
        </button>
      </div>

      {/* Filters + grouping */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'center' }}>
        <select
          value={routeTypeFilter}
          onChange={(e) => setRouteTypeFilter(e.target.value)}
          data-testid="schedule-route-type-filter"
          style={{ padding: '0.4rem 0.6rem', fontSize: '0.875rem', borderRadius: '6px', border: '1px solid hsl(var(--border))' }}
        >
          <option value="all">All route types</option>
          {allRouteTypes.map(rt => (
            <option key={rt} value={rt}>{ROUTE_TYPE_LABEL[rt] || rt}</option>
          ))}
        </select>
        <select
          value={driverFilter}
          onChange={(e) => setDriverFilter(e.target.value)}
          data-testid="schedule-driver-filter"
          style={{ padding: '0.4rem 0.6rem', fontSize: '0.875rem', borderRadius: '6px', border: '1px solid hsl(var(--border))' }}
        >
          <option value="all">All drivers</option>
          <option value="__none__">🚫 Unassigned only</option>
          {allDrivers.map(d => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
        <select
          value={schoolFilter}
          onChange={(e) => setSchoolFilter(e.target.value)}
          data-testid="schedule-school-filter"
          style={{ padding: '0.4rem 0.6rem', fontSize: '0.875rem', borderRadius: '6px', border: '1px solid hsl(var(--border))' }}
        >
          <option value="all">All schools</option>
          {allSchools.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <span style={{ marginLeft: '0.5rem', fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>Group by:</span>
        <select
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value)}
          data-testid="schedule-group-by"
          style={{ padding: '0.4rem 0.6rem', fontSize: '0.875rem', borderRadius: '6px', border: '1px solid hsl(var(--border))' }}
        >
          <option value="time">Time (default)</option>
          <option value="driver">Driver</option>
          <option value="school">School</option>
          <option value="route_type">Route type</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state">
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🗓</div>
          <h3>Nothing scheduled</h3>
          <p style={{ color: 'hsl(var(--muted-foreground))' }}>
            No routes for {new Date(selectedDate).toLocaleDateString()} match the current filters.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {Object.entries(grouped).map(([groupKey, groupRows]) => (
            <div key={groupKey}>
              {groupBy !== 'time' && (
                <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.4rem', color: 'hsl(var(--muted-foreground))' }}>
                  {groupKey} <span style={{ fontWeight: 400 }}>({groupRows.length})</span>
                </div>
              )}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }} data-testid={`schedule-table-${groupKey}`}>
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '1px solid hsl(var(--border))', background: 'hsl(var(--muted))' }}>
                      <th style={{ padding: '0.5rem' }}>Time</th>
                      <th style={{ padding: '0.5rem' }}>Route</th>
                      <th style={{ padding: '0.5rem' }}>Driver</th>
                      <th style={{ padding: '0.5rem' }}>Escorts</th>
                      <th style={{ padding: '0.5rem' }}>School / Children</th>
                      <th style={{ padding: '0.5rem' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupRows.map(row => (
                      <tr key={row.manifest_id} style={{ borderBottom: '1px solid hsl(var(--border))', verticalAlign: 'top' }} data-testid={`schedule-row-${row.manifest_id}`}>
                        <td style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>
                          <div>{row.departure_time || '--:--'}</div>
                          {row.target_completion_time && (
                            <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                              → {row.target_completion_time}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '0.5rem' }}>
                          <div style={{ fontWeight: 600 }}>{ROUTE_TYPE_LABEL[row.route_type] || row.route_type}</div>
                          <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                            {row.start_point} → {row.end_point}
                          </div>
                        </td>
                        <td style={{ padding: '0.5rem' }}>
                          {row.driver_name ? (
                            <>
                              <div>{row.driver_name}</div>
                              {row.driver_role && row.driver_role !== 'driver' && (
                                <div style={{ fontSize: '0.7rem', color: 'hsl(var(--muted-foreground))' }}>
                                  Staff (rota)
                                </div>
                              )}
                            </>
                          ) : (
                            <span style={{ color: 'hsl(0 70% 45%)', fontWeight: 500 }}>🚫 Unassigned</span>
                          )}
                        </td>
                        <td style={{ padding: '0.5rem' }}>
                          {row.escorts && row.escorts.length > 0
                            ? row.escorts.map(e => e.full_name).join(', ')
                            : <span style={{ color: 'hsl(var(--muted-foreground))' }}>—</span>}
                        </td>
                        <td style={{ padding: '0.5rem' }}>
                          {(row.schools || []).length > 0 && (
                            <div style={{ fontWeight: 500 }}>{(row.schools || []).join(', ')}</div>
                          )}
                          <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                            <strong>{row.total_children}</strong> children
                            {row.children_collected > 0 && ` (${row.children_collected} collected)`}
                          </div>
                          {row.children && row.children.length > 0 && (
                            <details style={{ marginTop: '0.25rem' }}>
                              <summary style={{ cursor: 'pointer', fontSize: '0.75rem' }}>View children</summary>
                              <ul style={{ margin: '0.25rem 0 0 1rem', padding: 0, fontSize: '0.75rem' }}>
                                {row.children.map(c => (
                                  <li key={c.child_id}>
                                    {c.child_name}
                                    {c.school_class && ` (${c.school_class})`}
                                    {c.collected && ' ✓'}
                                  </li>
                                ))}
                              </ul>
                            </details>
                          )}
                        </td>
                        <td style={{ padding: '0.5rem' }}>
                          <div>
                            <span style={{
                              padding: '0.1rem 0.4rem',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              background:
                                row.status === 'completed' ? 'hsl(145 70% 90%)' :
                                row.status === 'in_progress' ? 'hsl(220 90% 90%)' :
                                'hsl(45 92% 90%)',
                              color: 'hsl(var(--foreground))',
                            }}>
                              {(row.status || 'pending').replace('_', ' ').toUpperCase()}
                            </span>
                          </div>
                          {(row.warnings || []).map((w, i) => (
                            <div
                              key={i}
                              style={{
                                marginTop: '0.35rem',
                                padding: '0.25rem 0.45rem',
                                fontSize: '0.7rem',
                                background: 'hsl(45 92% 92%)',
                                border: '1px solid hsl(45 92% 70%)',
                                color: 'hsl(28 80% 30%)',
                                borderRadius: '4px',
                              }}
                              data-testid={`schedule-warning-${row.manifest_id}-${i}`}
                              title="Soft warning — does not block assignment"
                            >
                              ⚠️ {w}
                            </div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Inline avatar cell for the Staff Profiles table.
 *
 * Renders a 40px circular thumbnail with a hover-revealed edit/remove
 * overlay. Clicking the thumbnail (or "Change") opens a native file
 * picker; a successful upload auto-approves (per admin_upload_user_avatar)
 * and the parent table is refreshed via `onChanged`.
 */
function StaffAvatarCell({ profile, onChanged }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const avatarUrl = profile.user_avatar_url;
  const initials = (profile.user_name || profile.user_email || '?')
    .split(' ').map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

  const openPicker = () => { if (!busy) fileRef.current?.click(); };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same filename later
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo too large', { description: 'Max 5 MB. Try a smaller image.' });
      return;
    }
    if (!/^image\//i.test(file.type)) {
      toast.error('Not an image', { description: 'Please choose a PNG or JPG.' });
      return;
    }
    setBusy(true);
    try {
      await api.uploadUserAvatar(profile.user_id, file);
      toast.success('Photo updated', { description: `${profile.user_name}'s profile photo is live.` });
      onChanged && (await onChanged());
    } catch (err) {
      toast.error('Upload failed', { description: err?.message || 'Please try again.' });
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (e) => {
    e.stopPropagation();
    if (!avatarUrl) return;
    if (!window.confirm(`Remove ${profile.user_name}'s profile photo?`)) return;
    setBusy(true);
    try {
      await api.deleteUserAvatar(profile.user_id);
      toast.success('Photo removed');
      onChanged && (await onChanged());
    } catch (err) {
      toast.error('Remove failed', { description: err?.message || 'Please try again.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{ position: 'relative', width: 40, height: 40, cursor: busy ? 'wait' : 'pointer' }}
      onClick={openPicker}
      title={avatarUrl ? 'Click to change photo' : 'Click to add photo'}
      data-testid={`staff-avatar-cell-${profile._id}`}
    >
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={profile.user_name}
          style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', border: '1px solid hsl(var(--border))' }}
          data-testid={`staff-avatar-img-${profile._id}`}
        />
      ) : (
        <div
          style={{
            width: 40, height: 40, borderRadius: '50%',
            background: 'hsl(210 30% 92%)', color: 'hsl(210 30% 35%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.78rem', fontWeight: 700, border: '1px dashed hsl(210 20% 75%)',
          }}
          data-testid={`staff-avatar-initials-${profile._id}`}
        >
          {initials || '?'}
        </div>
      )}

      {/* Pencil badge overlay to signal editability */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute', bottom: -2, right: -2,
          width: 16, height: 16, borderRadius: '50%',
          background: 'hsl(var(--primary))', color: 'white',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.6rem', border: '2px solid white', boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
        }}
      >
        {busy ? '…' : '✎'}
      </span>

      {avatarUrl && !busy && (
        <button
          type="button"
          onClick={handleRemove}
          title="Remove photo"
          data-testid={`staff-avatar-remove-${profile._id}`}
          style={{
            position: 'absolute', top: -6, left: -6,
            width: 18, height: 18, borderRadius: '50%',
            background: 'hsl(0 70% 55%)', color: 'white',
            border: '2px solid white', cursor: 'pointer',
            fontSize: '0.75rem', lineHeight: '14px', padding: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
          }}
        >
          ×
        </button>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        style={{ display: 'none' }}
        data-testid={`staff-avatar-input-${profile._id}`}
      />
    </div>
  );
}

/**
 * Two-mode password admin dialog invoked from the Users tab.
 *
 * Mode A — "Set password directly":
 *   Admin types a value. Saved verbatim. No email is sent, no forced
 *   rotation flag; trust is placed in the admin sitting with the user.
 *
 * Mode B — "Reset & email temp password":
 *   Server mints a strong random password, flips `must_change_password`
 *   on the user record, and emails it to the user's inbox. The temp
 *   value is also surfaced one-time here so the admin can hand it over
 *   in person if email is delayed.
 *
 * Every action writes a `password_audit_log` row on the backend so the
 * chain of custody is preserved.
 */
function PasswordAdminModal({ open, target, onClose }) {
  const [mode, setMode] = useState('direct'); // 'direct' | 'reset'
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(null); // { temporary_password, email_sent, email }

  // Reset local state whenever the target user changes.
  useEffect(() => {
    if (open) {
      setMode('direct');
      setPwd('');
      setPwd2('');
      setIssued(null);
      setBusy(false);
    }
  }, [open, target?._id]);

  if (!open || !target) return null;

  const handleDirect = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (pwd !== pwd2) {
      toast.error('Passwords do not match');
      return;
    }
    setBusy(true);
    try {
      await api.adminSetUserPassword(target._id, pwd);
      toast.success('Password updated', {
        description: `${target.full_name || target.email} can now sign in with the new password.`,
      });
      onClose();
    } catch (err) {
      toast.error('Failed to set password', { description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async () => {
    setBusy(true);
    try {
      const res = await api.adminResetUserPassword(target._id);
      setIssued(res);
      if (res.email_sent) {
        toast.success('Reset email sent', {
          description: `Temporary password emailed to ${res.email}. They'll be asked to choose a new one on next login.`,
        });
      } else {
        toast.warning('Password reset — email failed', {
          description: 'Copy the temporary password below and share it out-of-band.',
        });
      }
    } catch (err) {
      toast.error('Reset failed', { description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="password-admin-overlay">
      <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="password-admin-modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h2>🔑 Manage password</h2>
          <button className="close-btn" onClick={onClose} data-testid="password-admin-close">✕</button>
        </div>
        <div style={{ padding: '0 0 0.75rem', fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>
          <strong style={{ color: 'hsl(var(--foreground))' }}>{target.full_name || target.email}</strong>
          {target.email && target.full_name ? <> · <span>{target.email}</span></> : null}
          <span className="badge badge-blue" style={{ marginLeft: '0.5rem', fontSize: '0.7rem' }}>
            {(target.role || '').replace(/_/g, ' ')}
          </span>
        </div>

        {issued ? (
          <div data-testid="password-admin-issued">
            <div style={{ padding: '0.75rem', background: 'hsl(140 60% 96%)', border: '1px solid hsl(140 50% 82%)', borderRadius: 8, marginBottom: '0.75rem' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.35rem' }}>
                {issued.email_sent ? '✅ Reset email sent' : '⚠️ Email delivery failed — share manually'}
              </div>
              <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))' }}>
                One-time temporary password (also emailed to {issued.email}):
              </div>
              <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <code
                  style={{ flex: 1, padding: '0.5rem 0.75rem', background: 'white', border: '1px dashed hsl(var(--border))', borderRadius: 6, fontSize: '0.95rem', letterSpacing: '0.03em' }}
                  data-testid="password-admin-temp-value"
                >
                  {issued.temporary_password}
                </code>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => { navigator.clipboard?.writeText(issued.temporary_password); toast.success('Copied'); }}
                  data-testid="password-admin-copy"
                >
                  📋 Copy
                </button>
              </div>
              <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                {target.full_name || 'The user'} will be forced to choose a new password on their next login.
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-primary" onClick={onClose} data-testid="password-admin-done">Done</button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
              <button
                type="button"
                className={mode === 'direct' ? 'btn btn-primary' : 'btn btn-ghost'}
                onClick={() => setMode('direct')}
                data-testid="password-admin-tab-direct"
                style={{ flex: 1 }}
              >
                Set directly
              </button>
              <button
                type="button"
                className={mode === 'reset' ? 'btn btn-primary' : 'btn btn-ghost'}
                onClick={() => setMode('reset')}
                data-testid="password-admin-tab-reset"
                style={{ flex: 1 }}
              >
                Reset & email
              </button>
            </div>

            {mode === 'direct' ? (
              <form onSubmit={handleDirect} autoComplete="off">
                <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.75rem' }}>
                  Type the new password. Nothing is emailed — hand this value to the user in person.
                </div>
                <div className="form-group">
                  <label>New password (min 8 characters)</label>
                  <input
                    type="text"
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    autoFocus
                    minLength={8}
                    autoComplete="new-password"
                    data-testid="password-admin-new"
                    placeholder="e.g. Sunrise-2026-!"
                  />
                </div>
                <div className="form-group">
                  <label>Confirm password</label>
                  <input
                    type="text"
                    value={pwd2}
                    onChange={(e) => setPwd2(e.target.value)}
                    autoComplete="new-password"
                    data-testid="password-admin-confirm"
                    placeholder="Re-enter to confirm"
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={busy} data-testid="password-admin-save-direct">
                    {busy ? 'Saving…' : '💾 Set password'}
                  </button>
                </div>
              </form>
            ) : (
              <div>
                <div style={{ padding: '0.75rem', background: 'hsl(45 100% 96%)', border: '1px solid hsl(45 80% 82%)', borderRadius: 8, marginBottom: '0.75rem', fontSize: '0.85rem' }}>
                  A strong temporary password will be generated and emailed to
                  {' '}<strong>{target.email || '—'}</strong>. They'll be required
                  to choose their own password the next time they sign in.
                  {!target.email && (
                    <div style={{ marginTop: '0.5rem', color: 'hsl(0 60% 40%)', fontWeight: 600 }}>
                      ⚠ No email on file for this user — use "Set directly" instead.
                    </div>
                  )}
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleReset}
                    disabled={busy || !target.email}
                    data-testid="password-admin-send-reset"
                  >
                    {busy ? 'Resetting…' : '✉ Reset & email'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function AdminDashboard({ user, organization: initialOrganization, onLogout, onChangePassword }) {
  const mobileNav = useMobileNav();
  // Initial tab: honour `?tab=<name>` from the URL so external deep-links
  // (e.g. the Billing Success page after a Mollie checkout) can jump
  // straight to a specific tab. Fallback to 'overview' when absent or
  // unrecognised. Whitelist is intentional so a stale link can't wedge
  // the UI into an invalid state.
  const _urlInitialTab = (() => {
    try {
      const q = new URLSearchParams(window.location.search).get('tab');
      const allowed = new Set([
        'overview', 'sessions', 'bookings', 'users', 'children',
        'activities', 'waitlist', 'financial', 'moments', 'enrollments',
        'transport', 'staff', 'compliance', 'organization', 'billing',
        'events', 'attendance', 'incidents', 'rooms', 'shifts',
        'timesheets', 'notifications', 'audit',
      ]);
      return q && allowed.has(q) ? q : 'overview';
    } catch {
      return 'overview';
    }
  })();
  const [activeTab, setActiveTab] = useState(_urlInitialTab);
  const [stats, setStats] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [bookings, setBookings] = useState([]);
  // Bookings filter state — initialised from URL so a filtered view
  // is shareable. Status changes trigger a backend refetch (since the
  // API filters status server-side); everything else is client-side
  // filtering on the loaded list via useMemo (see filteredBookings).
  const [bookingFilters, setBookingFilters] = useState(() =>
    (activeTab === undefined || activeTab === null) ? { ...DEFAULT_BOOKING_FILTERS } : readBookingFiltersFromURL()
  );
  // Derived: bookings after applying client-side filters + sort.
  // Memoised to avoid re-filtering large lists on every render.
  const filteredBookings = useMemo(
    () => applyBookingFilters(bookings, bookingFilters),
    [bookings, bookingFilters]
  );
  const [users, setUsers] = useState([]);
  // Parents who have self-registered and are awaiting admin approval. Only
  // populated when the org has `settings.require_parent_approval = true`.
  const [pendingParents, setPendingParents] = useState([]);
  // Signup-source filter for the Pending Parents card. Values: '' (all),
  // 'welcome_page', 'branded_login', 'other'. Lets admins isolate cohorts
  // arriving from their public marketing page.
  const [pendingSourceFilter, setPendingSourceFilter] = useState('');
  // Photo approval queue (Phase 10 — Hybrid safeguarding workflow).
  // Populated alongside the Users tab so managers can see and action
  // staff-uploaded ID photos before they go live on a badge.
  const [pendingAvatars, setPendingAvatars] = useState([]);
  const [children, setChildren] = useState([]);
  const [activities, setActivities] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [financialReport, setFinancialReport] = useState(null);
  const [moments, setMoments] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showMomentModal, setShowMomentModal] = useState(false);
  // When set, opens the Moment Detail modal for that specific moment.
  // Distinct from `showMomentModal` (create form) and from
  // `viewingChildId` (child profile drawer). Fixes the previous
  // behaviour where clicking a moment card opened the entire child
  // profile instead of the individual moment. The child profile is
  // still reachable via a link inside the detail modal.
  const [viewingMoment, setViewingMoment] = useState(null);
  // Edit-in-place state for the moment detail modal (Phase M: admin
  // moment editing). null = read-only; object = editing that moment.
  // Shape: { caption: string, tagsInput: string }.
  const [editingMoment, setEditingMoment] = useState(null);
  const [momentActionSubmitting, setMomentActionSubmitting] = useState(false);
  const [momentActionError, setMomentActionError] = useState('');
  const [loading, setLoading] = useState(true);

  // ====== Edit/Delete state for CRUD sweep ======
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editingActivity, setEditingActivity] = useState(null);
  // Controlled state for the activity icon input. The rest of the activity
  // form is uncontrolled (FormData), but emojis composed of multiple
  // Unicode code points (e.g. flags, family emojis with skin tones) get
  // miscounted by `maxLength` and can't be pasted reliably. We keep this
  // tiny piece controlled so the picker grid + keyboard input both work.
  const [activityIcon, setActivityIcon] = useState('');
  const [editingChild, setEditingChild] = useState(null);
  // Authorised collectors live as a controlled array because the rest of the
  // child modal is uncontrolled (FormData). Each entry: { full_name, relationship, phone }
  const [editingCollectors, setEditingCollectors] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  // Target user for the in-place role-tier promote/demote modal.
  const [roleTierUser, setRoleTierUser] = useState(null);
  const [editingCenter, setEditingCenter] = useState(null);
  // Controlled age-group list for the Edit Center modal. Kept out of
  // FormData (native <input type=checkbox> checkbox groups don't
  // round-trip cleanly as an array) so we sync it from the opened
  // centre and read it back in handleSaveCenter.
  const [editCenterAgeGroups, setEditCenterAgeGroups] = useState([]);
  const [editingStaff, setEditingStaff] = useState(null);
  // PayslipDialog state — kept at the dashboard level so it survives the
  // edit-staff modal closing (admin can launch the payslip dialog from
  // the staff profile editor in one click).
  const [payslipDialogFor, setPayslipDialogFor] = useState(null);
  const [editingRoom, setEditingRoom] = useState(null);
  const [editingShift, setEditingShift] = useState(null);
  const [showChildModal, setShowChildModal] = useState(false);
  const [showCenterEditModal, setShowCenterEditModal] = useState(false);
  // Meals tab sub-navigation: 'library' | 'planner' | 'today'
  const [mealsSubTab, setMealsSubTab] = useState('library');
  // Shared MessageComposer trigger — set to {parentUserId?, childId?,
  // childName?, parentName?} from any row-level "Message" button to
  // open the dialog with that context pre-filled. null when closed.
  const [composerCtx, setComposerCtx] = useState(null);
  // Unread message count for the admin Messages tab badge.
  const [adminUnreadMessages, setAdminUnreadMessages] = useState(0);
  // Children-tab force-aware delete dialog state.
  const [deletingChild, setDeletingChild] = useState(null);

  // ====== Admin "Book on behalf of parent" modal ======
  // Cascading picker: parent → child → session instance. Keeps record
  // ownership correct (booking + ledger filed against parent_id).
  //
  // Bulk mode extension (Phase L): when `bulkMode` is true the modal
  // asks the admin to pick MANY sessionInstanceIds instead of one, then
  // shows a dry-run preview from POST /api/bookings/bulk before
  // committing. All bulk-only fields are prefixed `bulk*` so they don't
  // leak into the single-session path when the toggle is off.
  const [showCreateBookingModal, setShowCreateBookingModal] = useState(false);
  const [bookingFlow, setBookingFlow] = useState({
    parentSearch: '',
    parent: null,            // selected parent user object
    parentChildren: [],      // children fetched for that parent
    // Multi-select — admin can book one session for several siblings
    // at once. Kept as an array even for single-child families so the
    // batch endpoint handles both cases uniformly.
    childIds: [],
    sessionInstanceId: '',
    requiresTransport: false,
    submitting: false,
    error: '',
    upcomingSessions: [],    // session_instances with capacity remaining
    // --- Bulk-mode fields (Phase L) ---
    bulkMode: false,                     // toggle inside the modal
    bulkStep: 'select',                  // 'select' | 'preview'
    bulkSessionIds: [],                  // multi-select of upcoming sessions
    bulkServiceType: 'nursery_all_day',  // one service per batch
    bulkTemplateFilter: '',              // client-side filter over session list
    bulkPreview: null,                   // dry-run result from /api/bookings/bulk
    bulkPreviewLoading: false,
  });

  // ====== Admin "View parent details" drawer ======
  // Holds the user object whose drawer is currently open. When non-null,
  // <UserDetailDrawer /> is rendered as a slide-in panel with their
  // children, enrolments and recent bookings.
  const [viewingUser, setViewingUser] = useState(null);

  // Password admin modal — populated with the user object whose password
  // the admin wants to set/reset. Cleared when the modal closes.
  const [passwordAdminUser, setPasswordAdminUser] = useState(null);

  // New-registration alert — count of parent self-signups in the last 7d.
  // Populated by /api/admin/new-registrations-count. Cleared locally
  // once the admin opens the Users tab so the badge feels responsive.
  const [newRegCount, setNewRegCount] = useState(0);
  const [newRegDismissed, setNewRegDismissed] = useState(false);

  // <ChildProfileDrawer /> shown when any Children/Moments row is clicked.
  // Reuses the same aggregated child profile endpoint as Staff Dashboard.
  const [viewingChildId, setViewingChildId] = useState(null);

  // Upgrade modal triggered by 402 PLAN_LIMIT_REACHED responses.
  // Listens for the global `kw:plan-limit-reached` CustomEvent dispatched
  // from the patched window.fetch interceptor installed in App.js — so any
  // backend 402 anywhere in the dashboard automatically pops the modal.
  const [upgradeDetail, setUpgradeDetail] = useState(null);
  const [showStaffIdModal, setShowStaffIdModal] = useState(false);
  // When set, opens StaffIDCardModal in admin-view mode for the given
  // staff member (from the Users tab "🎫 View ID" action). Distinct
  // from `showStaffIdModal` which is the current user's own badge.
  const [viewingStaffIdFor, setViewingStaffIdFor] = useState(null);
  useEffect(() => {
    function onPlanLimit(e) {
      const detail = (e && e.detail) || null;
      // Some backends nest the payload under .detail.detail — flatten if so.
      const flat = detail && detail.detail && detail.detail.error_code ? detail.detail : detail;
      setUpgradeDetail(flat || { error_code: 'PLAN_LIMIT_REACHED' });
    }
    window.addEventListener('kw:plan-limit-reached', onPlanLimit);
    return () => window.removeEventListener('kw:plan-limit-reached', onPlanLimit);
  }, []);

  // ====== Users tab — search + role filter ======
  // Both are pure client-side filters applied to the already-loaded `users`
  // list. We keep them client-side so the existing pagination/CSV export
  // helpers continue to operate on the raw list.
  const [usersSearch, setUsersSearch] = useState('');
  const [usersRoleFilter, setUsersRoleFilter] = useState('');

  // Walk-in enrolment helper modal — explains how an admin can complete
  // an enrolment when a family arrives in person and the parent doesn't
  // self-serve. We don't run the full Enrolment Wizard "on behalf of"
  // because that would require auth/scope rework — instead we shortcut
  // the admin to create the parent account first.
  const [showWalkInHelper, setShowWalkInHelper] = useState(false);

  // Enrolments tab — which card is expanded to show its full details.
  // Holds the enrolment _id (string) or null. Only one expanded at a time
  // to keep the page scannable.
  const [expandedEnrollmentId, setExpandedEnrollmentId] = useState(null);

  // Staff Ops → Rooms detail panel — shows the children currently
  // assigned to the room (the optional Phase C feature). Holds the
  // selected room object or null.
  const [roomDetail, setRoomDetail] = useState(null);

  // Staff Profiles tab — filter by underlying user role.
  // Values: '' (all) | 'staff' | 'driver' | 'room_leader' | 'deputy_manager' | 'org_admin' | 'org_owner'.
  const [staffRoleFilter, setStaffRoleFilter] = useState('');
  // Staff Profiles tab — filter by employment type (orthogonal to role).
  // '' (all) | 'employee' | 'contractor' | 'volunteer'.
  const [staffEmpTypeFilter, setStaffEmpTypeFilter] = useState('');
  // Staff Profiles tab — filter by lifecycle status. Defaults to 'active'
  // so archived/pending profiles don't clutter the main list. Clicking the
  // "N staff need review" banner jumps this to 'attention'.
  // '' (all) | 'active' | 'pending' | 'archived' | 'attention' (pending+archived).
  const [staffStatusFilter, setStaffStatusFilter] = useState('active');
  // Tracks the live value of the "Type" select inside the Staff modal
  // so the Hourly Rate field can be hidden when 🤝 Volunteer is picked.
  const [staffModalEmpType, setStaffModalEmpType] = useState('employee');
  // DriverAssignmentsDrawer state — set to { driverId, driverName } to open.
  const [driverDrawer, setDriverDrawer] = useState(null);
  // Reprice Bookings modal (Bookings tab → 💱 Reprice bookings…)
  const [showRepriceDialog, setShowRepriceDialog] = useState(false);

  // ====== Generate Session Instances modal — mode + term-time state ======
  // The modal supports two modes:
  //   - 'range'     : pick a start/end date (legacy behaviour)
  //   - 'term_time' : pick saved Term(s) / Holiday-Club window(s) +
  //                   weekdays; backend skips closed dates (half-term,
  //                   inset, bank holidays). Optional "Start from" date
  //                   clips the output without changing the underlying
  //                   term windows.
  const [generateMode, setGenerateMode] = useState('range');
  const [generateTerms, setGenerateTerms] = useState([]);
  const [generateSelectedTermIds, setGenerateSelectedTermIds] = useState([]);
  const [generateWeekdaysIso, setGenerateWeekdaysIso] = useState([1, 2, 3, 4, 5]);
  const [generateDateFrom, setGenerateDateFrom] = useState('');
  const [generateDateTo, setGenerateDateTo] = useState('');
  const [generatePreview, setGeneratePreview] = useState(null);
  const [generateBusy, setGenerateBusy] = useState(false);
  
  // Search and pagination states
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  
  // Staff Management State
  const [staffProfiles, setStaffProfiles] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [liveRatios, setLiveRatios] = useState(null);
  const [staffSubTab, setStaffSubTab] = useState('profiles');  // Sub-tab for the Organization Settings page. Splits ~20 cards across 6
  // focused tabs so the surface stays scannable and maintenance-heavy
  // actions (account cleanup) don't jump to the top of a routine visit.
  //
  // Values: 'profile' | 'public' | 'billing' | 'staff' | 'preferences' |
  // 'maintenance'. Persisted via URL hash so admins can deep-link.
  const [orgSubTab, setOrgSubTab] = useState(() => {
    if (typeof window === 'undefined') return 'profile';
    const h = (window.location.hash || '').replace('#', '').split('/').pop();
    return ['profile', 'public', 'billing', 'staff', 'preferences', 'maintenance'].includes(h) ? h : 'profile';
  });
  useEffect(() => {
    // Keep URL hash in sync so "Copy link" from the browser goes to the
    // right sub-tab. Only mutates the hash when we're actually on the
    // organization tab to avoid clobbering other pages' hash usage.
    if (typeof window === 'undefined') return;
    if (activeTab !== 'organization') return;
    const currentHash = (window.location.hash || '').replace('#', '');
    if (currentHash !== orgSubTab) {
      const url = `${window.location.pathname}${window.location.search}#${orgSubTab}`;
      window.history.replaceState(null, '', url);
    }
  }, [orgSubTab, activeTab]);
  // Financial tab sub-tab: 'overview' (P&L) or 'payroll' (PayrollCenter)
  const [financialSubTab, setFinancialSubTab] = useState('overview');
  // Aggregate placeholder audit count from the Policies Library.
  // Rendered as a small red pill on the "🎨 Public Presence" sub-tab
  // so admins spot outstanding placeholder work without opening it.
  // Updated via a callback prop on `<PoliciesLibrary />`.
  const [policyPlaceholderCount, setPolicyPlaceholderCount] = useState(0);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [showRoomModal, setShowRoomModal] = useState(false);
  const [showShiftModal, setShowShiftModal] = useState(false);
  // Controlled state for the Schedule Shift modal — using uncontrolled
  // defaultValue inputs caused at least one production report of the
  // time silently reverting (likely browser auto-fill kicking in). Now
  // that we own the value it cannot drift between renders.
  const [shiftFormState, setShiftFormState] = useState({
    staff_id: '',
    room_id: '',
    date: '',
    start_time: '08:00',
    end_time: '17:00',
    notes: '',
  });
  
  // Transport Management State
  const [manifests, setManifests] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [isGeneratingRoutes, setIsGeneratingRoutes] = useState(false);
  // Users who are eligible to drive a school run — populated from
  // `/api/transport/eligible-drivers` which the backend filters by
  // (role=driver OR can_do_school_runs=true). Used to populate the
  // driver dropdown on each manifest card.
  const [transportEligibleDrivers, setTransportEligibleDrivers] = useState([]);
  // Sub-tab inside Transport: 'manifests' (per-route cards) vs
  // 'schedule' (master daily schedule rota).
  const [transportSubTab, setTransportSubTab] = useState('manifests');
  // Master daily schedule rows (one per manifest, enriched + with
  // soft conflict warnings).
  const [dailySchedule, setDailySchedule] = useState(null);

  // Organization Management State — seeded from App-level prop so we always
  // have access to the current org (for sidebar logo etc.) even before the
  // Organization tab fetches the full detail.
  const [organization, setOrganization] = useState(initialOrganization || null);

  // Session Template modal — pricing-tier controlled fields. Live so we
  // can show a real-time preview AND so the "Flat price (no tiers)"
  // toggle can zero them out at once. Declared AFTER `organization` so
  // the syncing useEffect below can safely depend on it (avoids TDZ).
  const [tplBasePrice, setTplBasePrice] = useState('15.00');
  const [tplEarlyDiscount, setTplEarlyDiscount] = useState(20); // percent
  const [tplEarlyCutoff, setTplEarlyCutoff] = useState(7);
  const [tplLatePremium, setTplLatePremium] = useState(30); // percent
  const [tplLateCutoff, setTplLateCutoff] = useState(2);
  const [tplShowPricingTiers, setTplShowPricingTiers] = useState(false);

  // Sync the pricing tier state whenever the Session Template modal
  // opens. For an edit, hydrate from the template. For a create, fall
  // back to the org-level defaults (settings.pricing_defaults).
  useEffect(() => {
    if (!showTemplateModal) return;
    const orgDefaults = (organization?.settings?.pricing_defaults) || {};
    const t = editingTemplate;
    setTplBasePrice(t?.base_price != null ? String(t.base_price) : '15.00');
    setTplEarlyDiscount(Math.round(((t?.early_bird_discount ?? orgDefaults.early_bird_discount ?? 0.2) * 100)));
    setTplEarlyCutoff(t?.early_bird_cutoff_days ?? orgDefaults.early_bird_cutoff_days ?? 7);
    setTplLatePremium(Math.round(((t?.last_minute_premium ?? orgDefaults.last_minute_premium ?? 0.3) * 100)));
    setTplLateCutoff(t?.last_minute_premium_cutoff_days ?? orgDefaults.last_minute_premium_cutoff_days ?? 2);
    const nonDefault = t && (
      (t.early_bird_discount ?? 0.2) !== 0.2 ||
      (t.early_bird_cutoff_days ?? 7) !== 7 ||
      (t.last_minute_premium ?? 0.3) !== 0.3 ||
      (t.last_minute_premium_cutoff_days ?? 2) !== 2
    );
    setTplShowPricingTiers(!!nonDefault);
  }, [showTemplateModal, editingTemplate, organization]);
  useEffect(() => {
    if (initialOrganization) setOrganization(initialOrganization);
  }, [initialOrganization]);

  // Poll new-registration count for the sidebar Users badge.
  // Refreshes on mount + every 90s. Cleared locally the moment the
  // admin opens the Users tab so the badge feels responsive without
  // waiting for the next poll cycle.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await api.getNewRegistrationsCount();
        if (!cancelled) setNewRegCount(res?.count || 0);
      } catch { /* silent — the badge is best-effort */ }
    };
    tick();
    const id = setInterval(tick, 90 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Auto-clear the badge the first time the admin opens the Users tab.
  useEffect(() => {
    if (activeTab === 'users' && newRegCount > 0 && !newRegDismissed) {
      setNewRegDismissed(true);
    }
  }, [activeTab, newRegCount, newRegDismissed]);
  const [orgEditForm, setOrgEditForm] = useState({
    name: '', type: 'single_center',
    contact_email: '', contact_phone: '', website: '',
    company_number: '', vat_number: '', legal_name: '',
    // Statutory registrations & compliance identifiers used by Policies
    ofsted_urn: '', dfe_urn: '', local_authority: '', lado_phone: '',
    // People — appear verbatim in generated policy PDFs
    registered_manager: '',
    safeguarding_lead: '',
    deputy_safeguarding_lead: '',
    // Policy defaults (operational thresholds baked into policy text)
    opening_hours: '',
    notice_period: '',
    late_pickup_fee: '',
    payment_terms_days: '',
    policy_review_period: '',
    // settings
    currency: 'GBP', timezone: 'Europe/London',
    date_format: 'DD/MM/YYYY', time_format: '24h',
    primary_color: '#00BCD4', secondary_color: '#FFC107',
    logo_url: '',
    welcome_message: '',
    tagline: '',
    // features
    features: {
      school_transport: true, moments: true, financial_reports: true,
      staff_management: true, enrollment_wizard: true,
    },
    // notifications
    notification_settings: {
      email_notifications: true, sms_notifications: false,
      booking_confirmations: true, waitlist_updates: true,
    },
    // parent onboarding gating
    require_parent_approval: false,
    // Policy auto-render on save (opt-in). When true, saving Org
    // Settings automatically re-renders every default policy from
    // its shipped template using the fresh org context.
    policy_auto_reset: false,
    // workforce policy
    late_tolerance_minutes: 5,
    early_departure_tolerance_minutes: 5,
    overtime_tolerance_minutes: 15,
    enforce_availability: false,
    default_annual_leave_days: 28,
    leave_year_start_month: 4,
    auto_submit_timesheets: true,
    // Terms & Conditions
    terms_and_conditions: '',
    terms_url: '',
    terms_version: 1,
    terms_updated_at: null,
    // When true on save, server bumps `terms_version` and triggers
    // re-acknowledgement banners for all parents in this org. Local-only
    // form flag — never persisted.
    bump_terms_version: false,
  });
  const [showAddCenterModal, setShowAddCenterModal] = useState(false);
  const [newCenter, setNewCenter] = useState({
    name: '', address: '', city: '', postcode: '',
    phone: '', email: '', capacity: 50,
    ofsted_number: '', ofsted_registered_manager: '',
    age_groups: ['0-2', '2-3', '3-5'],
  });
  const [orgSaving, setOrgSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState(null);

  const handleLogoFileUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setLogoError('Please select an image file (PNG, JPG, WEBP, GIF, or SVG).');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setLogoError('Logo is too large. Please choose a file under 2 MB.');
      return;
    }
    setLogoError(null);
    setLogoUploading(true);
    try {
      const result = await api.uploadOrganizationLogo(file);
      // Reflect new URL in form + organization state so sidebar logo updates immediately.
      setOrgEditForm(prev => ({ ...prev, logo_url: result.logo_url }));
      setOrganization(prev => prev ? {
        ...prev,
        settings: { ...(prev.settings || {}), logo_url: result.logo_url },
      } : prev);
    } catch (err) {
      setLogoError(err.message || 'Upload failed');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoRemove = async () => {
    setLogoError(null);
    setLogoUploading(true);
    try {
      await api.removeOrganizationLogo();
      setOrgEditForm(prev => ({ ...prev, logo_url: '' }));
      setOrganization(prev => prev ? {
        ...prev,
        settings: { ...(prev.settings || {}), logo_url: null },
      } : prev);
    } catch (err) {
      setLogoError(err.message || 'Remove failed');
    } finally {
      setLogoUploading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [activeTab]);

  // Refresh the unread message count for the admin Messages nav badge
  // whenever we switch tabs, so the navigation stays accurate.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await api.getCommunications({ unread_only: true });
        if (cancelled) return;
        setAdminUnreadMessages(Array.isArray(list)
          ? list.filter((m) => m.sent_by_user_id !== user?._id).length
          : 0);
      } catch (_) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [activeTab, user?._id]);
  
  useEffect(() => {
    if (activeTab === 'staff') {
      loadData();
    }
  }, [staffSubTab]);

  // Refetch bookings whenever the status-filter set changes (status is
  // applied server-side; every other booking filter is client-side via
  // applyBookingFilters/useMemo so no extra fetch is needed). We also
  // persist the entire filter state into the URL so a filtered view is
  // shareable and survives reload.
  useEffect(() => {
    if (activeTab !== 'bookings') return;
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getBookings({ statusIn: bookingFilters.status });
        if (!cancelled) setBookings(data || []);
      } catch (_) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, JSON.stringify(bookingFilters.status)]);

  useEffect(() => {
    if (activeTab !== 'bookings') return;
    writeBookingFiltersToURL(bookingFilters);
  }, [activeTab, bookingFilters]);

  // Sync authorised collectors form state when the user opens edit modal
  useEffect(() => {
    if (editingChild) {
      const list = Array.isArray(editingChild.authorised_collectors)
        ? editingChild.authorised_collectors
        : [];
      setEditingCollectors(list.map(p => ({
        full_name: p.full_name || '',
        relationship: p.relationship || 'guardian',
        phone: p.phone || '',
      })));
    } else {
      setEditingCollectors([]);
    }
  }, [editingChild]);

  // Sync the controlled icon field whenever the activity modal opens.
  useEffect(() => {
    if (showActivityModal) {
      setActivityIcon(editingActivity?.icon || '');
    }
  }, [showActivityModal, editingActivity]);

  /**
   * Trim a free-text icon down to the first user-perceived character
   * ("grapheme cluster") so we accept any single emoji — including ones
   * composed of multiple Unicode code points like 👨‍🏫 or 🇬🇧 — but
   * silently drop the rest if a user pastes a string of emojis.
   */
  const firstGrapheme = (raw) => {
    if (!raw) return '';
    if (typeof Intl !== 'undefined' && Intl.Segmenter) {
      const seg = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
      const it = seg.segment(raw)[Symbol.iterator]();
      const first = it.next();
      return first.done ? '' : first.value.segment;
    }
    // Fallback for very old browsers — char-by-char with surrogate pairs.
    return Array.from(raw)[0] || '';
  };

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'overview') {
        const statsData = await api.getDashboardStats();
        // Defensive: if the API returned an error envelope (e.g. 403),
        // it'd lack the expected numeric fields and crash the render.
        // Only commit when the payload looks like real stats.
        if (statsData && typeof statsData === 'object' && !statsData.detail) {
          setStats(statsData);
        } else {
          setStats(null);
        }
      } else if (activeTab === 'sessions') {
        const templatesData = await api.getSessionTemplates();
        setTemplates(templatesData);
      } else if (activeTab === 'bookings') {
        const bookingsData = await api.getBookings({ statusIn: bookingFilters.status });
        setBookings(bookingsData);
      } else if (activeTab === 'users') {
        const usersData = await api.getUsers();
        setUsers(usersData);
        try {
          const pending = await api.getPendingParents();
          setPendingParents(pending || []);
        } catch (e) { /* non-fatal */ }
        try {
          const approvals = await api.listAvatarApprovals();
          setPendingAvatars(approvals?.items || []);
        } catch (e) { /* non-fatal — non-managers will hit 403 */ }
      } else if (activeTab === 'children') {
        const [childrenData, roomsData] = await Promise.all([
          api.getChildren(),
          api.getRooms().catch(() => []),
        ]);
        setChildren(childrenData);
        setRooms(roomsData);
      } else if (activeTab === 'activities') {
        const activitiesData = await api.getActivities();
        setActivities(activitiesData);
      } else if (activeTab === 'waitlist') {
        const waitlistData = await api.getWaitlist();
        setWaitlist(waitlistData);
      } else if (activeTab === 'financial') {
        // Load P&L report and attendance trends
        const pnlData = await api.getPNLReport();
        const attendanceData = await api.getAttendanceTrends();
        
        // Merge data for display
        setFinancialReport({
          ...pnlData,
          ...attendanceData
        });
      } else if (activeTab === 'moments') {
        const momentsData = await api.getMoments();
        setMoments(momentsData);
      } else if (activeTab === 'enrollments') {
        const enrollmentsData = await api.getEnrollments();
        setEnrollments(enrollmentsData);
      } else if (activeTab === 'transport') {
        // Load manifests + master schedule + users + eligible drivers
        // in parallel so the dropdown is populated on first paint and
        // both sub-tabs render without an extra round-trip.
        const [manifestsRes, scheduleRes, eligibleRes, usersData] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/manifests?date=${selectedDate}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          }),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/transport/daily-schedule?date=${selectedDate}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          }),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/transport/eligible-drivers`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          }),
          api.getUsers().catch(() => []),
        ]);
        if (manifestsRes.ok) {
          const manifestsData = await manifestsRes.json();
          setManifests(manifestsData);
        }
        if (scheduleRes.ok) {
          const sched = await scheduleRes.json();
          setDailySchedule(sched);
        }
        if (eligibleRes.ok) {
          const elig = await eligibleRes.json();
          setTransportEligibleDrivers(elig?.items || []);
        }
        if (Array.isArray(usersData)) {
          setUsers(usersData);
        }
      } else if (activeTab === 'organization') {
        try {
          const org = await api.getMyOrganization();
          setOrganization(org);
          const s = org.settings || {};
          setOrgEditForm({
            name: org.name || '',
            type: org.type || 'single_center',
            contact_email: org.contact_email || '',
            contact_phone: org.contact_phone || '',
            website: org.website || '',
            company_number: org.company_number || '',
            vat_number: org.vat_number || '',
            legal_name: org.legal_name || '',
            ofsted_urn: org.ofsted_urn || '',
            dfe_urn: org.dfe_urn || '',
            local_authority: org.local_authority || '',
            lado_phone: org.lado_phone || '',
            registered_manager: org.registered_manager || '',
            safeguarding_lead: org.safeguarding_lead || '',
            deputy_safeguarding_lead: org.deputy_safeguarding_lead || '',
            opening_hours: org.opening_hours || '',
            notice_period: org.notice_period || '',
            late_pickup_fee: org.late_pickup_fee || '',
            payment_terms_days: org.payment_terms_days || '',
            policy_review_period: org.policy_review_period || '',
            currency: s.currency || 'GBP',
            timezone: s.timezone || 'Europe/London',
            date_format: s.date_format || 'DD/MM/YYYY',
            time_format: s.time_format || '24h',
            primary_color: s.primary_color || '#00BCD4',
            secondary_color: s.secondary_color || '#FFC107',
            logo_url: s.logo_url || '',
            welcome_message: s.welcome_message || '',
            tagline: s.tagline || '',
            features: {
              school_transport: true, moments: true, financial_reports: true,
              staff_management: true, enrollment_wizard: true,
              ...(s.features || {}),
            },
            notification_settings: {
              email_notifications: true, sms_notifications: false,
              booking_confirmations: true, waitlist_updates: true,
              ...(s.notification_settings || {}),
            },
            require_parent_approval: !!s.require_parent_approval,
            // Policy auto-render on save toggle. Opt-in — defaults
            // off so hand-tailored edits are safe.
            policy_auto_reset: !!s.policy_auto_reset,
            late_tolerance_minutes: Number.isFinite(s.late_tolerance_minutes) ? s.late_tolerance_minutes : 5,
            early_departure_tolerance_minutes: Number.isFinite(s.early_departure_tolerance_minutes) ? s.early_departure_tolerance_minutes : 5,
            overtime_tolerance_minutes: Number.isFinite(s.overtime_tolerance_minutes) ? s.overtime_tolerance_minutes : 15,
            enforce_availability: !!s.enforce_availability,
            default_annual_leave_days: Number.isFinite(s.default_annual_leave_days) ? s.default_annual_leave_days : 28,
            leave_year_start_month: Number.isFinite(s.leave_year_start_month) ? s.leave_year_start_month : 4,
            auto_submit_timesheets: s.auto_submit_timesheets !== false,
            terms_and_conditions: s.terms_and_conditions || '',
            terms_url: s.terms_url || '',
            terms_version: Number.isFinite(s.terms_version) ? s.terms_version : 1,
            terms_updated_at: s.terms_updated_at || null,
            bump_terms_version: false,
          });
        } catch (err) {
          console.error('Failed to load organization', err);
        }
      } else if (activeTab === 'staff') {
        // Load users for staff profile modal dropdown
        const usersData = await api.getUsers();
        setUsers(usersData);
        
        if (staffSubTab === 'profiles') {
          const profilesData = await api.getStaffProfiles();
          setStaffProfiles(profilesData);
        } else if (staffSubTab === 'rooms') {
          const roomsData = await api.getRooms();
          setRooms(roomsData);
        } else if (staffSubTab === 'shifts') {
          const shiftsData = await api.getShifts();
          setShifts(shiftsData);
          // Also load staff profiles for shift modal dropdown
          const profilesData = await api.getStaffProfiles();
          setStaffProfiles(profilesData);
        } else if (staffSubTab === 'ratios') {
          const ratiosData = await api.getLiveRatios();
          setLiveRatios(ratiosData);
        } else if (staffSubTab === 'leave') {
          // Need staff profiles for the on-behalf picker.
          const profilesData = await api.getStaffProfiles();
          setStaffProfiles(profilesData);
        }
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const templateData = {
      name: formData.get('name'),
      type: formData.get('type'),
      start_time: formData.get('start_time'),
      end_time: formData.get('end_time'),
      days_of_week: formData.get('days_of_week').split(',').map(d => parseInt(d.trim())),
      capacity: parseInt(formData.get('capacity')),
      base_price: parseFloat(tplBasePrice || '0'),
      // Convert UI percentages back to decimals for the backend.
      early_bird_discount: Math.max(0, Math.min(100, Number(tplEarlyDiscount) || 0)) / 100,
      early_bird_cutoff_days: Math.max(0, Number(tplEarlyCutoff) || 0),
      last_minute_premium: Math.max(0, Math.min(500, Number(tplLatePremium) || 0)) / 100,
      last_minute_premium_cutoff_days: Math.max(0, Number(tplLateCutoff) || 0),
    };

    try {
      if (editingTemplate) {
        await api.updateSessionTemplate(editingTemplate._id, templateData);
      } else {
        await api.createSessionTemplate(templateData);
      }
      setShowTemplateModal(false);
      setEditingTemplate(null);
      loadData();
    } catch (err) {
      alert('Error saving template: ' + err.message);
    }
  };

  const handleDeleteTemplate = async (template, force = false) => {
    await api.deleteSessionTemplate(template._id, force);
    loadData();
  };

  const handleGenerateInstances = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const templateId = formData.get('template_id');
    const startDate = new Date(formData.get('start_date')).toISOString();
    const endDate = new Date(formData.get('end_date')).toISOString();

    try {
      const result = await api.generateSessionInstances(templateId, startDate, endDate);
      alert(`Generated ${result.generated} session instances!`);
      setShowGenerateModal(false);
    } catch (err) {
      alert('Error generating instances: ' + err.message);
    }
  };

  // Load saved terms whenever the Generate Instances modal opens so that
  // the "Term-time" mode tab can list them. Cheap (one Mongo query) and
  // keeps the dropdown fresh after an admin edits the term calendar.
  useEffect(() => {
    if (!showGenerateModal) return;
    setGenerateMode('range');
    setGeneratePreview(null);
    setGenerateSelectedTermIds([]);
    setGenerateDateFrom('');
    setGenerateDateTo('');
    (async () => {
      try {
        const data = await api.getTerms();
        setGenerateTerms(data || []);
      } catch (_e) {
        setGenerateTerms([]);
      }
    })();
  }, [showGenerateModal]);

  const toggleGenerateTerm = (id) => {
    setGenerateSelectedTermIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
    setGeneratePreview(null);
  };

  const toggleGenerateWeekday = (iso) => {
    setGenerateWeekdaysIso((prev) =>
      prev.includes(iso) ? prev.filter((x) => x !== iso) : [...prev, iso].sort()
    );
    setGeneratePreview(null);
  };

  const handleGenerateTermTime = async ({ previewOnly }) => {
    if (!showGenerateModal) return;
    if (generateSelectedTermIds.length === 0) {
      toast.error('Pick at least one term or holiday club.');
      return;
    }
    if (generateWeekdaysIso.length === 0) {
      toast.error('Pick at least one weekday.');
      return;
    }
    if (generateDateFrom && generateDateTo && generateDateFrom > generateDateTo) {
      toast.error('"Start from" must be on or before "Until".');
      return;
    }
    setGenerateBusy(true);
    try {
      const result = await api.generateTermTimeSessions({
        template_id: showGenerateModal,
        term_ids: generateSelectedTermIds,
        weekdays_iso: generateWeekdaysIso,
        preview_only: previewOnly,
        date_from: generateDateFrom || null,
        date_to: generateDateTo || null,
      });
      if (previewOnly) {
        setGeneratePreview(result);
        toast.info(`Preview: ${result.would_create} sessions across ${result.terms?.length || 0} window(s).`,
          { description: result.skipped?.length ? `Skipping ${result.skipped.length} closed day(s).` : 'No closures in range.' });
      } else {
        toast.success(`Created ${result.created} session instance(s).`,
          { description: result.skipped_existing ? `Skipped ${result.skipped_existing} already existing.` : '' });
        setShowGenerateModal(false);
      }
    } catch (err) {
      toast.error('Term-time generation failed', { description: err.message });
    } finally {
      setGenerateBusy(false);
    }
  };

  const handleCreateActivity = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const cleanIcon = firstGrapheme(activityIcon || formData.get('icon') || '');
    if (!cleanIcon) {
      alert('Please pick an emoji icon for this activity.');
      return;
    }
    const activityData = {
      name: formData.get('name'),
      description: formData.get('description'),
      icon: cleanIcon,
      color: formData.get('color')
    };

    try {
      if (editingActivity) {
        await api.updateActivity(editingActivity._id, activityData);
      } else {
        await api.createActivity(activityData);
      }
      setShowActivityModal(false);
      setEditingActivity(null);
      loadData();
    } catch (err) {
      alert('Error saving activity: ' + err.message);
    }
  };

  const handleDeleteActivity = async (activity) => {
    await api.deleteActivity(activity._id);
    loadData();
  };

  // --- Children edit/delete ---
  const handleSaveChild = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const childData = {
      first_name: formData.get('first_name'),
      last_name: formData.get('last_name'),
      date_of_birth: formData.get('date_of_birth'),
      age_group: formData.get('age_group'),
      // room_id may be empty string when "— Unassigned —" is picked; we
      // normalise to null so the field is explicitly cleared on the
      // child record (rather than left untouched).
      room_id: formData.get('room_id') || null,
      medical_notes: formData.get('medical_notes') || '',
      allergies: (formData.get('allergies') || '').split(',').map(s => s.trim()).filter(Boolean),
      // Chain-of-custody: people other than the parent who are allowed to
      // collect the child. Drop blank rows so the array is clean.
      authorised_collectors: (editingCollectors || [])
        .map(p => ({
          full_name: (p.full_name || '').trim(),
          relationship: p.relationship || 'guardian',
          phone: (p.phone || '').trim(),
        }))
        .filter(p => p.full_name),
      // School info (school name, address, class, teacher, collection
      // location etc.). Persisted on the Child record so transport
      // manifests, daily roster and parent dashboard all read the same
      // canonical source.
      school_info: {
        ...(editingChild?.school_info || {}),
        school_name: formData.get('school_name') || '',
        school_address: formData.get('school_address') || '',
        school_postcode: formData.get('school_postcode') || '',
        school_class: formData.get('school_class') || '',
        teacher_name: formData.get('teacher_name') || '',
        school_meeting_point: formData.get('school_meeting_point') || '',
        school_contact_number: formData.get('school_contact_number') || '',
        school_notes: formData.get('school_notes') || '',
      },
    };
    try {
      if (editingChild) {
        await api.updateChild(editingChild._id, childData);
      } else {
        await api.createChild(childData);
      }
      setShowChildModal(false);
      setEditingChild(null);
      loadData();
    } catch (err) {
      alert('Error saving child: ' + err.message);
    }
  };

  const handleDeleteChild = async (child, force = false) => {
    await api.deleteChild(child._id, force);
    loadData();
  };

  // ===== "Book on behalf of parent" helpers =====
  const openCreateBookingModal = async () => {
    // Reset and open immediately so the user gets feedback, then fetch data.
    setBookingFlow({
      parentSearch: '',
      parent: null,
      parentChildren: [],
      childIds: [],
      sessionInstanceId: '',
      requiresTransport: false,
      submitting: false,
      error: '',
      upcomingSessions: [],
      bulkMode: false,
      bulkStep: 'select',
      bulkSessionIds: [],
      bulkServiceType: 'nursery_all_day',
      bulkTemplateFilter: '',
      bulkPreview: null,
      bulkPreviewLoading: false,
    });
    setShowCreateBookingModal(true);
    try {
      // Load users (we filter to parents in render) and upcoming sessions
      // for the next 60 days. We anchor the query at MIDNIGHT today (not
      // "now") so a session earlier in the day (e.g. breakfast club that
      // starts at 08:00 when the admin is booking at 14:00) still shows
      // up — admins often need to book a same-day session retroactively.
      const [usersData, sessionsData] = await Promise.all([
        api.getUsers(),
        (async () => {
          const startOfToday = new Date();
          startOfToday.setHours(0, 0, 0, 0);
          const end = new Date(startOfToday);
          end.setDate(end.getDate() + 60);
          const startISO = startOfToday.toISOString();
          const endISO = end.toISOString();
          try {
            return await api.getSessionInstances(startISO, endISO);
          } catch { return []; }
        })(),
      ]);
      setUsers(usersData);
      // Client-side re-filter — also anchored at midnight today so
      // today's sessions remain visible for the whole day.
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);
      const upcoming = (sessionsData || []).filter((s) => {
        const d = new Date(s.date);
        return d >= startOfToday;
      }).sort((a, b) => new Date(a.date) - new Date(b.date));
      setBookingFlow((bf) => ({ ...bf, upcomingSessions: upcoming }));
    } catch (err) {
      setBookingFlow((bf) => ({ ...bf, error: err.message || 'Failed to load booking data' }));
    }
  };

  const selectBookingParent = async (parent) => {
    setBookingFlow((bf) => ({ ...bf, parent, childIds: [], parentChildren: [], error: '' }));
    try {
      const kids = await api.getUserChildren(parent._id);
      setBookingFlow((bf) => ({ ...bf, parentChildren: kids || [] }));
    } catch (err) {
      setBookingFlow((bf) => ({
        ...bf,
        error: err.message || 'Failed to load children for that parent',
      }));
    }
  };

  const submitOnBehalfBooking = async (e) => {
    e?.preventDefault?.();
    const { parent, childIds, sessionInstanceId, requiresTransport } = bookingFlow;
    const ids = Array.isArray(childIds) ? childIds.filter(Boolean) : [];
    if (!parent || ids.length === 0 || !sessionInstanceId) {
      setBookingFlow((bf) => ({
        ...bf,
        error: 'Pick a parent, at least one child and a session before submitting.',
      }));
      return;
    }
    setBookingFlow((bf) => ({ ...bf, submitting: true, error: '' }));
    try {
      // Build the admin_payment_choice payload — same shape both the
      // batch and single endpoints understand. Only applied server-side
      // for PAYG children; ignored otherwise.
      const paymentChoice = _buildAdminPaymentChoice(bookingFlow);
      if (paymentChoice.method === 'mark_paid') {
        const validationErr = _validateMarkPaidChoice(paymentChoice);
        if (validationErr) {
          setBookingFlow((bf) => ({ ...bf, submitting: false, error: validationErr }));
          return;
        }
      }
      // Use the same batch endpoint parents hit — supplying
      // `on_behalf_of_parent_id` switches the booking owner to that
      // parent while keeping the admin as the audit actor. Returns
      // per-child outcome so we can summarise siblings cleanly.
      const result = await api.createBookingsBatch({
        session_instance_id: sessionInstanceId,
        child_ids: ids,
        on_behalf_of_parent_id: parent._id,
        service_type: 'nursery_all_day',
        requires_transport: !!requiresTransport,
        transport_requirements: null,
        admin_payment_choice: paymentChoice,
      });

      const parts = [];
      if (result?.confirmed?.length) parts.push(`✓ ${result.confirmed.length} booked`);
      if (result?.waitlisted?.length) parts.push(`⏳ ${result.waitlisted.length} waitlisted`);
      if (result?.errors?.length) parts.push(`⚠️ ${result.errors.length} couldn’t book`);
      const headline = parts.length ? parts.join(' · ') : 'No bookings were made.';
      const totalLabel = result?.total_charged
        ? ` — £${Number(result.total_charged).toFixed(2)}`
        : '';
      const parentLabel = parent.full_name || parent.email || 'parent';

      if (result?.errors?.length && !result?.confirmed?.length && !result?.waitlisted?.length) {
        throw new Error(result.errors[0].message || 'Booking failed');
      }
      if (result?.errors?.length) {
        toast.warning(
          `${headline}${totalLabel} for ${parentLabel}. First issue: ${result.errors[0].message}`
        );
      } else if (result?.confirmed?.length) {
        toast.success(`${headline}${totalLabel} for ${parentLabel}.`);
      } else if (result?.waitlisted?.length) {
        toast.info(`${headline} for ${parentLabel}.`);
      }

      setShowCreateBookingModal(false);
      // Reload bookings list so the new entries show immediately.
      if (activeTab === 'bookings') loadData();
    } catch (err) {
      setBookingFlow((bf) => ({ ...bf, error: err.message || 'Booking failed' }));
    } finally {
      setBookingFlow((bf) => ({ ...bf, submitting: false }));
    }
  };

  // ===== Bulk multi-session booking helpers (Phase L) =====
  //
  // The bulk flow is a two-step UX inside the same modal:
  //   1) Selection: admin picks children + service + multiple sessions
  //   2) Preview:   we call /api/bookings/bulk with dry_run=true and
  //                 render a summary; admin can go back or confirm
  // Confirm sends the same body with dry_run=false to actually create
  // the bookings.

  const runBulkPreview = async () => {
    const { parent, childIds, bulkSessionIds, bulkServiceType, requiresTransport } = bookingFlow;
    if (!parent) {
      setBookingFlow((bf) => ({ ...bf, error: 'Pick a parent first.' }));
      return;
    }
    if (!childIds || childIds.length === 0) {
      setBookingFlow((bf) => ({ ...bf, error: 'Pick at least one child.' }));
      return;
    }
    if (!bulkSessionIds || bulkSessionIds.length === 0) {
      setBookingFlow((bf) => ({ ...bf, error: 'Pick at least one session date.' }));
      return;
    }
    setBookingFlow((bf) => ({ ...bf, bulkPreviewLoading: true, error: '' }));
    try {
      const preview = await api.createBookingsBulk({
        session_instance_ids: bulkSessionIds,
        child_ids: childIds,
        service_type: bulkServiceType,
        requires_transport: !!requiresTransport,
        transport_requirements: null,
        on_behalf_of_parent_id: parent._id,
        dry_run: true,
      });
      setBookingFlow((bf) => ({
        ...bf,
        bulkPreview: preview,
        bulkStep: 'preview',
        bulkPreviewLoading: false,
      }));
    } catch (err) {
      setBookingFlow((bf) => ({
        ...bf,
        bulkPreviewLoading: false,
        error: err.message || 'Failed to preview bulk booking',
      }));
    }
  };

  const submitBulkBooking = async () => {
    const { parent, childIds, bulkSessionIds, bulkServiceType, requiresTransport } = bookingFlow;
    if (!parent || !childIds?.length || !bulkSessionIds?.length) return;
    setBookingFlow((bf) => ({ ...bf, submitting: true, error: '' }));
    try {
      const paymentChoice = _buildAdminPaymentChoice(bookingFlow);
      if (paymentChoice.method === 'mark_paid') {
        const validationErr = _validateMarkPaidChoice(paymentChoice);
        if (validationErr) {
          setBookingFlow((bf) => ({ ...bf, submitting: false, error: validationErr }));
          return;
        }
      }
      const result = await api.createBookingsBulk({
        session_instance_ids: bulkSessionIds,
        child_ids: childIds,
        service_type: bulkServiceType,
        requires_transport: !!requiresTransport,
        transport_requirements: null,
        on_behalf_of_parent_id: parent._id,
        dry_run: false,
        admin_payment_choice: paymentChoice,
      });
      const okCount = result?.confirmed?.length || 0;
      const wlCount = result?.waitlisted?.length || 0;
      const errCount = result?.errors?.length || 0;
      const parts = [];
      if (okCount) parts.push(`✓ ${okCount} booked`);
      if (wlCount) parts.push(`⏳ ${wlCount} waitlisted`);
      if (errCount) parts.push(`⚠️ ${errCount} failed`);
      const headline = parts.join(' · ') || 'No bookings were made.';
      const parentLabel = parent.full_name || parent.email || 'parent';
      if (okCount && !errCount) {
        toast.success(`${headline} for ${parentLabel}.`);
      } else if (okCount || wlCount) {
        toast.warning(
          `${headline} for ${parentLabel}.${
            errCount ? ` First issue: ${result.errors[0]?.message || 'unknown'}` : ''
          }`
        );
      } else {
        throw new Error(result?.errors?.[0]?.message || 'No bookings could be created.');
      }
      setShowCreateBookingModal(false);
      if (activeTab === 'bookings') loadData();
    } catch (err) {
      setBookingFlow((bf) => ({ ...bf, error: err.message || 'Bulk booking failed' }));
    } finally {
      setBookingFlow((bf) => ({ ...bf, submitting: false }));
    }
  };

  // --- Centers edit/delete ---
  const handleSaveCenter = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = {
      name: formData.get('name'),
      address: formData.get('address') || '',
      city: formData.get('city') || '',
      postcode: formData.get('postcode') || '',
      phone: formData.get('phone') || '',
      email: formData.get('email') || '',
      capacity: parseInt(formData.get('capacity') || '50', 10),
      ofsted_number: formData.get('ofsted_number') || null,
      ofsted_registered_manager: formData.get('ofsted_registered_manager') || null,
      // Per-centre age groups override org-level defaults on the
      // Welcome Page + booking eligibility. Read from controlled
      // state because checkbox groups don't round-trip via FormData.
      age_groups: Array.isArray(editCenterAgeGroups) ? editCenterAgeGroups : [],
    };
    try {
      if (editingCenter) {
        await api.updateOrganizationCenter(editingCenter.center_id, data);
      } else {
        await api.addOrganizationCenter(data);
      }
      setShowCenterEditModal(false);
      setEditingCenter(null);
      setEditCenterAgeGroups([]);
      // Refresh org so centers list updates
      const fresh = await api.getMyOrganization();
      setOrganization(fresh);
    } catch (err) {
      alert('Error saving center: ' + err.message);
    }
  };

  const handleDeleteCenter = async (center, force = false) => {
    await api.deleteOrganizationCenter(center.center_id, force);
    const fresh = await api.getMyOrganization();
    setOrganization(fresh);
  };

  // --- Staff/Rooms/Shifts delete + Holiday delete ---
  const handleDeleteStaff = async (staff, force = false) => {
    await api.deleteStaffProfile(staff._id, force);
    loadData();
  };
  const handleDeleteRoom = async (room, force = false) => {
    await api.deleteRoom(room._id, force);
    loadData();
  };
  const handleDeleteShift = async (shift) => {
    await api.deleteShift(shift._id);
    loadData();
  };
  const handleDeleteUser = async (u) => {
    await api.deleteUser(u._id);
    loadData();
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const password = formData.get('password');
    const canDoSchoolRuns = formData.get('can_do_school_runs') === 'on';
    const userData = {
      email: formData.get('email'),
      full_name: formData.get('full_name'),
      role: formData.get('role'),
      phone: formData.get('phone'),
    };

    try {
      if (editingUser && editingUser._id) {
        // Edit mode — only send fields that have changed / are editable.
        // Email is immutable (it's used as the login key) so we skip it.
        const updates = {
          full_name: userData.full_name,
          role: userData.role,
          phone: userData.phone || null,
          can_do_school_runs: canDoSchoolRuns,
        };
        // Allow admins to reset the password only when they fill the field.
        if (password) updates.password = password;
        await api.updateUser(editingUser._id, updates);
      } else {
        await api.register({ ...userData, password });
        // For new users we can't pass can_do_school_runs through the
        // public register endpoint (would let anyone self-grant the
        // capability); admin sets it via the edit drawer after create.
        if (canDoSchoolRuns) {
          toast.info(
            'User created. Re-open the user to grant "Can do school runs" — capability flag is set via edit mode.'
          );
        }
      }
      setShowUserModal(false);
      setEditingUser(null);
      loadData();
    } catch (err) {
      alert(`Error ${editingUser ? 'updating' : 'creating'} user: ${err.message}`);
    }
  };

  const [adminMomentPhoto, setAdminMomentPhoto] = useState(null);
  const [adminMomentUploading, setAdminMomentUploading] = useState(false);
  const [adminMomentError, setAdminMomentError] = useState(null);

  const handleAdminMomentPhotoPick = async (file) => {
    if (!file) return;
    setAdminMomentError(null);
    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
      setAdminMomentError('Please choose an image or short video.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setAdminMomentError('File too large (max 10 MB).');
      return;
    }
    setAdminMomentUploading(true);
    try {
      const r = await api.uploadFile(file, { category: 'moments' });
      setAdminMomentPhoto(r);
    } catch (err) {
      setAdminMomentError(err.message || 'Upload failed');
    } finally {
      setAdminMomentUploading(false);
    }
  };

  const handleAdminClearMomentPhoto = async () => {
    if (adminMomentPhoto?.file_id) {
      try { await api.deleteFile(adminMomentPhoto.file_id); } catch { /* best effort */ }
    }
    setAdminMomentPhoto(null);
    setAdminMomentError(null);
  };

  const handleCreateMoment = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const momentData = {
      child_id: formData.get('child_id'),
      caption: formData.get('caption'),
      tags: formData.get('tags').split(',').map(t => t.trim()).filter(t => t),
      photo_url: adminMomentPhoto?.url || null,
      media_file_id: adminMomentPhoto?.file_id || null,
    };

    try {
      await api.createMoment(momentData);
      setShowMomentModal(false);
      setAdminMomentPhoto(null);
      setAdminMomentError(null);
      loadData();
    } catch (err) {
      alert('Error creating moment: ' + err.message);
    }
  };

  const handleCreateStaffProfile = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const competencies = [];
    if (formData.get('first_aider')) competencies.push('first_aider');
    if (formData.get('unqualified')) competencies.push('unqualified');
    if (formData.get('level_2')) competencies.push('level_2_qualified');
    if (formData.get('level_3')) competencies.push('level_3_qualified');
    if (formData.get('level_5')) competencies.push('level_5_qualified');
    if (formData.get('food_hygiene')) competencies.push('food_hygiene_l2_l3');
    if (formData.get('driver')) competencies.push('driver');
    if (formData.get('senco')) competencies.push('senco');
    if (formData.get('safeguarding')) competencies.push('safeguarding_lead');

    const employmentType = formData.get('employment_type') || 'employee';
    // Volunteers are unpaid by definition. Force the hourly_rate to 0
    // so any UI that ignores employment_type still produces a £0 total
    // (the timesheet view explicitly hides pay for volunteers anyway).
    const hourlyRateRaw = parseFloat(formData.get('hourly_rate') || '0');

    // Parse the JSON-encoded custom competency selections from
    // StaffProfileExtras' hidden input. Fall back to an empty array so
    // we never crash if the extras panel wasn't mounted (create mode).
    let customCompetencies = [];
    try {
      const raw = formData.get('custom_competencies_json');
      if (raw) customCompetencies = JSON.parse(raw);
      if (!Array.isArray(customCompetencies)) customCompetencies = [];
    } catch { customCompetencies = []; }

    // Small helper that returns a form value as-is (empty string
    // becomes undefined so we don't send blanks that would clear
    // real data on the backend for optional string fields).
    const optStr = (name) => {
      const v = formData.get(name);
      return v ? String(v).trim() : undefined;
    };
    const optInt = (name) => {
      const v = formData.get(name);
      if (v === '' || v === null || v === undefined) return undefined;
      const n = parseInt(v, 10);
      return Number.isNaN(n) ? undefined : n;
    };

    const profileData = {
      user_id: formData.get('user_id'),
      competencies,
      custom_competencies: customCompetencies,
      hourly_rate: employmentType === 'volunteer' ? 0 : hourlyRateRaw,
      contract_hours: parseInt(formData.get('contract_hours')),
      employment_type: employmentType,
      annual_leave_entitlement: parseInt(formData.get('annual_leave_entitlement') || '28', 10),
      carried_over_leave_days: parseInt(formData.get('carried_over_leave_days') || '0', 10),
      // ---- Phase M additions ----
      start_date:              optStr('start_date'),
      probation_end_date:      optStr('probation_end_date'),
      notice_period_weeks:     optInt('notice_period_weeks'),
      job_title:               optStr('job_title'),
      line_manager_user_id:    optStr('line_manager_user_id'),
      assigned_room_id:        optStr('assigned_room_id'),
      date_of_birth:           optStr('date_of_birth'),
      nationality:             optStr('nationality'),
      sex:                     optStr('sex'),
      starter_declaration:     optStr('starter_declaration'),
      student_loan_plan:       optStr('student_loan_plan'),
      payroll_id:              optStr('payroll_id'),
      emergency_contact_name:  optStr('emergency_contact_name'),
      emergency_contact_phone: optStr('emergency_contact_phone'),
      emergency_contact_relationship: optStr('emergency_contact_relationship'),
    };
    // Strip undefined so we don't overwrite existing values with nulls.
    Object.keys(profileData).forEach((k) => profileData[k] === undefined && delete profileData[k]);

    try {
      if (editingStaff && editingStaff._id) {
        // Edit mode — preserve user_id (it's the immutable link to the user
        // record) and only update the editable fields.
        const { user_id: _uid, ...updates } = profileData;
        await api.updateStaffProfile(editingStaff._id, updates);
      } else {
        await api.createStaffProfile(profileData);
      }
      setShowStaffModal(false);
      setEditingStaff(null);
      loadData();
    } catch (err) {
      alert('Error saving staff profile: ' + err.message);
    }
  };

  const handleCreateRoom = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const roomData = {
      name: formData.get('name'),
      age_group: formData.get('age_group'),
      capacity: parseInt(formData.get('capacity')),
      required_ratio: parseFloat(formData.get('required_ratio')),
      required_first_aider: formData.get('required_first_aider') === 'true'
    };

    try {
      await api.createRoom(roomData);
      setShowRoomModal(false);
      loadData();
    } catch (err) {
      alert('Error creating room: ' + err.message);
    }
  };

  const handleCreateShift = async (e) => {
    e.preventDefault();
    const shiftData = {
      staff_id: shiftFormState.staff_id,
      room_id: shiftFormState.room_id,
      date: new Date(shiftFormState.date).toISOString(),
      start_time: shiftFormState.start_time,
      end_time: shiftFormState.end_time,
      notes: shiftFormState.notes,
    };

    try {
      const created = await api.createShift(shiftData);
      // Surface non-blocking workforce warnings (availability gaps,
      // missing declarations) so admins know there's something off
      // even when policy allows the save.
      const warns = created?.validation?.warnings || [];
      if (warns.length) {
        alert('Shift created, but heads up:\n\n• ' + warns.join('\n• '));
      }
      setShowShiftModal(false);
      // Reset to defaults for the next shift the admin schedules.
      setShiftFormState({
        staff_id: '', room_id: '', date: '',
        start_time: '08:00', end_time: '17:00', notes: '',
      });
      loadData();
    } catch (err) {
      // Hard-block error from the workforce policy comes back as an
      // object with `errors`/`warnings`. Render it nicely instead of
      // [object Object].
      let detail = err.message;
      try {
        const parsed = JSON.parse(detail);
        if (parsed.errors) detail = '• ' + parsed.errors.join('\n• ');
      } catch { /* not JSON */ }
      alert('Could not save shift:\n\n' + detail);
    }
  };

  const handlePublishShift = async (shiftId) => {
    try {
      await api.updateShift(shiftId, { status: 'published' });
      loadData();
    } catch (err) {
      let detail = err.message;
      try {
        const parsed = JSON.parse(detail);
        if (parsed.errors) detail = '• ' + parsed.errors.join('\n• ');
      } catch { /* not JSON */ }
      alert('Could not publish shift:\n\n' + detail);
    }
  };

  const handleToggleUserActive = async (userId, currentStatus) => {
    try {
      await api.updateUser(userId, { is_active: !currentStatus });
      loadData();
    } catch (err) {
      alert('Error updating user: ' + err.message);
    }
  };

  const handleApproveParent = async (u) => {
    if (!window.confirm(`Approve ${u.full_name || u.email}? They will gain full access.`)) return;
    try {
      await api.approveUser(u._id);
      loadData();
    } catch (err) {
      alert('Could not approve: ' + err.message);
    }
  };

  const handleRejectParent = async (u) => {
    const reason = window.prompt(
      `Reject ${u.full_name || u.email}? Optional reason for your records:`,
      ''
    );
    if (reason === null) return; // cancelled
    try {
      await api.rejectUser(u._id, reason);
      loadData();
    } catch (err) {
      alert('Could not reject: ' + err.message);
    }
  };

  // -- Photo Approval workflow (Phase 10) --------------------------------
  // Manager actions on pending staff ID photos. We optimistically remove
  // the row from the queue, then refresh on background error/success so
  // the badge counts stay in sync with the server.
  const handleApprovePhoto = async (item) => {
    if (!window.confirm(
      `Approve the photo uploaded by ${item.full_name}? It will appear on their ID card right away.`,
    )) return;
    try {
      await api.approveAvatar(item.user_id);
      setPendingAvatars(prev => prev.filter(p => p.user_id !== item.user_id));
      toast.success(`${item.full_name}'s photo approved.`);
    } catch (err) {
      toast.error('Could not approve: ' + err.message);
    }
  };

  const handleRejectPhoto = async (item) => {
    const reason = window.prompt(
      `Reject ${item.full_name}'s photo? Optional reason (emailed to them so they know what to fix):`,
      '',
    );
    if (reason === null) return; // cancelled
    try {
      await api.rejectAvatar(item.user_id, reason || '');
      setPendingAvatars(prev => prev.filter(p => p.user_id !== item.user_id));
      toast.success(`${item.full_name}'s photo rejected.`);
    } catch (err) {
      toast.error('Could not reject: ' + err.message);
    }
  };

  return (
    <div className="dashboard">
      <button
        type="button"
        className="mobile-nav-toggle"
        onClick={mobileNav.toggle}
        aria-label="Toggle navigation"
        data-testid="mobile-nav-toggle"
      >
        {mobileNav.isOpen ? '✕' : '☰'}
      </button>
      <div className={`mobile-nav-backdrop ${mobileNav.isOpen ? 'show' : ''}`} onClick={mobileNav.close}></div>
      <div className={`sidebar ${mobileNav.isOpen ? 'mobile-open' : ''}`} onClick={(e) => {
        // Close sidebar on mobile when a nav item is tapped
        if (e.target.closest('.nav-item')) mobileNav.close();
      }}>
        <div className="sidebar-header" data-testid="sidebar-header">
          {organization && organization.settings && organization.settings.logo_url ? (
            <>
              <img
                src={organization.settings.logo_url}
                alt={organization.name}
                style={{ maxHeight: 48, maxWidth: 160, objectFit: 'contain', marginBottom: '0.5rem' }}
                data-testid="sidebar-org-logo"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <h2 style={{ fontSize: '1rem', margin: 0 }}>⚡ {organization.name}</h2>
            </>
          ) : (
            <h2>⚡ {organization ? organization.name : 'Kids WiseApp Admin'}</h2>
          )}
          <p>{user.full_name}</p>
        </div>

        <ul className="nav-menu">
          <li 
            className={`nav-item ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
            data-testid="nav-overview"
          >
            📊 Overview
          </li>
          <GetStartedSidebarPill onNavigate={(path) => {
            try {
              const url = new URL(path, window.location.origin);
              const t = url.searchParams.get('tab');
              if (t) { setActiveTab(t); return; }
            } catch (_) { /* fall through */ }
            window.location.assign(path);
          }} />
          <li
            className={`nav-item ${activeTab === 'roster' ? 'active' : ''}`}
            onClick={() => setActiveTab('roster')}
            data-testid="nav-roster"
          >
            🪪 Daily Roster
          </li>
          <li 
            className={`nav-item ${activeTab === 'sessions' ? 'active' : ''}`}
            onClick={() => setActiveTab('sessions')}
            data-testid="nav-sessions"
          >
            🗓️ Sessions
          </li>
          <li 
            className={`nav-item ${activeTab === 'bookings' ? 'active' : ''}`}
            onClick={() => setActiveTab('bookings')}
            data-testid="nav-bookings"
          >
            📋 Bookings
          </li>
          <li
            className={`nav-item ${activeTab === 'plans' ? 'active' : ''}`}
            onClick={() => setActiveTab('plans')}
            data-testid="nav-plans"
          >
            🔁 Recurring Plans
          </li>
          <li
            className={`nav-item ${activeTab === 'terms' ? 'active' : ''}`}
            onClick={() => setActiveTab('terms')}
            data-testid="nav-terms"
          >
            📅 Term Calendar
          </li>
          {/* Events — date-bound things (trips, themed days, visitors,
              parent sessions, celebrations). Distinct from /api/activities
              which is just a catalogue of holiday-club activity *types*.
              Placed between Term Calendar and Users to keep date-driven
              admin entries clustered. */}
          <li
            className={`nav-item ${activeTab === 'events' ? 'active' : ''}`}
            onClick={() => setActiveTab('events')}
            data-testid="nav-events"
          >
            🎪 Events
          </li>
          <li 
            className={`nav-item ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
            data-testid="nav-users"
            style={{ position: 'relative' }}
          >
            👥 Users
            {newRegCount > 0 && !newRegDismissed && (
              <span
                data-testid="nav-users-new-badge"
                title={`${newRegCount} new registration${newRegCount === 1 ? '' : 's'} in the last 7 days`}
                style={{
                  position: 'absolute',
                  top: '50%',
                  right: '0.9rem',
                  transform: 'translateY(-50%)',
                  minWidth: 20,
                  height: 20,
                  padding: '0 6px',
                  borderRadius: 999,
                  background: 'hsl(0 75% 55%)',
                  color: 'white',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 0 0 2px white, 0 0 0 3px hsl(0 75% 55% / 0.35)',
                  animation: 'kw-badge-pulse 1.8s ease-in-out infinite',
                }}
              >
                {newRegCount > 99 ? '99+' : newRegCount}
              </span>
            )}
          </li>
          <li 
            className={`nav-item ${activeTab === 'children' ? 'active' : ''}`}
            onClick={() => setActiveTab('children')}
            data-testid="nav-children"
          >
            👶 Children
          </li>
          <li 
            className={`nav-item ${activeTab === 'activities' ? 'active' : ''}`}
            onClick={() => setActiveTab('activities')}
            data-testid="nav-activities"
          >
            🎨 Activities
          </li>
          <li
            className={`nav-item ${activeTab === 'meals' ? 'active' : ''}`}
            onClick={() => setActiveTab('meals')}
            data-testid="nav-meals"
          >
            🍽️ Meals
          </li>
          <li 
            className={`nav-item ${activeTab === 'waitlist' ? 'active' : ''}`}
            onClick={() => setActiveTab('waitlist')}
            data-testid="nav-waitlist"
          >
            ⏳ Waitlist
          </li>
          <li 
            className={`nav-item ${activeTab === 'financial' ? 'active' : ''}`}
            onClick={() => setActiveTab('financial')}
            data-testid="nav-financial"
          >
            💰 Financial
          </li>
          <li 
            className={`nav-item ${activeTab === 'moments' ? 'active' : ''}`}
            onClick={() => setActiveTab('moments')}
            data-testid="nav-moments"
          >
            📸 Moments
          </li>
          <li
            className={`nav-item ${activeTab === 'messages' ? 'active' : ''}`}
            onClick={() => setActiveTab('messages')}
            data-testid="nav-messages"
          >
            ✉️ Messages{adminUnreadMessages > 0 ? ` (${adminUnreadMessages})` : ''}
          </li>
          <li 
            className={`nav-item ${activeTab === 'staff' ? 'active' : ''}`}
            onClick={() => setActiveTab('staff')}
            data-testid="nav-staff"
          >
            👨‍🏫 Staff Ops
          </li>
          <li 
            className={`nav-item ${activeTab === 'enrollments' ? 'active' : ''}`}
            onClick={() => setActiveTab('enrollments')}
            data-testid="nav-enrollments"
          >
            📋 Enrollments
          </li>
          <li 
            className={`nav-item ${activeTab === 'transport' ? 'active' : ''}`}
            onClick={() => setActiveTab('transport')}
            data-testid="nav-transport"
          >
            🚐 Transport
          </li>
          {(user.role === 'org_owner' || user.role === 'admin' || user.role === 'org_admin') && (
            <li
              className={`nav-item ${activeTab === 'organization' ? 'active' : ''}`}
              onClick={() => setActiveTab('organization')}
              data-testid="nav-organization"
            >
              🏢 Organization
            </li>
          )}
          {/* Billing — visible to admin-tier only. Mollie subscriptions,
              plan, usage gauges + invoice history all live here. */}
          {['admin', 'org_owner', 'org_admin'].includes(user?.role) && (
            <li
              className={`nav-item ${activeTab === 'billing' ? 'active' : ''}`}
              onClick={() => setActiveTab('billing')}
              data-testid="nav-billing"
            >
              💳 Billing
            </li>
          )}
          <li
            className="nav-item"
            onClick={() => setShowStaffIdModal(true)}
            data-testid="nav-staff-id-card"
            title="Show or share my staff ID card"
          >
            🪪 My ID Card
          </li>
          <li
            className="nav-item"
            onClick={onChangePassword}
            data-testid="nav-change-password"
            title="Change your password"
          >
            🔐 Change password
          </li>
          <li 
            className="nav-item logout-btn" 
            onClick={onLogout}
            data-testid="logout-btn"
          >
            🚪 Logout
          </li>
        </ul>
      </div>

      <div className="main-content">
        {/* Trial countdown banner — auto-hides for paid subs; ramps up
            in urgency as the deadline approaches; forces "Reactivate"
            when expired (dismiss is blocked in that state). */}
        <TrialCountdownBanner />

        {/* Usage banner — shown on every tab when an org is at 80%/100% of any cap */}
        <UsageBanner />

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <>
            <WelcomeHeroCard user={user} onNavigate={(path) => {
              // Deep-link like "/admin?tab=branding" — pull the tab out
              // and switch locally rather than hard-navigating so we
              // don't tear down the whole dashboard state.
              try {
                const url = new URL(path, window.location.origin);
                const t = url.searchParams.get('tab');
                if (t) { setActiveTab(t); return; }
              } catch (_) { /* fall through */ }
              window.location.assign(path);
            }} />
            <div className="page-header">
              <h1>Command Center</h1>
              <p>Real-time system overview</p>
            </div>

            {loading ? (
              <div>Loading...</div>
            ) : stats ? (
              <>
                <div className="card-grid">
                  <div 
                    className="stat-card" 
                    data-testid="stat-total-children"
                    onClick={() => setActiveTab('children')}
                    style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(16,24,40,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '';
                    }}
                  >
                    <h3>Total Children</h3>
                    <p className="value">{stats.total_children}</p>
                    <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem' }}>
                      Click to view →
                    </div>
                  </div>
                  <div 
                    className="stat-card" 
                    data-testid="stat-total-users"
                    onClick={() => setActiveTab('users')}
                    style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(16,24,40,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '';
                    }}
                  >
                    <h3>Total Users</h3>
                    <p className="value">{stats.total_users}</p>
                    <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem' }}>
                      Click to manage →
                    </div>
                  </div>
                  <div 
                    className="stat-card" 
                    data-testid="stat-total-parents"
                    onClick={() => setActiveTab('users')}
                    style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(16,24,40,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '';
                    }}
                  >
                    <h3>Parents</h3>
                    <p className="value">{stats.total_parents}</p>
                    <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem' }}>
                      Click to view →
                    </div>
                  </div>
                  <div 
                    className="stat-card" 
                    data-testid="stat-total-staff"
                    onClick={() => setActiveTab('staff')}
                    style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(16,24,40,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '';
                    }}
                  >
                    <h3>Staff</h3>
                    <p className="value">{stats.total_staff}</p>
                    <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem' }}>
                      Click to view ops →
                    </div>
                  </div>
                  <div 
                    className="stat-card" 
                    data-testid="stat-today-sessions"
                    onClick={() => setActiveTab('sessions')}
                    style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(16,24,40,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '';
                    }}
                  >
                    <h3>Today's Sessions</h3>
                    <p className="value">{stats.today_sessions}</p>
                    <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem' }}>
                      Click to manage →
                    </div>
                  </div>
                  <div 
                    className="stat-card" 
                    data-testid="stat-today-bookings"
                    onClick={() => setActiveTab('bookings')}
                    style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(16,24,40,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '';
                    }}
                  >
                    <h3>Today's Bookings</h3>
                    <p className="value">{stats.today_bookings}</p>
                    <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem' }}>
                      Click to view →
                    </div>
                  </div>
                  <div 
                    className="stat-card" 
                    data-testid="stat-total-bookings"
                    onClick={() => setActiveTab('bookings')}
                    style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(16,24,40,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '';
                    }}
                  >
                    <h3>Total Bookings</h3>
                    <p className="value">{stats.total_bookings}</p>
                    <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem' }}>
                      Click to view all →
                    </div>
                  </div>
                  <div 
                    className="stat-card" 
                    data-testid="stat-waitlist"
                    onClick={() => setActiveTab('waitlist')}
                    style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(16,24,40,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '';
                    }}
                  >
                    <h3>Waiting</h3>
                    <p className="value">{stats.waitlist_count}</p>
                    <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem' }}>
                      Click to view waitlist →
                    </div>
                  </div>
                  <div 
                    className="stat-card" 
                    data-testid="stat-waitlist-offered"
                    onClick={() => setActiveTab('waitlist')}
                    style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(16,24,40,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '';
                    }}
                  >
                    <h3>Offers Pending</h3>
                    <p className="value">{stats.waitlist_offered}</p>
                    <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem' }}>
                      Click to review →
                    </div>
                  </div>
                  <div 
                    className="stat-card" 
                    data-testid="stat-revenue"
                    onClick={() => setActiveTab('financial')}
                    style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(16,24,40,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '';
                    }}
                  >
                    <h3>Month Revenue</h3>
                    <p className="value">£{(stats.month_revenue ?? 0).toFixed(2)}</p>
                    <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem' }}>
                      Click for full report →
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="empty-state">No data available</div>
            )}

            {/* Chase list — overdue invoices with parent contact and
                "Mark contacted" action. Only visible to admin roles;
                sits at the bottom of overview so nothing important is
                pushed off-screen when the list is empty. */}
            <ChaseListCard onNavigateInvoices={() => setActiveTab('financial')} />
          </>
        )}

        {/* ROSTER TAB — Daily Chain of Custody */}
        {activeTab === 'roster' && (
          <>
            <div className="page-header">
              <h1>🪪 Daily Roster</h1>
              <p>Today's check-ins, check-outs and chain of custody</p>
            </div>
            <DailyRoster
              allowEdit={true}
              onMessageParent={(c) => setComposerCtx({
                parentUserId: c.parent_id,
                childId: c.child_id,
                childName: `${c.first_name || ''} ${c.last_name || ''}`.trim(),
              })}
            />
          </>
        )}

        {/* SESSIONS TAB */}
        {activeTab === 'sessions' && (
          <>
            <div className="page-header">
              <h1>Session Templates</h1>
              <button 
                className="btn btn-primary" 
                onClick={() => setShowTemplateModal(true)}
                data-testid="create-template-btn"
              >
                + Create Template
              </button>
            </div>

            {loading ? (
              <div>Loading...</div>
            ) : templates.length > 0 ? (
              <div className="card">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Time</th>
                      <th>Days</th>
                      <th>Capacity</th>
                      <th>Base Price</th>
                      <th>Early Bird</th>
                      <th>Last Minute</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {templates.map(template => {
                      const eb = template.early_bird_discount ?? 0;
                      const lm = template.last_minute_premium ?? 0;
                      const ebDays = template.early_bird_cutoff_days ?? 0;
                      const lmDays = template.last_minute_premium_cutoff_days ?? 2;
                      const isFlat = eb === 0 && lm === 0;
                      return (
                      <tr key={template._id}>
                        <td><strong>{template.name}</strong></td>
                        <td><span className="badge badge-blue">{template.type}</span></td>
                        <td>{template.start_time} - {template.end_time}</td>
                        <td>{template.days_of_week.join(', ')}</td>
                        <td>{template.capacity}</td>
                        <td>£{template.base_price}</td>
                        <td title={isFlat ? 'Flat price' : `Booked ≥ ${ebDays}d ahead`} data-testid={`template-${template._id}-early`}>
                          {isFlat ? '—' : eb > 0 ? `-${(eb * 100).toFixed(0)}% (≥${ebDays}d)` : '—'}
                        </td>
                        <td title={isFlat ? 'Flat price' : `Booked < ${lmDays}d ahead`} data-testid={`template-${template._id}-late`}>
                          {isFlat ? '—' : lm > 0 ? `+${(lm * 100).toFixed(0)}% (<${lmDays}d)` : '—'}
                        </td>
                        <td>
                          <div className="inline-flex flex-wrap gap-2 items-center">
                            <button 
                              className="btn btn-secondary"
                              onClick={() => setShowGenerateModal(template._id)}
                              data-testid={`generate-instances-${template._id}`}
                            >
                              Generate
                            </button>
                            <RowActions
                              testIdPrefix={`template-${template._id}`}
                              onEdit={() => { setEditingTemplate(template); setShowTemplateModal(true); }}
                              onDelete={(force) => handleDeleteTemplate(template, force)}
                              confirmTitle={`Delete template "${template.name}"?`}
                              confirmText="Templates with future bookings will be archived (soft-deleted) so the historical record stays intact."
                            />
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <p>No session templates</p>
                <button className="btn btn-primary" onClick={() => setShowTemplateModal(true)}>Create First Template</button>
              </div>
            )}
          </>
        )}

        {/* BOOKINGS TAB */}
        {activeTab === 'bookings' && (
          <>
            <div className="page-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '0.75rem' }}>
                <div>
                  <h1>All Bookings</h1>
                  <p>{filteredBookings.length === bookings.length
                    ? `${bookings.length} total bookings`
                    : `Showing ${filteredBookings.length} of ${bookings.length} bookings`}</p>
                </div>
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-ghost"
                    onClick={() => setShowRepriceDialog(true)}
                    data-testid="open-reprice-dialog-btn"
                    title="Bulk-fix prices for bookings created under stale pricing rules"
                    style={{ fontSize: '0.85rem' }}
                  >
                    💱 Reprice bookings…
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={openCreateBookingModal}
                    data-testid="create-booking-btn"
                  >
                    + Book on behalf of a parent
                  </button>
                </div>
              </div>
            </div>

            {/* Filter bar — status is server-side, everything else is
                client-side via applyBookingFilters/useMemo. State is
                URL-persisted for shareable filtered views. */}
            <BookingsFilterBar
              filters={bookingFilters}
              onChange={setBookingFilters}
              templates={templates}
              centers={organization?.centers || []}
              totalCount={bookings.length}
              filteredCount={filteredBookings.length}
              onReset={() => setBookingFilters({ ...DEFAULT_BOOKING_FILTERS })}
              onUseInReprice={() => setShowRepriceDialog(true)}
            />

            {loading ? (
              <div>Loading...</div>
            ) : filteredBookings.length > 0 ? (
              <div className="card">
                <table>
                  <thead>
                    <tr>
                      <th>Child</th>
                      <th>Parent</th>
                      <th>Session</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Price</th>
                      <th>Status</th>
                      <th>Booked</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBookings.map(booking => {
                      // Cross-reference with children list so we can show
                      // year group + school inline (helps admins triage
                      // transport bookings without opening child cards).
                      const childRec = children.find(c => c._id === booking.child_id);
                      const yg = childRec ? ukYearGroup(childRec.date_of_birth) : null;
                      const school = childRec?.school_info?.school_name;
                      // Compute price tier badge against base_price for
                      // quick visual triage of early-bird / last-minute.
                      const paid = Number(booking.price_paid || 0);
                      const base = Number(booking.base_price || 0);
                      let tierBadge = null;
                      if (base > 0) {
                        if (Math.abs(paid - base) <= 0.01) {
                          tierBadge = { label: '= base', color: '#6b7280' };
                        } else if (paid < base) {
                          tierBadge = { label: '🐦 early', color: '#10b981' };
                        } else {
                          tierBadge = { label: '⏰ late', color: '#ef4444' };
                        }
                      }
                      const statusColor = ({
                        confirmed: 'badge-green',
                        pending: 'badge-yellow',
                        cancelled: 'badge-red',
                        completed: 'badge-blue',
                      })[booking.status] || 'badge-green';
                      return (
                      <tr
                        key={booking._id}
                        data-testid={`booking-row-${booking._id}`}
                        onClick={() => {
                          const parent = users.find((u) => u._id === booking.parent_id);
                          if (parent) setViewingUser(parent);
                        }}
                        title="Click to view parent profile, children, enrolments and bookings"
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'hsl(var(--muted) / 0.35)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
                      >
                        <td>
                          <div>
                            <div>{booking.child_name || 'N/A'}</div>
                            {(yg || school) && (
                              <div style={{ fontSize: '0.7rem', color: 'hsl(var(--muted-foreground))' }} data-testid={`booking-child-meta-${booking._id}`}>
                                {yg ? yg.label : ''}
                                {yg && school ? ' · ' : ''}
                                {school ? `🏫 ${school}` : ''}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div style={{ fontSize: '0.85rem' }}>{booking.parent_name || '—'}</div>
                          {booking.parent_email && (
                            <div style={{ fontSize: '0.7rem', color: 'hsl(var(--muted-foreground))' }}>
                              {booking.parent_email}
                            </div>
                          )}
                        </td>
                        <td>
                          <div>{booking.session_name || 'N/A'}</div>
                          {booking.service_type && (
                            <div style={{ fontSize: '0.7rem', color: 'hsl(var(--muted-foreground))' }}>
                              {booking.service_type.replace(/_/g, ' ')}
                              {booking.requires_transport ? ' · 🚌' : ''}
                            </div>
                          )}
                        </td>
                        <td>{booking.session_date ? new Date(booking.session_date).toLocaleDateString() : 'N/A'}</td>
                        <td>{booking.start_time} - {booking.end_time}</td>
                        <td>
                          <div>£{booking.price_paid}</div>
                          {tierBadge && (
                            <div
                              style={{
                                display: 'inline-block',
                                marginTop: '2px',
                                padding: '1px 6px',
                                borderRadius: '999px',
                                fontSize: '0.65rem',
                                background: `${tierBadge.color}1A`,
                                color: tierBadge.color,
                                fontWeight: 600,
                              }}
                              title={base ? `Base £${base}` : ''}
                              data-testid={`booking-tier-badge-${booking._id}`}
                            >
                              {tierBadge.label}
                            </div>
                          )}
                        </td>
                        <td><span className={`badge ${statusColor}`}>{booking.status}</span></td>
                        <td><small>{new Date(booking.created_at).toLocaleDateString()}</small></td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="btn btn-ghost"
                            style={{ fontSize: '0.78rem', padding: '0.2rem 0.5rem' }}
                            onClick={() => setComposerCtx({
                              parentUserId: booking.parent_id,
                              childId: booking.child_id,
                              childName: booking.child_name,
                            })}
                            data-testid={`booking-message-${booking._id}`}
                            title="Message the parent about this booking"
                          >
                            ✉️ Message
                          </button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                {bookings.length === 0
                  ? 'No bookings yet'
                  : 'No bookings match your current filters.'}
              </div>
            )}
          </>
        )}

        {/* RECURRING PLANS TAB — Booking-v2 Slice B */}
        {activeTab === 'plans' && (
          <>
            <div className="page-header">
              <h1>🔁 Recurring booking plans</h1>
              <p>Subscribe a child to a weekly schedule (All Year / Term Time / Ad-hoc).</p>
            </div>
            <EnrollmentPlanManager mode="admin" />
          </>
        )}

        {/* TERM CALENDAR TAB */}
        {activeTab === 'terms' && (
          <>
            <TermCalendarManager />
          </>
        )}

        {/* USERS TAB */}
        {activeTab === 'users' && (
          <>
            <div className="page-header">
              <h1>User Management</h1>
              <button 
                className="btn btn-primary" 
                onClick={() => { setEditingUser(null); setShowUserModal(true); }}
                data-testid="create-user-btn"
              >
                + Create User
              </button>
            </div>

            {/* Pending parent approvals — only shown when there's at least
                one parent in pending state. Lets admins approve/reject
                self-registered parents in one place. */}
            {pendingParents.length > 0 && (() => {
              // Bucket each pending parent by signup source so we can
              // both show counts on the filter chips and let admins
              // narrow the table to (e.g.) leads that came in via the
              // public welcome page.
              const bucketOf = (p) => {
                const s = (p.signup_source || '').toLowerCase();
                if (s === 'welcome_page') return 'welcome_page';
                if (s === 'branded_login') return 'branded_login';
                // Legacy self-registrations pre-dated signup_source. Any
                // parent with `is_branded_login` metadata or slug on
                // registration is still a branded-login joiner; anything
                // else lands in "other" so nothing is hidden.
                return 'other';
              };
              const counts = { welcome_page: 0, branded_login: 0, other: 0 };
              pendingParents.forEach((p) => { counts[bucketOf(p)]++; });
              const visible = pendingSourceFilter
                ? pendingParents.filter((p) => bucketOf(p) === pendingSourceFilter)
                : pendingParents;
              const SOURCE_META = {
                welcome_page: { label: '🌐 Welcome Page', bg: 'hsl(190 65% 92%)', fg: 'hsl(190 65% 25%)' },
                branded_login: { label: '🔗 Branded Login', bg: 'hsl(220 60% 94%)', fg: 'hsl(220 55% 35%)' },
                other: { label: '👤 Other', bg: 'hsl(var(--muted))', fg: 'hsl(var(--muted-foreground))' },
              };
              const chips = [
                { v: '', label: `All (${pendingParents.length})` },
                { v: 'welcome_page', label: `🌐 Welcome Page (${counts.welcome_page})` },
                { v: 'branded_login', label: `🔗 Branded Login (${counts.branded_login})` },
                { v: 'other', label: `👤 Other (${counts.other})` },
              ];
              return (
              <div
                className="card"
                style={{ marginBottom: '1.25rem', borderColor: 'hsl(45 95% 65%)' }}
                data-testid="pending-parents-card"
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <h3 style={{ margin: 0 }}>
                    ⏳ Pending parent approvals
                    <span className="badge badge-yellow" style={{ marginLeft: '0.5rem' }}>
                      {pendingParents.length}
                    </span>
                  </h3>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))' }}>
                    Prospective parents awaiting a decision.
                  </p>
                </div>

                {/* Signup-source filter chip row — surfaced only when
                    there's more than one source to choose between. Keeps
                    the UI quiet on greenfield orgs while making
                    marketing-cohort review a one-click job the moment
                    the welcome page starts pulling leads. */}
                {(counts.welcome_page + counts.branded_login + counts.other) > 0 && (
                  <div
                    style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center', marginTop: '0.75rem' }}
                    data-testid="pending-parents-source-filter"
                  >
                    <span style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Source</span>
                    {chips.map((opt) => (
                      <button
                        key={opt.v || 'all'}
                        type="button"
                        onClick={() => setPendingSourceFilter(opt.v)}
                        className={pendingSourceFilter === opt.v ? 'btn btn-primary' : 'btn btn-ghost'}
                        style={{ fontSize: '0.78rem', padding: '0.2rem 0.6rem' }}
                        data-testid={`pending-parents-source-chip-${opt.v || 'all'}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}

                <div className="table-wrap" style={{ marginTop: '0.75rem' }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Source</th>
                        <th>Registered</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.length === 0 && (
                        <tr>
                          <td colSpan={6} style={{ padding: '1rem', color: 'hsl(var(--muted-foreground))', textAlign: 'center' }}>
                            No pending parents in this cohort.
                          </td>
                        </tr>
                      )}
                      {visible.map((p) => {
                        const bucket = bucketOf(p);
                        const meta = SOURCE_META[bucket];
                        const md = p.signup_metadata || {};
                        const subtitleBits = [];
                        if (md.child_age) subtitleBits.push(`Child: ${md.child_age}`);
                        if (md.message) subtitleBits.push(`"${md.message}"`);
                        return (
                          <tr
                            key={p._id}
                            data-testid={`pending-parent-row-${p._id}`}
                            data-source={bucket}
                            onClick={() => setViewingUser(p)}
                            title="Click to open profile"
                            style={{ cursor: 'pointer' }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'hsl(var(--muted) / 0.35)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
                          >
                            <td>
                              <strong>{p.full_name || '—'}</strong>
                              {subtitleBits.length > 0 && (
                                <div
                                  style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: 2 }}
                                  data-testid={`pending-parent-meta-${p._id}`}
                                >
                                  {subtitleBits.join(' · ')}
                                </div>
                              )}
                            </td>
                            <td>{p.email}</td>
                            <td>{p.phone || 'N/A'}</td>
                            <td>
                              <span
                                data-testid={`pending-parent-source-badge-${p._id}`}
                                style={{
                                  display: 'inline-block',
                                  padding: '2px 8px',
                                  borderRadius: 4,
                                  fontSize: '0.72rem',
                                  fontWeight: 600,
                                  background: meta.bg,
                                  color: meta.fg,
                                  whiteSpace: 'nowrap',
                                }}
                              >{meta.label}</span>
                            </td>
                            <td>{p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}</td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <div className="inline-flex flex-wrap gap-2 items-center">
                                <button
                                  className="btn btn-ghost"
                                  onClick={() => setViewingUser(p)}
                                  data-testid={`view-pending-parent-${p._id}`}
                                  title="View this parent's details"
                                >
                                  👁️ View
                                </button>
                                <button
                                  className="btn btn-primary"
                                  onClick={() => handleApproveParent(p)}
                                  data-testid={`approve-parent-${p._id}`}
                                >
                                  ✅ Approve
                                </button>
                                <button
                                  className="btn btn-secondary"
                                  onClick={() => handleRejectParent(p)}
                                  data-testid={`reject-parent-${p._id}`}
                                >
                                  ❌ Reject
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              );
            })()}

            {/* Photo approvals — staff have uploaded a profile photo for
                their ID card that needs management verification before it
                appears on the badge. Same visual treatment as the pending
                parents card so managers naturally scan it for action. */}
            {pendingAvatars.length > 0 && (
              <div
                className="card"
                style={{ marginBottom: '1.25rem', borderColor: 'hsl(38 95% 60%)' }}
                data-testid="pending-avatars-card"
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <h3 style={{ margin: 0 }}>
                    📸 ID photos awaiting approval
                    <span className="badge badge-yellow" style={{ marginLeft: '0.5rem' }} data-testid="pending-avatars-count">
                      {pendingAvatars.length}
                    </span>
                  </h3>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))' }}>
                    Verify each face before it appears on a live Staff ID card.
                  </p>
                </div>
                <div className="table-wrap" style={{ marginTop: '0.75rem' }}>
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: 72 }}>Photo</th>
                        <th>Name</th>
                        <th>Role</th>
                        <th>Uploaded</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingAvatars.map((p) => (
                        <tr key={p.user_id} data-testid={`pending-avatar-row-${p.user_id}`}>
                          <td>
                            {p.avatar_pending_url ? (
                              <a
                                href={p.avatar_pending_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Open full-size photo"
                                data-testid={`pending-avatar-thumb-${p.user_id}`}
                              >
                                <img
                                  src={p.avatar_pending_url}
                                  alt={`${p.full_name}'s pending ID photo`}
                                  style={{
                                    width: 56, height: 56, borderRadius: '50%',
                                    objectFit: 'cover', display: 'block',
                                    border: '2px solid hsl(var(--border))',
                                  }}
                                />
                              </a>
                            ) : (
                              <div style={{
                                width: 56, height: 56, borderRadius: '50%',
                                background: 'hsl(var(--muted))',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontWeight: 700, color: 'hsl(var(--muted-foreground))',
                              }}>
                                {(p.full_name || '?').charAt(0).toUpperCase()}
                              </div>
                            )}
                          </td>
                          <td>
                            <strong>{p.full_name}</strong>
                            <div style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))' }}>
                              {p.email}
                            </div>
                          </td>
                          <td>{p.role_label || p.role}</td>
                          <td>
                            {p.avatar_pending_uploaded_at
                              ? new Date(p.avatar_pending_uploaded_at).toLocaleString()
                              : '—'}
                          </td>
                          <td>
                            <div className="inline-flex flex-wrap gap-2 items-center">
                              <button
                                className="btn btn-primary"
                                onClick={() => handleApprovePhoto(p)}
                                data-testid={`approve-avatar-${p.user_id}`}
                              >
                                ✅ Approve
                              </button>
                              <button
                                className="btn btn-secondary"
                                onClick={() => handleRejectPhoto(p)}
                                data-testid={`reject-avatar-${p.user_id}`}
                              >
                                ❌ Reject
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {loading ? (
              <div>Loading...</div>
            ) : users.length > 0 ? (
              <div className="card">
                {/* Search + role filter — pure client-side filters so the
                    underlying users list and any pagination/CSV helpers
                    stay in sync. */}
                <div
                  style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}
                  data-testid="users-filter-bar"
                >
                  <input
                    type="search"
                    value={usersSearch}
                    onChange={(e) => setUsersSearch(e.target.value)}
                    placeholder="Search name, email or phone…"
                    style={{ flex: 1, minWidth: 220, padding: '0.5rem 0.75rem', borderRadius: 6, border: '1px solid hsl(var(--border))' }}
                    data-testid="users-search-input"
                  />
                  <select
                    value={usersRoleFilter}
                    onChange={(e) => setUsersRoleFilter(e.target.value)}
                    style={{ padding: '0.5rem 0.75rem', borderRadius: 6, border: '1px solid hsl(var(--border))' }}
                    data-testid="users-role-filter"
                  >
                    <option value="">All roles</option>
                    <option value="parent">Parent</option>
                    <option value="staff">Staff</option>
                    <option value="admin">Admin</option>
                    <option value="org_owner">Org owner</option>
                    <option value="org_admin">Org admin</option>
                    <option value="driver">Driver</option>
                  </select>
                </div>

                {(() => {
                  const q = usersSearch.trim().toLowerCase();
                  const filtered = users.filter((u) => {
                    if (usersRoleFilter && u.role !== usersRoleFilter) return false;
                    if (!q) return true;
                    return [
                      u.full_name, u.email, u.phone,
                    ].some((v) => v && String(v).toLowerCase().includes(q));
                  });
                  if (filtered.length === 0) {
                    return (
                      <div className="empty-state" data-testid="users-empty-filtered">
                        No users match your search.
                      </div>
                    );
                  }
                  return (
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Phone</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(u => (
                      <tr
                        key={u._id}
                        onClick={() => setViewingUser(u)}
                        data-testid={`user-row-${u._id}`}
                        title="Click to open profile"
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'hsl(var(--muted) / 0.35)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
                      >
                        <td><strong>{u.full_name}</strong></td>
                        <td>{u.email}</td>
                        <td>
                          <span className={`badge badge-${
                            u.role === 'org_owner' ? 'red'
                            : u.role === 'org_admin' || u.role === 'admin' ? 'red'
                            : u.role === 'deputy_manager' ? 'yellow'
                            : u.role === 'room_leader' ? 'yellow'
                            : u.role === 'staff' ? 'blue'
                            : u.role === 'parent' ? 'green'
                            : 'gray'
                          }`} data-testid={`user-${u._id}-role-badge`}>
                            {(u.role || '').replace(/_/g, ' ')}
                          </span>
                          {u.role === 'room_leader' && u.assigned_room_id && (
                            <small style={{ display: 'block', fontSize: '0.7rem', color: 'hsl(var(--muted-foreground))', marginTop: 2 }}>
                              🚪 {rooms.find(r => r._id === u.assigned_room_id)?.name || 'assigned room'}
                            </small>
                          )}
                        </td>
                        <td>{u.phone || 'N/A'}</td>
                        <td>
                          <span className={`badge ${u.is_active ? 'badge-green' : 'badge-red'}`}>
                            {u.is_active ? 'Active' : 'Inactive'}
                          </span>
                          {u.approval_status === 'pending' && (
                            <span
                              className="badge badge-yellow"
                              style={{ marginLeft: '0.35rem' }}
                              data-testid={`pending-badge-${u._id}`}
                            >
                              Pending approval
                            </span>
                          )}
                          {u.approval_status === 'rejected' && (
                            <span
                              className="badge badge-red"
                              style={{ marginLeft: '0.35rem' }}
                              data-testid={`rejected-badge-${u._id}`}
                            >
                              Rejected
                            </span>
                          )}
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="inline-flex flex-wrap gap-2 items-center">
                            <button
                              className="btn btn-ghost"
                              onClick={() => setViewingUser(u)}
                              data-testid={`view-user-${u._id}`}
                              title="View full profile, children, enrolments and bookings"
                            >
                              👁️ View
                            </button>
                            {u.role === 'parent' && u.approval_status === 'pending' && (
                              <button
                                className="btn btn-primary"
                                onClick={() => handleApproveParent(u)}
                                data-testid={`inline-approve-${u._id}`}
                              >
                                ✅ Approve
                              </button>
                            )}
                            <button 
                              className="btn btn-secondary"
                              onClick={() => handleToggleUserActive(u._id, u.is_active)}
                              data-testid={`toggle-user-${u._id}`}
                            >
                              {u.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                            {/* Password admin — only for org owners/admins (not deputies).
                                Opens a modal with two options: set a specific value
                                (no email, no forced change) or reset to a temp value
                                that's emailed to the user + must be rotated on next
                                login. Excluded on super_admin rows because those go
                                through the dedicated super-admin flow. */}
                            {u.role !== 'super_admin'
                              && ['org_owner', 'org_admin', 'admin'].includes(user?.role)
                              && u._id !== user?._id && (
                              <button
                                className="btn btn-ghost"
                                onClick={() => setPasswordAdminUser(u)}
                                data-testid={`password-admin-${u._id}`}
                                title="Set a password directly or send a reset email"
                              >
                                🔑 Password
                              </button>
                            )}
                            {/* Role tier promotion — only show for staff-tier
                                accounts (not parents) and not for the org
                                owner whose role is locked. */}
                            {u.role !== 'parent' && u.role !== 'org_owner' && u._id !== user?._id && (
                              <button
                                className="btn btn-ghost"
                                onClick={() => setRoleTierUser(u)}
                                data-testid={`change-role-${u._id}`}
                                title="Promote or demote this team member without creating a new account"
                              >
                                🎚️ Role
                              </button>
                            )}
                            {/* Staff ID card revoke/restore — applies to all
                                staff-tier roles (not parents). Reads the
                                live revocation state from `u` so the label
                                flips between Revoke and Restore. We don't
                                offer this for parents because they don't
                                have an ID card. */}
                            {u.role !== 'parent' && (
                              <button
                                className="btn btn-ghost"
                                onClick={() => setViewingStaffIdFor({ userId: u._id, label: u.full_name })}
                                data-testid={`view-staff-id-${u._id}`}
                                title="Preview this staff member's ID badge, copy the share link or rotate the QR token"
                              >
                                🎫 View ID
                              </button>
                            )}
                            {u.role !== 'parent' && (
                              u.staff_id_revoked_at ? (
                                <button
                                  className="btn btn-ghost"
                                  onClick={async () => {
                                    try {
                                      await api.restoreStaffId(u._id);
                                      toast.success(`Restored ID card for ${u.full_name}`);
                                      const usersData = await api.getUsers();
                                      setUsers(usersData);
                                    } catch (err) {
                                      toast.error(err.message || 'Failed to restore ID');
                                    }
                                  }}
                                  data-testid={`restore-staff-id-${u._id}`}
                                  title="Re-enable this staff member's ID card"
                                >
                                  ♻ Restore ID
                                </button>
                              ) : (
                                <button
                                  className="btn btn-ghost"
                                  onClick={async () => {
                                    if (!window.confirm(`Revoke the staff ID card for ${u.full_name}? Anyone scanning their QR will see "ID revoked".`)) return;
                                    try {
                                      await api.revokeStaffId(u._id);
                                      toast.success(`Revoked ID card for ${u.full_name}`);
                                      const usersData = await api.getUsers();
                                      setUsers(usersData);
                                    } catch (err) {
                                      toast.error(err.message || 'Failed to revoke ID');
                                    }
                                  }}
                                  data-testid={`revoke-staff-id-${u._id}`}
                                  title="Invalidate this staff member's ID card"
                                >
                                  🚫 Revoke ID
                                </button>
                              )
                            )}
                            <RowActions
                              testIdPrefix={`user-${u._id}`}
                              onEdit={() => { setEditingUser(u); setShowUserModal(true); }}
                              onDelete={() => handleDeleteUser(u)}
                              confirmTitle={`Delete user ${u.full_name}?`}
                              confirmText="This permanently removes the account. Their historical actions in audit logs are preserved."
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                  );
                })()}
              </div>
            ) : (
              <div className="empty-state">No users</div>
            )}
          </>
        )}

        {/* CHILDREN TAB */}
        {activeTab === 'children' && (
          <>
            <div className="page-header">
              <h1>Children Management</h1>
              <p>{children.length} registered children</p>
              <div className="flex gap-2 flex-wrap">
                <button
                  className="btn btn-primary"
                  onClick={() => { setEditingChild(null); setShowChildModal(true); }}
                  data-testid="create-child-btn"
                >
                  + Add Child
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={() => exportToCSV(prepareChildrenDataForExport(children), 'children_list')}
                  disabled={children.length === 0}
                  data-testid="export-children-btn"
                >
                  📊 Export to CSV
                </button>
              </div>
            </div>

            {/* Search */}
            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Search Children</label>
                <input
                  type="text"
                  placeholder="Search by name, age group..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1); // Reset to first page on search
                  }}
                  data-testid="search-children-input"
                />
              </div>
            </div>

            {loading ? (
              <div>Loading...</div>
            ) : children.length > 0 ? (
              (() => {
                // Filter children
                const filteredChildren = children.filter(child =>
                  JSON.stringify(child).toLowerCase().includes(searchQuery.toLowerCase())
                );
                
                // Pagination
                const totalPages = Math.ceil(filteredChildren.length / itemsPerPage);
                const startIndex = (currentPage - 1) * itemsPerPage;
                const paginatedChildren = filteredChildren.slice(startIndex, startIndex + itemsPerPage);
                
                if (filteredChildren.length === 0) {
                  return (
                    <div className="empty-state">
                      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
                      <h3>No Results Found</h3>
                      <p>Try adjusting your search</p>
                    </div>
                  );
                }
                
                return (
                  <>
                    <div className="card">
                      <table>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Age / Year</th>
                            <th>Room</th>
                            <th>School</th>
                            <th>Care Needs</th>
                            <th>Parent</th>
                            <th>Registered</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedChildren.map(child => (
                            <tr
                              key={child._id}
                              onClick={() => setViewingChildId(child._id)}
                              data-testid={`child-row-${child._id}`}
                              title="Click to open profile"
                              style={{
                                cursor: 'pointer',
                                ...(child.is_active === false ? { opacity: 0.55 } : {}),
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = 'hsl(var(--muted) / 0.35)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
                            >
                              <td>
                                <strong>{child.first_name} {child.last_name}</strong>
                                {child.is_active === false && (
                                  <span className="ml-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded">Archived</span>
                                )}
                              </td>
                              <td>
                                {(() => {
                                  const yg = ukYearGroup(child.date_of_birth);
                                  const age = ageLabel(child.date_of_birth);
                                  return (
                                    <div data-testid={`child-row-${child._id}-age-year`}>
                                      <div style={{ fontSize: '0.85rem' }}>{age || '—'}</div>
                                      {yg && (
                                        <span className="badge badge-blue" style={{ fontSize: '0.7rem' }}>
                                          {yg.label}
                                          {yg.key_stage && yg.key_stage !== '—' && (
                                            <span style={{ marginLeft: 4, opacity: 0.8 }}>· {yg.key_stage}</span>
                                          )}
                                        </span>
                                      )}
                                      <div style={{ fontSize: '0.7rem', color: 'hsl(var(--muted-foreground))' }}>
                                        DOB {new Date(child.date_of_birth).toLocaleDateString()}
                                      </div>
                                    </div>
                                  );
                                })()}
                              </td>
                              <td>
                                {/* Inline room quick-assign — admins can move
                                    a child between rooms without opening the
                                    full Edit modal. The select PUTs to
                                    /api/children/{id} on change. */}
                                <select
                                  value={child.room_id || ''}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={async (e) => {
                                    e.stopPropagation();
                                    const newRoom = e.target.value || null;
                                    try {
                                      await api.updateChild(child._id, { room_id: newRoom });
                                      // Mutate locally to avoid a full reload
                                      setChildren((prev) => prev.map((c) =>
                                        c._id === child._id ? { ...c, room_id: newRoom } : c
                                      ));
                                    } catch (err) {
                                      alert('Failed to move child: ' + err.message);
                                    }
                                  }}
                                  data-testid={`child-room-quick-${child._id}`}
                                  style={{
                                    fontSize: '0.85rem', padding: '0.25rem 0.4rem',
                                    borderRadius: 4, border: '1px solid hsl(var(--border))',
                                    maxWidth: 160,
                                  }}
                                  disabled={rooms.length === 0}
                                  title={rooms.length === 0 ? 'No rooms yet — create one in Staff Ops → Rooms' : 'Move to another room'}
                                >
                                  <option value="">— Unassigned —</option>
                                  {rooms.map((r) => (
                                    <option key={r._id} value={r._id}>{r.name}</option>
                                  ))}
                                </select>
                              </td>
                              <td>
                                {/* School + collection point — shows the
                                    child's school + class so admins can
                                    spot transport-relevant kids at a glance. */}
                                {child.school_info?.school_name ? (
                                  <div data-testid={`child-row-${child._id}-school`} style={{ fontSize: '0.8rem' }}>
                                    <strong>{child.school_info.school_name}</strong>
                                    {child.school_info.school_class && (
                                      <span style={{ marginLeft: 4, color: 'hsl(var(--muted-foreground))' }}>
                                        · {child.school_info.school_class}
                                      </span>
                                    )}
                                    {child.school_info.school_meeting_point && (
                                      <div style={{ fontSize: '0.7rem', color: 'hsl(var(--muted-foreground))' }} title="Driver collection location">
                                        🚌 {child.school_info.school_meeting_point}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <small style={{ color: 'hsl(var(--muted-foreground))' }}>—</small>
                                )}
                              </td>
                              <td>
                                {/* SEND, dietary and allergy flags inline so
                                    admins can spot at-risk children without
                                    opening every profile. Renders nothing
                                    when the child has no flags. */}
                                <ChildCareFlags child={child} size="xs" testIdPrefix={`child-row-${child._id}-`} />
                                {!(child.allergies?.length || child.dietary_requirements?.length || child.special_needs?.has_sen) && (
                                  <small style={{ color: 'hsl(var(--muted-foreground))' }}>—</small>
                                )}
                              </td>
                              <td>
                                {/* Parent UUID was confusing — show the parent's
                                    name + email and make it click-through to
                                    the user-detail drawer for full context. */}
                                {(() => {
                                  const parent = users.find((u) => u._id === child.parent_id);
                                  if (!parent) {
                                    return <small style={{ color: 'hsl(var(--muted-foreground))' }}>{child.parent_id || '—'}</small>;
                                  }
                                  return (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); setViewingUser(parent); }}
                                      data-testid={`child-parent-link-${child._id}`}
                                      style={{
                                        background: 'transparent', border: 0, padding: 0,
                                        color: 'hsl(var(--primary))', cursor: 'pointer',
                                        textAlign: 'left',
                                      }}
                                      title="View this parent's full record"
                                    >
                                      <strong style={{ fontSize: '0.85rem' }}>{parent.full_name || parent.email}</strong>
                                      {parent.email && parent.full_name && (
                                        <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                                          {parent.email}
                                        </div>
                                      )}
                                    </button>
                                  );
                                })()}
                              </td>
                              <td><small>{new Date(child.created_at).toLocaleDateString()}</small></td>
                              <td>
                                <div style={{ display: 'inline-flex', gap: '0.5rem', alignItems: 'center' }}>
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setEditingChild(child); setShowChildModal(true); }}
                                    className="px-2 py-1 text-xs rounded-md border border-cyan-200 bg-cyan-50 text-cyan-800 hover:bg-cyan-100 font-medium"
                                    data-testid={`child-${child._id}-edit-btn`}
                                  >
                                    ✏️ Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setDeletingChild(child); }}
                                    className="px-2 py-1 text-xs rounded-md border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 font-medium"
                                    data-testid={`child-${child._id}-delete-btn`}
                                  >
                                    🗑️ Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        gap: '1rem',
                        marginTop: '1.5rem'
                      }}>
                        <button
                          className="btn btn-secondary"
                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                          data-testid="prev-page-btn"
                        >
                          ← Previous
                        </button>
                        <span>
                          Page {currentPage} of {totalPages} ({filteredChildren.length} results)
                        </span>
                        <button
                          className="btn btn-secondary"
                          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                          disabled={currentPage === totalPages}
                          data-testid="next-page-btn"
                        >
                          Next →
                        </button>
                      </div>
                    )}
                  </>
                );
              })()
            ) : (
              <div className="empty-state">No children registered</div>
            )}
          </>
        )}

        {/* EVENTS TAB — date-bound things (trips, themed days, visitors,
            parent sessions, celebrations). See /app/frontend/src/components/EventsPage.js */}
        {activeTab === 'events' && (
          <EventsPage currentUser={user} />
        )}

        {/* GET STARTED TAB — 8-step onboarding checklist (Phase K.5) */}
        {activeTab === 'get-started' && (
          <GettingStartedChecklist user={user} onNavigate={(path) => {
            try {
              const url = new URL(path, window.location.origin);
              const t = url.searchParams.get('tab');
              if (t) { setActiveTab(t); return; }
            } catch (_) { /* fall through */ }
            window.location.assign(path);
          }} />
        )}

        {/* BILLING TAB — Mollie subscriptions, plan card, usage gauges */}
        {activeTab === 'billing' && ['admin', 'org_owner', 'org_admin'].includes(user?.role) && (
          <>
            <div className="page-header">
              <h1>💳 Billing</h1>
              <p>Manage your subscription, payment method, and invoices.</p>
            </div>
            <BillingTab />
          </>
        )}

        {/* ACTIVITIES TAB */}
        {activeTab === 'activities' && (
          <>
            <div className="page-header">
              <h1>Activities Management</h1>
              <button 
                className="btn btn-primary" 
                onClick={() => setShowActivityModal(true)}
                data-testid="create-activity-btn"
              >
                + Create Activity
              </button>
            </div>

            {loading ? (
              <div>Loading...</div>
            ) : activities.length > 0 ? (
              <div className="card-grid">
                {activities.map(activity => (
                  <div key={activity._id} className="card">
                    <div style={{ fontSize: '3rem', textAlign: 'center', marginBottom: '1rem' }}>
                      {activity.icon}
                    </div>
                    <h3 style={{ textAlign: 'center', marginBottom: '0.5rem' }}>{activity.name}</h3>
                    <p style={{ textAlign: 'center', color: 'hsl(var(--muted-foreground))', fontSize: '0.875rem' }}>
                      {activity.description}
                    </p>
                    <div style={{ 
                      marginTop: '1rem', 
                      height: '4px', 
                      borderRadius: '2px', 
                      background: activity.color 
                    }}></div>
                    <div className="mt-3 flex justify-center">
                      <RowActions
                        testIdPrefix={`activity-${activity._id}`}
                        onEdit={() => { setEditingActivity(activity); setShowActivityModal(true); }}
                        onDelete={() => handleDeleteActivity(activity)}
                        confirmTitle={`Delete activity "${activity.name}"?`}
                        confirmText="Activities used in holiday club days cannot be deleted until those references are removed."
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <p>No activities created</p>
                <button className="btn btn-primary" onClick={() => setShowActivityModal(true)}>Create First Activity</button>
              </div>
            )}
          </>
        )}

        {/* MEALS TAB — Phase Meals */}
        {activeTab === 'meals' && (
          <>
            <div className="page-header">
              <h1>🍽️ Meals &amp; Menu Planner</h1>
              <p>Build a 2/3/4-week rotating menu. Parents see the published week with per-child allergy alerts.</p>
            </div>
            <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
              <button
                className={`btn ${mealsSubTab === 'library' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setMealsSubTab('library')}
                data-testid="meals-tab-library"
              >
                Meal Library
              </button>
              <button
                className={`btn ${mealsSubTab === 'planner' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setMealsSubTab('planner')}
                data-testid="meals-tab-planner"
              >
                Menu Planner
              </button>
              <button
                className={`btn ${mealsSubTab === 'today' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setMealsSubTab('today')}
                data-testid="meals-tab-today"
              >
                Today / Override
              </button>
            </div>
            {mealsSubTab === 'library' && <MealsManager mode="admin-library" />}
            {mealsSubTab === 'planner' && <MealsManager mode="admin-planner" />}
            {mealsSubTab === 'today' && <MealsManager mode="admin-today" />}
          </>
        )}

        {/* WAITLIST TAB */}
        {activeTab === 'waitlist' && (
          <>
            <div className="page-header">
              <h1>Waitlist Management</h1>
              <p>{waitlist.length} total entries</p>
            </div>

            {loading ? (
              <div>Loading...</div>
            ) : waitlist.length > 0 ? (
              <div className="card">
                <table>
                  <thead>
                    <tr>
                      <th>Position</th>
                      <th>Session</th>
                      <th>Date</th>
                      <th>Parent ID</th>
                      <th>Child ID</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Expires</th>
                    </tr>
                  </thead>
                  <tbody>
                    {waitlist.map(entry => (
                      <tr key={entry._id}>
                        <td><strong>#{entry.position}</strong></td>
                        <td>{entry.session_name || 'N/A'}</td>
                        <td>{entry.session_date ? new Date(entry.session_date).toLocaleDateString() : 'N/A'}</td>
                        <td><small>{entry.parent_id}</small></td>
                        <td><small>{entry.child_id}</small></td>
                        <td>
                          <span className={`badge ${
                            entry.status === 'waiting' ? 'badge-yellow' :
                            entry.status === 'offered' ? 'badge-green' :
                            entry.status === 'claimed' ? 'badge-blue' :
                            'badge-red'
                          }`}>
                            {entry.status}
                          </span>
                        </td>
                        <td><small>{new Date(entry.created_at).toLocaleDateString()}</small></td>
                        <td><small>{entry.offer_expires_at ? new Date(entry.offer_expires_at).toLocaleString() : '-'}</small></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">No waitlist entries</div>
            )}
          </>
        )}

        {/* FINANCIAL TAB */}
        {activeTab === 'financial' && (
          <>
            <div className="page-header">
              <h1>💰 Financial Intelligence</h1>
              <p>P&L, Attendance Trends & Business Analytics</p>
              <button 
                className="btn btn-primary"
                onClick={() => exportToCSV(prepareFinancialDataForExport(financialReport), 'financial_report')}
                disabled={!financialReport || financialSubTab !== 'overview'}
                data-testid="export-financial-btn"
              >
                📊 Export to CSV
              </button>
            </div>

            {/* Sub-tab switcher: Overview (P&L) vs Payroll Center.
                Payroll is a fundamentally financial operation, so it
                lives under Financial rather than getting its own
                top-level sidebar item. */}
            <div
              style={{
                display: 'flex',
                gap: '0.5rem',
                borderBottom: '1px solid hsl(var(--border))',
                marginBottom: '1.25rem',
              }}
              data-testid="financial-subtabs"
            >
              {[
                { v: 'overview', label: '📈 Overview' },
                { v: 'payroll',  label: '💷 Payroll' },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setFinancialSubTab(opt.v)}
                  data-testid={`financial-subtab-${opt.v}`}
                  style={{
                    padding: '0.55rem 1rem',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: financialSubTab === opt.v ? '3px solid hsl(var(--primary))' : '3px solid transparent',
                    color: financialSubTab === opt.v ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))',
                    fontWeight: financialSubTab === opt.v ? 600 : 500,
                    cursor: 'pointer',
                    marginBottom: '-1px',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {financialSubTab === 'payroll' ? (
              <PayrollCenter />
            ) : loading ? (
              <div className="loading">Loading financial data...</div>
            ) : (
              <>
                {/* Pending Payments — PAYG bookings waiting on card
                    checkout or bank-transfer confirmation. Highest
                    priority slot so cash-flow issues are obvious. */}
                <PendingPaymentsSection />

                {/* Grouped Payment Intents (baskets) — admin can refund
                    whole basket or specific bookings from here. */}
                <PaymentIntentsSection />

                {/* Invoicing (Phase AC) — create/edit/download/mark-paid
                    invoices with a branded ReportLab PDF. Sits between
                    payment intents and P&L so admins can move from
                    "money coming in" to "money we've billed for". */}
                <InvoicesSection parents={users.filter((u) => u.role === 'parent')} />

                {/* Bulk Invoice Run button — the quick "generate this
                    month for everyone" flow. Rendered above the
                    recurring schedules table so operators can pick
                    "one-shot" vs "keep it running". */}
                <div style={{ marginTop: '1rem' }}>
                  <BulkInvoiceRunButton />
                </div>

                {/* Recurring invoice schedules. Wakes every 15 min. */}
                <RecurringSchedulesSection parents={users.filter((u) => u.role === 'parent')} />

                {/* P&L Summary Cards */}
                {financialReport && financialReport.profit && (
                  <div style={{ marginBottom: '2rem' }}>
                    <h3 style={{ marginBottom: '1rem' }}>Profit & Loss Summary</h3>
                    <div className="card-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
                      <div 
                        className="stat-card"
                        onClick={() => {
                          // Scroll to the transactions section if it's
                          // rendered; otherwise show a graceful toast so
                          // the click doesn't feel like a dead button
                          // (fresh orgs with zero ledger entries hit this
                          // path).
                          const transactionsTable = document.querySelector('[data-section="transactions"]');
                          if (transactionsTable) {
                            transactionsTable.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          } else {
                            toast.info('No transactions yet', {
                              description: 'This section will populate as bookings are paid and expenses are logged.',
                            });
                          }
                        }}
                        data-testid="finance-total-revenue-card"
                        style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-4px)';
                          e.currentTarget.style.boxShadow = '0 8px 24px rgba(16,24,40,0.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '';
                        }}
                      >
                        <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.25rem' }}>
                          TOTAL REVENUE
                        </div>
                        <div style={{ fontSize: '2rem', fontWeight: '700', color: 'hsl(145 70% 35%)' }}>
                          £{financialReport.revenue?.total?.toFixed(2) || '0.00'}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
                          Booking Revenue
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem' }}>
                          Click to view transactions →
                        </div>
                      </div>
                      
                      <div 
                        className="stat-card"
                        onClick={() => setActiveTab('staff')}
                        style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-4px)';
                          e.currentTarget.style.boxShadow = '0 8px 24px rgba(16,24,40,0.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '';
                        }}
                      >
                        <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.25rem' }}>
                          TOTAL COSTS
                        </div>
                        <div style={{ fontSize: '2rem', fontWeight: '700', color: 'hsl(0 70% 50%)' }}>
                          £{financialReport.costs?.total?.toFixed(2) || '0.00'}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
                          Staff: £{financialReport.costs?.staff_costs?.toFixed(2) || '0'} • 
                          Materials: £{financialReport.costs?.consumable_costs?.toFixed(2) || '0'}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem' }}>
                          Click to view staff ops →
                        </div>
                      </div>
                      
                      <div 
                        className="stat-card" 
                        style={{ 
                          background: financialReport.profit?.net >= 0 ? 'hsl(145 63% 92%)' : 'hsl(0 93% 94%)',
                          cursor: 'pointer',
                          transition: 'transform 0.2s, box-shadow 0.2s'
                        }}
                        data-testid="finance-net-profit-card"
                        onClick={() => {
                          const transactionsTable = document.querySelector('[data-section="transactions"]');
                          if (transactionsTable) {
                            transactionsTable.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          } else {
                            toast.info('No transactions yet', {
                              description: 'Profit will update automatically as revenue and costs are recorded.',
                            });
                          }
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-4px)';
                          e.currentTarget.style.boxShadow = '0 8px 24px rgba(16,24,40,0.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '';
                        }}
                      >
                        <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.25rem' }}>
                          NET PROFIT
                        </div>
                        <div style={{ 
                          fontSize: '2rem', 
                          fontWeight: '700',
                          color: financialReport.profit?.net >= 0 ? 'hsl(145 70% 22%)' : 'hsl(0 72% 30%)'
                        }}>
                          £{financialReport.profit?.net?.toFixed(2) || '0.00'}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
                          Margin: {financialReport.profit?.margin_percent?.toFixed(1) || '0'}%
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem' }}>
                          Click to view details →
                        </div>
                      </div>
                      
                      <div 
                        className="stat-card"
                        onClick={() => setActiveTab('staff')}
                        style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-4px)';
                          e.currentTarget.style.boxShadow = '0 8px 24px rgba(16,24,40,0.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '';
                        }}
                      >
                        <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.25rem' }}>
                          OPERATIONAL DATA
                        </div>
                        <div style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                          {financialReport.costs?.shifts_count || 0} Shifts
                        </div>
                        <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
                          {financialReport.costs?.consumables_count || 0} Consumable logs
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem' }}>
                          Click to manage staff →
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Attendance & Utilization */}
                {financialReport && financialReport.summary && (
                  <div style={{ marginBottom: '2rem' }}>
                    <h3 style={{ marginBottom: '1rem' }}>Attendance & Capacity Utilization</h3>
                    <div className="card" style={{ padding: '1.5rem' }}>
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
                        gap: '1.5rem',
                        marginBottom: '1.5rem'
                      }}>
                        <div 
                          onClick={() => setActiveTab('sessions')}
                          style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                        >
                          <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.5rem' }}>
                            Total Capacity
                          </div>
                          <div style={{ fontSize: '1.75rem', fontWeight: '700' }}>
                            {financialReport.summary.total_capacity || 0}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>
                            Click to manage sessions →
                          </div>
                        </div>
                        <div 
                          onClick={() => setActiveTab('bookings')}
                          style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                        >
                          <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.5rem' }}>
                            Total Booked
                          </div>
                          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'hsl(220 90% 60%)' }}>
                            {financialReport.summary.total_booked || 0}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>
                            Click to view bookings →
                          </div>
                        </div>
                        <div 
                          onClick={() => setActiveTab('sessions')}
                          style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                        >
                          <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.5rem' }}>
                            Ghost Slots
                          </div>
                          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'hsl(0 70% 50%)' }}>
                            {financialReport.summary.total_ghost_slots || 0}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>
                            Revenue opportunity →
                          </div>
                        </div>
                        <div 
                          onClick={() => setActiveTab('sessions')}
                          style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                        >
                          <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.5rem' }}>
                            Utilization
                          </div>
                          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'hsl(145 70% 35%)' }}>
                            {financialReport.summary.average_utilization_percent || 0}%
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>
                            Click for details →
                          </div>
                        </div>
                      </div>

                      {/* Utilization Progress Bar */}
                      <div style={{ 
                        height: '24px', 
                        background: 'hsl(var(--muted))', 
                        borderRadius: '12px',
                        overflow: 'hidden',
                        position: 'relative'
                      }}>
                        <div style={{
                          height: '100%',
                          width: `${financialReport.summary.average_utilization_percent || 0}%`,
                          background: 'linear-gradient(90deg, hsl(220 90% 60%), hsl(168 76% 32%))',
                          transition: 'width 0.3s ease'
                        }} />
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: 'hsl(var(--foreground))',
                          mixBlendMode: 'difference'
                        }}>
                          {financialReport.summary.average_utilization_percent || 0}% Utilized
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Outstanding Balances */}
                {financialReport && financialReport.outstanding_balances && financialReport.outstanding_balances.length > 0 && (
                  <div className="card" style={{ marginBottom: '2rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <h3 style={{ margin: 0 }}>Outstanding Balances</h3>
                      <span className="badge badge-red">
                        {financialReport.outstanding_balances.length} accounts
                      </span>
                    </div>
                    <table>
                      <thead>
                        <tr>
                          <th>Parent</th>
                          <th style={{ textAlign: 'right' }}>Balance Due</th>
                        </tr>
                      </thead>
                      <tbody>
                        {financialReport.outstanding_balances.map(item => (
                          <tr key={item.parent_id}>
                            <td>{item.parent_name}</td>
                            <td style={{ textAlign: 'right' }}>
                              <strong style={{ color: 'hsl(0 70% 50%)' }}>
                                £{item.balance.toFixed(2)}
                              </strong>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Recent Transactions — always renders when a financial
                    report is loaded (empty state included) so the stat-
                    card "Click to view transactions →" click target
                    always has somewhere to land. */}
                {financialReport && (
                  <div className="card" data-section="transactions">
                    <h3 style={{ marginBottom: '1rem' }}>Recent Transactions</h3>
                    {(financialReport.entries && financialReport.entries.length > 0) ? (
                      <table>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Parent</th>
                            <th>Description</th>
                            <th>Type</th>
                            <th style={{ textAlign: 'right' }}>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {financialReport.entries.slice(0, 20).map(entry => (
                            <tr key={entry._id}>
                              <td>{new Date(entry.created_at).toLocaleDateString()}</td>
                              <td>{entry.parent_name || 'Unknown'}</td>
                              <td>{entry.description}</td>
                              <td>
                                <span className={`badge ${entry.type === 'charge' ? 'badge-red' : 'badge-green'}`}>
                                  {entry.type}
                                </span>
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: '600' }}>
                                £{entry.amount.toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="empty-state" style={{ padding: '1.25rem 0.5rem' }}>
                        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🧾</div>
                        <p style={{ color: 'hsl(var(--muted-foreground))', margin: 0 }}>
                          No transactions yet for this period.
                        </p>
                        <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.85rem', marginTop: '0.35rem' }}>
                          Rows will appear here as bookings are paid and expenses are logged.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {!financialReport && (
                  <div className="empty-state">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💰</div>
                    <h3>No Financial Data</h3>
                    <p style={{ color: 'hsl(var(--muted-foreground))' }}>
                      Create some bookings to see financial reports.
                    </p>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* MOMENTS TAB */}
        {activeTab === 'moments' && (
          <>
            <div className="page-header">
              <h1>Moments Management</h1>
              <button 
                className="btn btn-primary" 
                onClick={() => setShowMomentModal(true)}
                data-testid="create-moment-btn"
              >
                + Post Moment
              </button>
            </div>

            {loading ? (
              <div>Loading...</div>
            ) : moments.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.5rem' }}>
                {moments.map(moment => {
                  // Distinguish image vs video by MIME type. Falls back to
                  // treating the URL as an image if `photo_content_type`
                  // is missing (legacy moments posted before we started
                  // tracking it).
                  const contentType = moment.photo_content_type || '';
                  const isVideo = contentType.startsWith('video/');
                  const hasMedia = Boolean(moment.photo_url);
                  return (
                    <div
                      key={moment._id}
                      className="card"
                      role="button"
                      tabIndex={0}
                      onClick={() => setViewingMoment(moment)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setViewingMoment(moment);
                        }
                      }}
                      data-testid={`moment-card-${moment._id}`}
                      title="Click to view this moment"
                      style={{
                        cursor: 'pointer',
                        overflow: 'hidden',
                        padding: 0,
                        transition: 'transform 120ms ease, box-shadow 120ms ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.08)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = '';
                        e.currentTarget.style.boxShadow = '';
                      }}
                    >
                      {/* Media thumbnail — image or video preview at the
                          top of the card. Presigned URL is refreshed on
                          every GET /api/moments so it will not expire
                          under a normal admin session. */}
                      {hasMedia && (
                        isVideo ? (
                          <video
                            src={moment.photo_url}
                            preload="metadata"
                            muted
                            data-testid={`moment-card-video-${moment._id}`}
                            style={{
                              width: '100%', height: 220,
                              objectFit: 'cover',
                              background: '#000',
                              display: 'block',
                              pointerEvents: 'none',
                            }}
                          />
                        ) : (
                          <img
                            src={moment.photo_url}
                            alt={moment.caption || 'Moment photo'}
                            data-testid={`moment-card-image-${moment._id}`}
                            style={{
                              width: '100%', height: 220,
                              objectFit: 'cover',
                              display: 'block',
                            }}
                            loading="lazy"
                          />
                        )
                      )}
                      <div style={{ padding: '1rem' }}>
                        <div style={{ marginBottom: '0.75rem' }}>
                          <h3 style={{ margin: 0, fontSize: '1.05rem' }}>{moment.child_name}</h3>
                          <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                            Posted by {moment.posted_by_name} • {new Date(moment.created_at).toLocaleDateString()}
                          </small>
                        </div>
                        <p
                          style={{
                            margin: '0 0 0.5rem 0',
                            fontSize: '0.925rem',
                            // Clamp long captions on the card — the full
                            // caption is shown in the detail modal.
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {moment.caption}
                        </p>
                        {moment.tags && moment.tags.length > 0 && (
                          <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                            {moment.tags.map((tag, i) => (
                              <span key={i} className="badge badge-blue" style={{ fontSize: '0.72rem' }}>#{tag}</span>
                            ))}
                          </div>
                        )}
                        {moment.comment_count > 0 && (
                          <div
                            style={{
                              marginTop: '0.5rem',
                              fontSize: '0.78rem',
                              color: 'hsl(var(--muted-foreground))',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                            }}
                            data-testid={`moment-card-comment-count-${moment._id}`}
                          >
                            💬 {moment.comment_count} {moment.comment_count === 1 ? 'reply' : 'replies'}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">
                <p>No moments posted yet</p>
                <button className="btn btn-primary" onClick={() => setShowMomentModal(true)}>Post First Moment</button>
              </div>
            )}
          </>
        )}

        {/* TRANSPORT MANAGEMENT TAB */}
        {activeTab === 'transport' && (
          <>
            <div className="page-header">
              <h1>🚐 School Transport Management</h1>
              <p>Generate and view daily route manifests</p>
            </div>

            {/* Sub-tab toggle: per-route Manifests view vs Master Daily Schedule */}
            <div
              style={{
                display: 'flex',
                gap: '0.5rem',
                marginBottom: '1.25rem',
                flexWrap: 'wrap',
              }}
              data-testid="transport-subtab-toggle"
            >
              <button
                className={`btn ${transportSubTab === 'manifests' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setTransportSubTab('manifests')}
                data-testid="transport-subtab-manifests"
              >
                📋 Route Manifests
              </button>
              <button
                className={`btn ${transportSubTab === 'schedule' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setTransportSubTab('schedule')}
                data-testid="transport-subtab-schedule"
              >
                🗓 Daily Transport Schedule
              </button>
            </div>

            {/* Shared date picker + generate button (visible on both sub-tabs) */}
            <div className="card" style={{ marginBottom: '2rem' }}>
              <h3 style={{ marginBottom: '1rem' }}>Generate Daily Routes</h3>
              <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', marginBottom: '1rem' }}>
                Generate transport route manifests for a specific date based on confirmed bookings.
              </p>
              
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
                <div className="form-group" style={{ margin: 0, flex: 1, maxWidth: '300px' }}>
                  <label>Target Date</label>
                  <input 
                    type="date" 
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    data-testid="transport-date-selector"
                  />
                </div>
                <button 
                  className="btn btn-primary" 
                  onClick={async () => {
                    setIsGeneratingRoutes(true);
                    try {
                      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/manifests/generate`, {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${localStorage.getItem('token')}`,
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({ target_date: selectedDate })
                      });
                      
                      if (response.ok) {
                        const result = await response.json();
                        alert(`Routes generated successfully!\n\nTotal manifests: ${result.total_manifests}\n${Object.entries(result.by_route_type).map(([type, count]) => `${type}: ${count}`).join('\n')}`);
                        loadData(); // Reload manifest data
                      } else {
                        const error = await response.json();
                        alert('Error generating routes: ' + (error.detail || 'Unknown error'));
                      }
                    } catch (err) {
                      alert('Error: ' + err.message);
                    }
                    setIsGeneratingRoutes(false);
                  }}
                  disabled={isGeneratingRoutes}
                  data-testid="generate-routes-btn"
                >
                  {isGeneratingRoutes ? '⏳ Generating...' : '🔄 Generate Routes'}
                </button>
              </div>
            </div>

            {/* Manifests Display */}
            {transportSubTab === 'manifests' && (
            <div className="card">
              <h3 style={{ marginBottom: '1rem' }}>
                Route Manifests for {new Date(selectedDate).toLocaleDateString()}
              </h3>
              
              {loading ? (
                <div className="loading">Loading manifests...</div>
              ) : manifests.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {manifests.map((manifest) => {
                    const getStatusBadgeClass = (status) => {
                      const classes = {
                        'pending': 'badge-yellow',
                        'in_progress': 'badge-blue',
                        'completed': 'badge-green'
                      };
                      return classes[status] || 'badge-gray';
                    };

                    const getRouteTypeLabel = (routeType) => {
                      const labels = {
                        'home_to_school': '🌅 Home → School',
                        'club_to_school': '🌅 Centre → School',
                        'school_to_club': '🏫 School → Centre',
                        'school_to_home': '🏫 School → Home',
                        'club_to_home': '🌆 Centre → Home',
                      };
                      return labels[routeType] || routeType;
                    };

                    return (
                      <div 
                        key={manifest._id || manifest.id} 
                        className="card" 
                        style={{ 
                          borderLeft: `4px solid ${
                            manifest.status === 'completed' ? 'hsl(145 63% 42%)' :
                            manifest.status === 'in_progress' ? 'hsl(220 90% 60%)' :
                            'hsl(45 92% 50%)'
                          }`,
                          background: 'hsl(var(--background))'
                        }}
                        data-testid={`manifest-${manifest._id || manifest.id}`}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <h4 style={{ margin: '0 0 0.5rem 0' }}>
                              {getRouteTypeLabel(manifest.route_type)}
                            </h4>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                              <span className={`badge ${getStatusBadgeClass(manifest.status)}`}>
                                {manifest.status.replace('_', ' ').toUpperCase()}
                              </span>
                              <span style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
                                {manifest.departure_time} → {manifest.target_completion_time}
                              </span>
                            </div>
                            
                            <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.5rem' }}>
                              {manifest.start_point} → {manifest.end_point}
                            </div>
                            
                            <div style={{ fontSize: '0.875rem' }}>
                              <strong>{manifest.total_children}</strong> children • 
                              <strong> {manifest.school_stops ? manifest.school_stops.length : 0}</strong> school stops •
                              <strong> {manifest.children_collected}/{manifest.total_children}</strong> collected
                            </div>

                            {/* Driver + Escort Assignment Section (Phase D) */}
                            <ManifestDriverAssignment
                              manifest={manifest}
                              eligibleDrivers={transportEligibleDrivers}
                              allStaff={users.filter(u => (u.role || '').toLowerCase() !== 'parent')}
                              onUpdated={loadData}
                            />

                            {/* School Stops Summary */}
                            {manifest.school_stops && manifest.school_stops.length > 0 && (
                              <details style={{ marginTop: '1rem' }}>
                                <summary style={{ cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }}>
                                  View School Stops & Children
                                </summary>
                                <div style={{ marginTop: '1rem', paddingLeft: '1rem' }}>
                                  {manifest.school_stops.map((stop, idx) => (
                                    <div 
                                      key={idx} 
                                      style={{ 
                                        marginBottom: '1rem',
                                        padding: '1rem',
                                        background: 'hsl(var(--muted))',
                                        borderRadius: '6px'
                                      }}
                                    >
                                      <div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>
                                        {stop.school_name}
                                      </div>
                                      <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.75rem' }}>
                                        {stop.school_address} • {stop.drop_off_point || 'Main gate'}
                                      </div>
                                      {stop.children && stop.children.length > 0 && (
                                        <div>
                                          <div style={{ fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                                            CHILDREN ({stop.children.length}):
                                          </div>
                                          <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.875rem' }}>
                                            {stop.children.map(child => (
                                              <li key={child.child_id}>
                                                {child.child_name}
                                                {child.school_class && ` (${child.school_class})`}
                                                {child.teacher_name && ` - Teacher: ${child.teacher_name}`}
                                                {child.collected && <span style={{ color: 'hsl(145 70% 22%)' }}> ✓</span>}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </details>
                            )}
                          </div>
                          
                          <div style={{ fontSize: '2rem', marginLeft: '1rem' }}>
                            {manifest.status === 'completed' ? '✓' : 
                             manifest.status === 'in_progress' ? '▶' : '⏸'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="empty-state">
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚐</div>
                  <h3>No routes generated</h3>
                  <p style={{ color: 'hsl(var(--muted-foreground))' }}>
                    Click "Generate Routes" above to create transport manifests for {new Date(selectedDate).toLocaleDateString()}.
                  </p>
                </div>
              )}
            </div>
            )}

            {/* Master Daily Transport Schedule sub-tab */}
            {transportSubTab === 'schedule' && (
              <DailyTransportSchedule
                schedule={dailySchedule}
                loading={loading}
                selectedDate={selectedDate}
              />
            )}
          </>
        )}

        {/* ORGANIZATION MANAGEMENT TAB */}
        {activeTab === 'organization' && (
          <>
            <div className="page-header">
              <h1>🏢 Organization Settings</h1>
              <p>Manage your organization profile and locations</p>
            </div>

            {/* Sub-tab pill bar — splits 20+ cards across 6 focused
                sections so the page stays scannable and maintenance
                actions (Account Cleanup, etc.) don't jump to the top
                of a routine visit. Same visual language as the Staff
                Ops and Financial sub-tabs. */}
            <div
              data-testid="org-subtabs"
              style={{
                display: 'flex', gap: '0.4rem',
                marginBottom: '1.25rem',
                flexWrap: 'wrap',
                borderBottom: '1px solid hsl(var(--border))',
                paddingBottom: '0.5rem',
              }}
            >
              {[
                ['profile',      '🏢 Profile',           null],
                ['public',       '🎨 Public Presence',   null],
                ['billing',      '💰 Billing & Payments', null],
                ['staff',        '👥 Staff & Compliance', null],
                ['preferences',  '⚙️ Preferences',       null],
                ['maintenance',  '🔧 Maintenance',       'danger'],
              ].map(([key, label, variant]) => {
                const active = orgSubTab === key;
                const isDanger = variant === 'danger';
                // Badge dictionary — extend here as other sub-tabs
                // grow their own attention counters.
                const badgeCount = key === 'public' ? policyPlaceholderCount : 0;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setOrgSubTab(key)}
                    className={`sub-tab ${active ? 'active' : ''}`}
                    data-testid={`org-subtab-${key}`}
                    aria-pressed={active}
                    style={{
                      padding: '0.55rem 1.05rem',
                      background: active
                        ? (isDanger ? 'hsl(0 65% 45%)' : 'hsl(var(--primary))')
                        : 'transparent',
                      color: active
                        ? (isDanger ? '#fff' : 'hsl(var(--primary-foreground))')
                        : (isDanger ? 'hsl(0 65% 40%)' : 'hsl(var(--foreground))'),
                      border: active
                        ? 'none'
                        : `1px solid ${isDanger ? 'hsl(0 65% 45% / 0.35)' : 'hsl(var(--border))'}`,
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '0.88rem',
                      transition: 'background 160ms ease, color 160ms ease, transform 120ms ease',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                    }}
                  >
                    <span>{label}</span>
                    {badgeCount > 0 && (
                      <span
                        data-testid={`org-subtab-${key}-badge`}
                        title={`${badgeCount} polic${badgeCount === 1 ? 'y contains' : 'ies contain'} unresolved placeholders — click to review.`}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: 20,
                          height: 20,
                          padding: '0 0.4rem',
                          borderRadius: 999,
                          background: active ? 'hsl(0 0% 100% / 0.22)' : 'hsl(0 78% 50%)',
                          color: active ? '#fff' : '#fff',
                          fontSize: '0.7rem',
                          fontWeight: 800,
                          lineHeight: 1,
                        }}
                      >
                        🔴 {badgeCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {loading ? (
              <div>Loading organization...</div>
            ) : organization ? (
              <>
                {/* Shareable workspace login link (Phase A+B) */}
                {orgSubTab === 'public' && (
                  <WorkspaceLinkCard slug={organization.slug} orgName={organization.name} compact />
                )}

                {/* Invite users by email (Phase C of Workspace link sharing).
                    Renders inline so the WorkspaceLinkCard "Invite users by email"
                    CTA can scroll the page to this card. */}
                {orgSubTab === 'public' && (
                  <InviteUsersCard organization={organization} currentUser={user} />
                )}

                {/* Compliance roll-up — surfaces the Staff Compliance summary
                    here so org owners get a single-glance health check on
                    the most regulated part of the operation. Click-through
                    jumps into the full Compliance panel under Staff Ops. */}
                {orgSubTab === 'staff' && (
                  <ComplianceSummaryCard
                    onOpenFullPanel={() => setActiveTab('staff')}
                  />
                )}

                {/* Account Cleanup — Delete or archive accounts so the
                    email is freed for re-registration. Useful when a
                    parent accidentally signed up under the wrong role
                    or a staff/driver leaves the organisation.
                    Lives under Maintenance so it doesn't crowd the top
                    of a routine settings visit. */}
                {orgSubTab === 'maintenance' && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <AccountCleanupCard currentUser={user} />
                  </div>
                )}

                {/* Default Pricing Tiers — early-bird discount + last-minute
                    surcharge defaults inherited by new Session Templates. */}
                {orgSubTab === 'billing' && (
                  <PricingDefaultsCard
                    organization={organization}
                    onSaved={async () => {
                      try {
                        const fresh = await api.getMyOrganization();
                        setOrganization(fresh);
                      } catch { /* non-fatal */ }
                    }}
                  />
                )}

                {/* Email Notifications — per-org sender identity (From-name,
                    Reply-To) + test-send button. SMTP credentials live in
                    backend .env; this card only configures how parents see
                    the email in their inbox. */}
                {orgSubTab === 'preferences' && (
                  <EmailSettingsCard />
                )}

                {/* HMRC identifiers used by P60 / P45 / Remittance PDFs. */}
                {orgSubTab === 'staff' && (
                  <PayrollSettingsCard />
                )}

                {/* Bank account details shown to parents choosing bank
                    transfer as their booking payment method. */}
                {orgSubTab === 'billing' && (
                  <BankDetailsCard />
                )}

                {/* Sibling discount — auto-applied as a negative line
                    item on grouped payment intents when a parent books
                    2+ children in one basket. */}
                {orgSubTab === 'billing' && (
                  <SiblingDiscountCard />
                )}

                {/* Grace-period auto-lock — blocks parents with any
                    invoice > N days overdue from creating new bookings.
                    Sits above the Branding Studio so it's easy to find
                    while an admin is already thinking about billing. */}
                {orgSubTab === 'billing' && organization && (
                  <GracePeriodCard organization={organization} onSaved={setOrganization} />
                )}

                {/* Invoice Branding Studio (AC.2) — live-preview editor
                    for colours, footer text, paid stamp wording. Sits
                    inside the Billing sub-tab because that's where
                    finance admins already are. */}
                {orgSubTab === 'billing' && organization && (
                  <InvoiceBrandingStudio organization={organization} onSaved={setOrganization} />
                )}

                {/* Policy Defaults — commercial thresholds baked into
                    the auto-generated Fees / Cancellation / Uncollected
                    Child policies. Living here so admins can tune the
                    money-related terms without hunting through
                    Preferences. */}
                {orgSubTab === 'billing' && (
                  <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="policy-defaults-card">
                    <h3 style={{ marginBottom: '0.25rem' }}>📚 Policy Defaults</h3>
                    <p style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', margin: '0 0 1rem' }}>
                      These values are substituted into your Fees, Cancellation, and Uncollected Child policies when you regenerate the pack. Editing them here changes the wording of every policy that references them.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <div className="form-group">
                        <label>Notice Period</label>
                        <input
                          type="text"
                          value={orgEditForm.notice_period}
                          onChange={(e) => setOrgEditForm({ ...orgEditForm, notice_period: e.target.value })}
                          placeholder="e.g. 4 weeks"
                          data-testid="org-notice-period-input"
                        />
                        <small style={{ color: 'hsl(var(--muted-foreground))' }}>Used in the Cancellation policy.</small>
                      </div>
                      <div className="form-group">
                        <label>Payment Terms (days)</label>
                        <input
                          type="number"
                          min="1"
                          max="90"
                          value={orgEditForm.payment_terms_days}
                          onChange={(e) => setOrgEditForm({ ...orgEditForm, payment_terms_days: e.target.value })}
                          placeholder="7"
                          data-testid="org-payment-terms-input"
                        />
                        <small style={{ color: 'hsl(var(--muted-foreground))' }}>Days a parent has to settle an invoice.</small>
                      </div>
                      <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Late Pick-up Fee</label>
                        <input
                          type="text"
                          value={orgEditForm.late_pickup_fee}
                          onChange={(e) => setOrgEditForm({ ...orgEditForm, late_pickup_fee: e.target.value })}
                          placeholder="e.g. £10 per every 15 minutes (or part thereof)"
                          data-testid="org-late-pickup-fee-input"
                        />
                        <small style={{ color: 'hsl(var(--muted-foreground))' }}>Wording shown in the Uncollected Child policy.</small>
                      </div>
                    </div>
                    <div style={{
                      marginTop: '1rem', padding: '0.75rem 0.9rem', borderRadius: 8,
                      background: 'hsl(210 65% 97%)', border: '1px solid hsl(210 65% 86%)',
                      color: 'hsl(210 60% 22%)', fontSize: '0.82rem', lineHeight: 1.55,
                    }}>
                      <label
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: '0.6rem',
                          cursor: 'pointer', margin: 0,
                        }}
                        data-testid="org-policy-auto-reset-label"
                      >
                        <input
                          type="checkbox"
                          checked={!!orgEditForm.policy_auto_reset}
                          onChange={(e) => setOrgEditForm({ ...orgEditForm, policy_auto_reset: e.target.checked })}
                          data-testid="org-policy-auto-reset-toggle"
                          style={{ marginTop: 2, cursor: 'pointer', transform: 'scale(1.15)' }}
                        />
                        <span style={{ flex: 1 }}>
                          <strong>🔄 Auto-render policies on save</strong>
                          <br />
                          <span style={{ color: 'hsl(var(--muted-foreground))' }}>
                            When ON, saving these settings <strong>re-renders every default policy</strong> from its shipped template using your new values — no need to click Reset in the Policies Library.
                            {' '}
                            <strong style={{ color: 'hsl(0 70% 35%)' }}>
                              ⚠️ This will overwrite any hand-tailored edits on default policies (custom policies are always safe).
                            </strong>
                          </span>
                        </span>
                      </label>
                    </div>

                    <div style={{
                      marginTop: '0.6rem', padding: '0.6rem 0.85rem', borderRadius: 8,
                      background: 'hsl(190 65% 96%)', border: '1px solid hsl(190 65% 85%)',
                      color: 'hsl(190 60% 22%)', fontSize: '0.78rem', lineHeight: 1.55,
                    }}>
                      💡 {orgEditForm.policy_auto_reset
                        ? <>Auto-render is <strong>ON</strong> — saving here re-renders every affected policy automatically.</>
                        : <>Auto-render is <strong>OFF</strong> — after saving, open <strong>Public Presence → Policies Library</strong> and click <strong>Reset to template</strong> (or the aggregate <strong>Reset all</strong>) on affected policies.</>}
                    </div>
                    <div style={{ marginTop: '0.85rem', textAlign: 'right' }}>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={orgSaving}
                        onClick={async () => {
                          // Fire the standard org update payload — the Save
                          // button at the bottom of Profile/Preferences saves
                          // everything, but a local Save here is friendlier
                          // when the admin is only tweaking Policy Defaults.
                          setOrgSaving(true);
                          try {
                            const updated = await api.updateMyOrganization({
                              notice_period: orgEditForm.notice_period || '',
                              late_pickup_fee: orgEditForm.late_pickup_fee || '',
                              payment_terms_days: orgEditForm.payment_terms_days || '',
                            });
                            const autoReset = updated && updated._auto_policy_reset;
                            if (autoReset && autoReset.triggered && autoReset.reset_count > 0) {
                              alert(
                                `Policy defaults saved · ${autoReset.reset_count} polic${autoReset.reset_count === 1 ? 'y' : 'ies'} auto-rendered with your new values.`
                              );
                            } else {
                              alert('Policy defaults saved. Reset any affected policy in the Policies Library to re-render.');
                            }
                          } catch (e) {
                            alert('Save failed: ' + (e?.message || 'unknown error'));
                          } finally {
                            setOrgSaving(false);
                          }
                        }}
                        data-testid="save-policy-defaults-btn"
                      >
                        {orgSaving ? 'Saving…' : '💾 Save Policy Defaults'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Public branded marketing page + inline parent signup.
                    Editor lets the owner customise headline/about/
                    services, publish it, and share via QR/link. */}
                {orgSubTab === 'public' && (
                  <WelcomePageEditor />
                )}

                {/* Ofsted / EYFS policy pack. Admin edits + publishes;
                    parents see published policies on their own tab. */}
                {orgSubTab === 'public' && (
                  <PoliciesLibrary onAuditChange={setPolicyPlaceholderCount} />
                )}

                {/* Per-user email category toggles (messages / bookings /
                    billing). Mounted in Org Settings for admins; also
                    usable from any future "My Profile" page. */}
                {orgSubTab === 'preferences' && (
                  <EmailPreferencesCard />
                )}

                {/* Organization Identity & Status Card */}
                {orgSubTab === 'profile' && (
                <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="organization-profile-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <div>
                      <h3 style={{ margin: 0 }}>{organization.name}</h3>
                      <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
                        Slug: <code>{organization.slug}</code> • Tenant ID: <code>{organization._id}</code>
                      </div>
                    </div>
                    <span className="badge badge-success" data-testid="org-plan-badge">
                      {(organization.subscription && organization.subscription.plan) || 'professional'}
                    </span>
                  </div>
                </div>
                )}

                {/* Basic Information */}
                {orgSubTab === 'profile' && (
                <div className="card" style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ marginBottom: '1rem' }}>📋 Basic Information</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label>Organization Name</label>
                      <input
                        type="text"
                        value={orgEditForm.name}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, name: e.target.value })}
                        data-testid="org-name-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Organization Type</label>
                      <select
                        value={orgEditForm.type}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, type: e.target.value })}
                        data-testid="org-type-select"
                      >
                        <option value="single_center">Single Center</option>
                        <option value="multi_center">Multi-Center</option>
                        <option value="franchise">Franchise</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Contact Email</label>
                      <input
                        type="email"
                        value={orgEditForm.contact_email}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, contact_email: e.target.value })}
                        data-testid="org-email-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Contact Phone</label>
                      <input
                        type="tel"
                        value={orgEditForm.contact_phone}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, contact_phone: e.target.value })}
                        data-testid="org-phone-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Website</label>
                      <input
                        type="url"
                        value={orgEditForm.website}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, website: e.target.value })}
                        placeholder="https://..."
                        data-testid="org-website-input"
                      />
                    </div>
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label>Company Logo</label>
                      <div style={{
                        display: 'flex',
                        gap: '1rem',
                        alignItems: 'flex-start',
                        flexWrap: 'wrap',
                        padding: '0.75rem',
                        border: '1px dashed hsl(var(--border))',
                        borderRadius: '0.5rem',
                        background: 'hsl(var(--muted) / 0.3)',
                      }}>
                        {/* Preview */}
                        <div style={{
                          width: '96px',
                          height: '96px',
                          borderRadius: '0.5rem',
                          background: '#fff',
                          border: '1px solid hsl(var(--border))',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          flexShrink: 0,
                        }}>
                          {orgEditForm.logo_url ? (
                            <img
                              src={orgEditForm.logo_url}
                              alt="Logo preview"
                              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                              data-testid="org-logo-preview"
                            />
                          ) : (
                            <span style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', textAlign: 'center', padding: '0.25rem' }}>
                              No logo
                            </span>
                          )}
                        </div>

                        {/* Upload controls */}
                        <div style={{ flex: 1, minWidth: '240px', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                            <label
                              className="btn btn-primary"
                              style={{ cursor: logoUploading ? 'wait' : 'pointer', margin: 0 }}
                              data-testid="org-logo-upload-label"
                            >
                              {logoUploading ? 'Uploading…' : (orgEditForm.logo_url ? '🔄 Replace logo' : '⬆️ Upload logo')}
                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/jpg,image/webp,image/gif,image/svg+xml"
                                style={{ display: 'none' }}
                                disabled={logoUploading}
                                onChange={(e) => {
                                  const f = e.target.files && e.target.files[0];
                                  handleLogoFileUpload(f);
                                  e.target.value = ''; // allow re-selecting the same file
                                }}
                                data-testid="org-logo-file-input"
                              />
                            </label>
                            {orgEditForm.logo_url && (
                              <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={handleLogoRemove}
                                disabled={logoUploading}
                                data-testid="org-logo-remove-btn"
                              >
                                🗑️ Remove
                              </button>
                            )}
                          </div>
                          <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                            PNG, JPG, WEBP, GIF or SVG · max 2 MB. Recommended: square ≥ 256×256, transparent background.
                          </small>
                          {logoError && (
                            <div style={{
                              fontSize: '0.8rem',
                              color: '#9f1239',
                              background: '#fff1f2',
                              border: '1px solid #fecdd3',
                              borderRadius: '0.375rem',
                              padding: '0.4rem 0.6rem',
                            }} data-testid="org-logo-error">
                              {logoError}
                            </div>
                          )}
                          <details>
                            <summary style={{ cursor: 'pointer', fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))' }}>
                              Or paste a hosted image URL
                            </summary>
                            <input
                              type="url"
                              value={orgEditForm.logo_url || ''}
                              onChange={(e) => setOrgEditForm({ ...orgEditForm, logo_url: e.target.value })}
                              placeholder="https://cdn.yoursite.com/logo.png"
                              style={{ marginTop: '0.5rem' }}
                              data-testid="org-logo-input"
                            />
                            <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                              Pasting a URL only updates this form — it will save when you click "Save Organization Settings" below.
                            </small>
                          </details>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                )}

                {/* Legal / Registration */}
                {orgSubTab === 'profile' && (
                <div className="card" style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ marginBottom: '0.25rem' }}>📜 Legal &amp; Registration</h3>
                  <p style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', margin: '0 0 1rem' }}>
                    These identifiers appear verbatim on your generated Policies pack and are referenced by Ofsted / HMRC / your local authority.
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label>Legal / Trading Name</label>
                      <input
                        type="text"
                        value={orgEditForm.legal_name}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, legal_name: e.target.value })}
                        placeholder="e.g. WizKids Childcare Ltd"
                        data-testid="org-legal-name-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Company Number</label>
                      <input
                        type="text"
                        value={orgEditForm.company_number}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, company_number: e.target.value })}
                        placeholder="UK Companies House number"
                        data-testid="org-company-number-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>VAT Number</label>
                      <input
                        type="text"
                        value={orgEditForm.vat_number}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, vat_number: e.target.value })}
                        placeholder="GB123456789"
                        data-testid="org-vat-number-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Ofsted URN</label>
                      <input
                        type="text"
                        value={orgEditForm.ofsted_urn}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, ofsted_urn: e.target.value })}
                        placeholder="e.g. EY123456"
                        data-testid="org-ofsted-urn-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>DfE URN</label>
                      <input
                        type="text"
                        value={orgEditForm.dfe_urn}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, dfe_urn: e.target.value })}
                        placeholder="e.g. 123456"
                        data-testid="org-dfe-urn-input"
                      />
                    </div>
                  </div>
                </div>

                )}

                {/* Regulatory & Safeguarding — key people + local
                    safeguarding contacts. Consumed by the Policies
                    module (Safeguarding, Complaints, Uncollected Child,
                    Missing Child etc.) so leaving these blank produces
                    "[Registered Manager]" placeholder text in the
                    exported PDFs. */}
                {orgSubTab === 'profile' && (
                <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="regulatory-safeguarding-card">
                  <h3 style={{ marginBottom: '0.25rem' }}>🛡️ Regulatory &amp; Safeguarding</h3>
                  <p style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', margin: '0 0 1rem' }}>
                    Names and contacts that appear on every safeguarding-related policy. Blanks show up as <code>[Registered Manager]</code> etc. in exported documents.
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label>Registered Manager</label>
                      <input
                        type="text"
                        value={orgEditForm.registered_manager}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, registered_manager: e.target.value })}
                        placeholder="Full name of the Ofsted-registered manager"
                        data-testid="org-registered-manager-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Designated Safeguarding Lead (DSL)</label>
                      <input
                        type="text"
                        value={orgEditForm.safeguarding_lead}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, safeguarding_lead: e.target.value })}
                        placeholder="Full name — defaults to Registered Manager"
                        data-testid="org-safeguarding-lead-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Deputy DSL</label>
                      <input
                        type="text"
                        value={orgEditForm.deputy_safeguarding_lead}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, deputy_safeguarding_lead: e.target.value })}
                        placeholder="Full name"
                        data-testid="org-deputy-dsl-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Local Authority</label>
                      <input
                        type="text"
                        value={orgEditForm.local_authority}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, local_authority: e.target.value })}
                        placeholder="e.g. London Borough of Camden"
                        data-testid="org-local-authority-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>LADO Phone</label>
                      <input
                        type="text"
                        value={orgEditForm.lado_phone}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, lado_phone: e.target.value })}
                        placeholder="Local Authority Designated Officer number"
                        data-testid="org-lado-phone-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Default Opening Hours</label>
                      <input
                        type="text"
                        value={orgEditForm.opening_hours}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, opening_hours: e.target.value })}
                        placeholder="e.g. 07:30–18:30, Monday to Friday"
                        data-testid="org-opening-hours-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Policy Review Period</label>
                      <select
                        value={orgEditForm.policy_review_period}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, policy_review_period: e.target.value })}
                        data-testid="org-policy-review-period-input"
                      >
                        <option value="">(select — how often you review policies)</option>
                        <option value="every 6 months">Every 6 months</option>
                        <option value="annually">Annually (recommended)</option>
                        <option value="every 2 years">Every 2 years</option>
                      </select>
                    </div>
                  </div>
                </div>

                )}

                {/* Locale & Branding */}
                {orgSubTab === 'profile' && (
                <div className="card" style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ marginBottom: '1rem' }}>🎨 Locale & Branding</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                    <div className="form-group">
                      <label>Currency</label>
                      <select
                        value={orgEditForm.currency}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, currency: e.target.value })}
                        data-testid="org-currency-select"
                      >
                        <option value="GBP">£ GBP</option>
                        <option value="USD">$ USD</option>
                        <option value="EUR">€ EUR</option>
                        <option value="AUD">A$ AUD</option>
                        <option value="CAD">C$ CAD</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Timezone</label>
                      <select
                        value={orgEditForm.timezone}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, timezone: e.target.value })}
                        data-testid="org-timezone-select"
                      >
                        <option value="Europe/London">Europe/London</option>
                        <option value="Europe/Dublin">Europe/Dublin</option>
                        <option value="America/New_York">America/New_York</option>
                        <option value="America/Los_Angeles">America/Los_Angeles</option>
                        <option value="Australia/Sydney">Australia/Sydney</option>
                        <option value="UTC">UTC</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Date Format</label>
                      <select
                        value={orgEditForm.date_format}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, date_format: e.target.value })}
                        data-testid="org-date-format-select"
                      >
                        <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                        <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                        <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Time Format</label>
                      <select
                        value={orgEditForm.time_format}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, time_format: e.target.value })}
                        data-testid="org-time-format-select"
                      >
                        <option value="24h">24 Hour</option>
                        <option value="12h">12 Hour (AM/PM)</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Primary Color</label>
                      <input
                        type="color"
                        value={orgEditForm.primary_color}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, primary_color: e.target.value })}
                        style={{ height: '38px', padding: '2px', width: '100%' }}
                        data-testid="org-primary-color-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Secondary Color</label>
                      <input
                        type="color"
                        value={orgEditForm.secondary_color}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, secondary_color: e.target.value })}
                        style={{ height: '38px', padding: '2px', width: '100%' }}
                        data-testid="org-secondary-color-input"
                      />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
                    <div className="form-group">
                      <label>Login Welcome Message</label>
                      <input
                        type="text"
                        value={orgEditForm.welcome_message}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, welcome_message: e.target.value })}
                        placeholder="e.g. Welcome to Sunshine Childcare"
                        data-testid="org-welcome-message-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Tagline</label>
                      <input
                        type="text"
                        value={orgEditForm.tagline}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, tagline: e.target.value })}
                        placeholder="e.g. Nurturing tomorrow's leaders"
                        data-testid="org-tagline-input"
                      />
                    </div>
                  </div>
                  <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'hsl(var(--muted))', borderRadius: 'var(--radius)', fontSize: '0.85rem' }}>
                    💡 The full shareable URL is at the top of this page (under <strong>Your workspace login link</strong>).
                  </div>
                </div>

                )}

                {/* Feature Toggles */}
                {orgSubTab === 'preferences' && (
                <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="features-card">
                  <h3 style={{ marginBottom: '1rem' }}>✨ Enabled Features</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0.75rem' }}>
                    {[
                      ['school_transport', '🚐 School Transport'],
                      ['moments', '📸 Moments & Media'],
                      ['financial_reports', '💰 Financial Reports'],
                      ['staff_management', '👨‍🏫 Staff Management'],
                      ['enrollment_wizard', '📝 Enrollment Wizard'],
                    ].map(([key, label]) => (
                      <label
                        key={key}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '0.75rem', background: 'hsl(var(--muted))',
                          borderRadius: 'var(--radius)', cursor: 'pointer',
                        }}
                      >
                        <span>{label}</span>
                        <input
                          type="checkbox"
                          checked={!!orgEditForm.features[key]}
                          onChange={(e) => setOrgEditForm({
                            ...orgEditForm,
                            features: { ...orgEditForm.features, [key]: e.target.checked },
                          })}
                          data-testid={`feature-${key}`}
                        />
                      </label>
                    ))}
                  </div>
                </div>

                )}

                {/* Notification Preferences */}
                {orgSubTab === 'preferences' && (
                <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="notifications-card">
                  <h3 style={{ marginBottom: '1rem' }}>🔔 Notification Settings</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0.75rem' }}>
                    {[
                      ['email_notifications', '📧 Email Notifications'],
                      ['sms_notifications', '📱 SMS Notifications'],
                      ['booking_confirmations', '✅ Booking Confirmations'],
                      ['waitlist_updates', '⏳ Waitlist Updates'],
                    ].map(([key, label]) => (
                      <label
                        key={key}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '0.75rem', background: 'hsl(var(--muted))',
                          borderRadius: 'var(--radius)', cursor: 'pointer',
                        }}
                      >
                        <span>{label}</span>
                        <input
                          type="checkbox"
                          checked={!!orgEditForm.notification_settings[key]}
                          onChange={(e) => setOrgEditForm({
                            ...orgEditForm,
                            notification_settings: {
                              ...orgEditForm.notification_settings,
                              [key]: e.target.checked,
                            },
                          })}
                          data-testid={`notification-${key}`}
                        />
                      </label>
                    ))}
                  </div>
                </div>

                )}

                {/* Parent Onboarding Gating */}
                {orgSubTab === 'preferences' && (
                <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="parent-onboarding-card">
                  <h3 style={{ marginBottom: '0.5rem' }}>👪 Parent Onboarding</h3>
                  <p style={{ marginTop: 0, fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>
                    Control whether parents who self-register through your branded login page require your approval before they can use the app.
                  </p>
                  <label
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '0.85rem 1rem',
                      background: 'hsl(var(--muted))',
                      borderRadius: 'var(--radius)',
                      cursor: 'pointer',
                      marginTop: '0.5rem',
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600 }}>🛡️ Require admin approval for new parents</div>
                      <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.15rem' }}>
                        New parents will land in <strong>Pending</strong> and cannot book until you approve them in the Users tab.
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={!!orgEditForm.require_parent_approval}
                      onChange={(e) => setOrgEditForm({
                        ...orgEditForm,
                        require_parent_approval: e.target.checked,
                      })}
                      data-testid="require-parent-approval-toggle"
                    />
                  </label>
                </div>

                )}

                {/* Workforce Policy */}
                {orgSubTab === 'staff' && (
                <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="workforce-policy-card">
                  <h3 style={{ marginBottom: '0.5rem' }}>🕒 Workforce Policy</h3>
                  <p style={{ marginTop: 0, fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>
                    Calibrate timesheet strictness and shift scheduling rules. Tolerances control when a clock-in/out is flagged as late, early, or overtime.
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginTop: '0.5rem' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Late tolerance (min)</label>
                      <input
                        type="number" min="0" max="120"
                        value={orgEditForm.late_tolerance_minutes}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, late_tolerance_minutes: e.target.value })}
                        data-testid="policy-late-tolerance"
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Early-out tolerance (min)</label>
                      <input
                        type="number" min="0" max="120"
                        value={orgEditForm.early_departure_tolerance_minutes}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, early_departure_tolerance_minutes: e.target.value })}
                        data-testid="policy-early-tolerance"
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Overtime tolerance (min)</label>
                      <input
                        type="number" min="0" max="240"
                        value={orgEditForm.overtime_tolerance_minutes}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, overtime_tolerance_minutes: e.target.value })}
                        data-testid="policy-overtime-tolerance"
                      />
                    </div>
                  </div>
                  <label
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '0.85rem 1rem', marginTop: '0.85rem',
                      background: 'hsl(var(--muted))', borderRadius: 'var(--radius)', cursor: 'pointer',
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600 }}>🚧 Hard-block scheduling outside availability</div>
                      <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.15rem' }}>
                        When ON, admins cannot save shifts outside a staff member's declared availability. Shifts on approved leave are <strong>always</strong> blocked.
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={!!orgEditForm.enforce_availability}
                      onChange={(e) => setOrgEditForm({ ...orgEditForm, enforce_availability: e.target.checked })}
                      data-testid="policy-enforce-availability"
                    />
                  </label>

                  {/* Leave year + auto-submit policy */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '0.75rem',
                      marginTop: '0.85rem',
                    }}
                  >
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Default annual leave (days)</label>
                      <input
                        type="number" min="0" max="60"
                        value={orgEditForm.default_annual_leave_days}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, default_annual_leave_days: e.target.value })}
                        data-testid="policy-default-leave-days"
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Leave year starts</label>
                      <select
                        value={orgEditForm.leave_year_start_month}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, leave_year_start_month: parseInt(e.target.value, 10) })}
                        data-testid="policy-leave-year-month"
                      >
                        {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                          <option key={m} value={i + 1}>{m}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <label
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '0.85rem 1rem', marginTop: '0.85rem',
                      background: 'hsl(var(--muted))', borderRadius: 'var(--radius)', cursor: 'pointer',
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600 }}>📨 Weekly auto-submit timesheets</div>
                      <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.15rem' }}>
                        When ON, every Sunday at 23:55 UTC every active staff member's timesheet is automatically submitted for admin approval.
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={!!orgEditForm.auto_submit_timesheets}
                      onChange={(e) => setOrgEditForm({ ...orgEditForm, auto_submit_timesheets: e.target.checked })}
                      data-testid="policy-auto-submit"
                    />
                  </label>
                </div>
                )}

                {/* ============================================================
                    Terms & Conditions editor
                   ============================================================ */}
                {orgSubTab === 'preferences' && (
                <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="org-terms-card">
                  <h3 style={{ marginTop: 0, marginBottom: '0.25rem' }}>📜 Terms &amp; Conditions</h3>
                  <p style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))', marginTop: 0 }}>
                    Shown verbatim to parents in the Enrollment Wizard. The exact text + version is snapshotted onto each enrollment record so parents always retain the document they actually accepted.
                  </p>

                  <div className="form-row">
                    <div className="form-group" style={{ flex: '0 0 auto', minWidth: '12rem' }}>
                      <label>Current version</label>
                      <div style={{ padding: '0.55rem 0.75rem', background: 'hsl(var(--muted))', borderRadius: 'var(--radius)', fontWeight: 600 }}>
                        v{orgEditForm.terms_version || 1}
                        {orgEditForm.terms_updated_at && (
                          <span style={{ marginLeft: '0.5rem', fontWeight: 400, color: 'hsl(var(--muted-foreground))', fontSize: '0.8rem' }}>
                            · updated {new Date(orgEditForm.terms_updated_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>External URL (optional)</label>
                      <input
                        type="url"
                        placeholder="https://your-site.com/terms"
                        value={orgEditForm.terms_url || ''}
                        onChange={(e) => setOrgEditForm({ ...orgEditForm, terms_url: e.target.value })}
                        data-testid="org-terms-url"
                      />
                      <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                        If set, parents see a "View full document" link alongside the inline text.
                      </small>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Document text</label>
                    <textarea
                      rows={14}
                      value={orgEditForm.terms_and_conditions || ''}
                      onChange={(e) => setOrgEditForm({ ...orgEditForm, terms_and_conditions: e.target.value })}
                      placeholder="Paste your full Terms &amp; Conditions text here…"
                      data-testid="org-terms-text"
                      style={{ width: '100%', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.825rem' }}
                    />
                    <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                      Editing the text will <strong>auto-bump the version on save</strong> and prompt all parents to re-acknowledge.
                    </small>
                  </div>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!orgEditForm.bump_terms_version}
                      onChange={(e) => setOrgEditForm({ ...orgEditForm, bump_terms_version: e.target.checked })}
                      data-testid="org-terms-bump"
                    />
                    <span style={{ fontSize: '0.85rem' }}>
                      Force version bump &amp; require all parents to re-acknowledge (even if text didn’t change)
                    </span>
                  </label>
                </div>
                )}

                {/* Save button — visible on Profile AND Preferences tabs
                    since both own fields on the shared `orgEditForm`. */}
                {(orgSubTab === 'profile' || orgSubTab === 'preferences') && (
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem', justifyContent: 'flex-end' }}>
                  <button
                    className="btn btn-primary"
                    disabled={orgSaving}
                    onClick={async () => {
                      setOrgSaving(true);
                      try {
                        const payload = {
                          name: orgEditForm.name,
                          type: orgEditForm.type,
                          contact_email: orgEditForm.contact_email,
                          contact_phone: orgEditForm.contact_phone,
                          website: orgEditForm.website,
                          company_number: orgEditForm.company_number,
                          vat_number: orgEditForm.vat_number,
                          legal_name: orgEditForm.legal_name || '',
                          ofsted_urn: orgEditForm.ofsted_urn || '',
                          dfe_urn: orgEditForm.dfe_urn || '',
                          local_authority: orgEditForm.local_authority || '',
                          lado_phone: orgEditForm.lado_phone || '',
                          registered_manager: orgEditForm.registered_manager || '',
                          safeguarding_lead: orgEditForm.safeguarding_lead || '',
                          deputy_safeguarding_lead: orgEditForm.deputy_safeguarding_lead || '',
                          opening_hours: orgEditForm.opening_hours || '',
                          notice_period: orgEditForm.notice_period || '',
                          late_pickup_fee: orgEditForm.late_pickup_fee || '',
                          payment_terms_days: orgEditForm.payment_terms_days || '',
                          policy_review_period: orgEditForm.policy_review_period || '',
                          settings: {
                            currency: orgEditForm.currency,
                            timezone: orgEditForm.timezone,
                            date_format: orgEditForm.date_format,
                            time_format: orgEditForm.time_format,
                            primary_color: orgEditForm.primary_color,
                            secondary_color: orgEditForm.secondary_color,
                            logo_url: orgEditForm.logo_url,
                            welcome_message: orgEditForm.welcome_message,
                            tagline: orgEditForm.tagline,
                            features: orgEditForm.features,
                            notification_settings: orgEditForm.notification_settings,
                            require_parent_approval: orgEditForm.require_parent_approval,
                            late_tolerance_minutes: parseInt(orgEditForm.late_tolerance_minutes, 10) || 0,
                            early_departure_tolerance_minutes: parseInt(orgEditForm.early_departure_tolerance_minutes, 10) || 0,
                            overtime_tolerance_minutes: parseInt(orgEditForm.overtime_tolerance_minutes, 10) || 0,
                            enforce_availability: !!orgEditForm.enforce_availability,
                            default_annual_leave_days: parseInt(orgEditForm.default_annual_leave_days, 10) || 28,
                            leave_year_start_month: parseInt(orgEditForm.leave_year_start_month, 10) || 4,
                            auto_submit_timesheets: !!orgEditForm.auto_submit_timesheets,
                            policy_auto_reset: !!orgEditForm.policy_auto_reset,
                            terms_and_conditions: orgEditForm.terms_and_conditions || '',
                            terms_url: orgEditForm.terms_url || '',
                            // bump_terms_version is consumed (popped) by
                            // the server. When true, it forces a version
                            // bump even if the text wasn't edited.
                            bump_terms_version: !!orgEditForm.bump_terms_version,
                          },
                        };
                        const updated = await api.updateMyOrganization(payload);
                        setOrganization(updated);
                        // Re-apply branding live so changes appear immediately
                        try {
                          const { applyBranding } = await import('../utils/branding');
                          const s = updated.settings || {};
                          applyBranding({
                            primary_color: s.primary_color,
                            secondary_color: s.secondary_color,
                            logo_url: s.logo_url,
                            welcome_message: s.welcome_message,
                            tagline: s.tagline,
                            name: updated.name,
                            slug: updated.slug,
                          });
                        } catch (e) { /* ignore */ }
                        // Server echoes an `_auto_policy_reset` block
                        // when the "Auto-render on save" toggle fires
                        // — surface the counter so the admin knows the
                        // library was refreshed by this save.
                        const autoReset = updated && updated._auto_policy_reset;
                        if (autoReset && autoReset.triggered && autoReset.reset_count > 0) {
                          alert(
                            `Organization updated · ${autoReset.reset_count} polic${autoReset.reset_count === 1 ? 'y' : 'ies'} auto-rendered from the shipped templates using your new values.`
                          );
                        } else {
                          alert('Organization updated successfully');
                        }
                      } catch (err) {
                        alert(`Failed to update: ${err.message}`);
                      } finally {
                        setOrgSaving(false);
                      }
                    }}
                    data-testid="save-org-btn"
                  >
                    {orgSaving ? 'Saving...' : '💾 Save All Organization Settings'}
                  </button>
                </div>

                )}

                {/* Subscription / Usage Card */}
                {orgSubTab === 'profile' && (
                <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="subscription-card">
                  <h3 style={{ marginBottom: '1rem' }}>💎 Subscription & Limits</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
                    <div className="stat-card">
                      <div className="stat-label">Plan</div>
                      <div className="stat-value" style={{ fontSize: '1.25rem' }}>
                        {(organization.subscription && organization.subscription.plan) || 'professional'}
                      </div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-label">Max Children</div>
                      <div className="stat-value">{organization.subscription?.max_children || 100}</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-label">Max Staff</div>
                      <div className="stat-value">{organization.subscription?.max_staff || 20}</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-label">Max Centers</div>
                      <div className="stat-value">{organization.subscription?.max_centers || 1}</div>
                    </div>
                  </div>
                  <p style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.75rem' }}>
                    💡 Contact a platform super-admin to upgrade your plan.
                  </p>
                </div>

                )}

                {/* Centers Card */}
                {orgSubTab === 'profile' && (
                <div className="card" data-testid="centers-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h3 style={{ margin: 0 }}>
                      🏬 Centers / Locations ({(organization.centers || []).length})
                    </h3>
                    <button
                      className="btn btn-primary"
                      onClick={() => setShowAddCenterModal(true)}
                      data-testid="add-center-btn"
                    >
                      ➕ Add Center
                    </button>
                  </div>

                  {(organization.centers || []).length > 0 ? (
                    <div style={{ display: 'grid', gap: '1rem' }}>
                      {organization.centers.map((center) => (
                        <div
                          key={center.center_id}
                          style={{
                            padding: '1rem',
                            borderRadius: 'var(--radius)',
                            border: '1px solid hsl(var(--border))',
                            background: 'hsl(var(--background))',
                          }}
                          data-testid={`center-${center.center_id}`}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                              <h4 style={{ margin: '0 0 0.25rem 0' }}>{center.name}</h4>
                              <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
                                {center.address}{center.city ? `, ${center.city}` : ''}{center.postcode ? ` ${center.postcode}` : ''}
                              </div>
                              <div style={{ fontSize: '0.75rem', marginTop: '0.5rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                                <span>📞 {center.phone || '—'}</span>
                                <span>✉️ {center.email || '—'}</span>
                                <span>👥 Capacity: {center.capacity}</span>
                                <span>🆔 {center.center_id}</span>
                                {center.ofsted_number && <span>📋 OFSTED: {center.ofsted_number}</span>}
                              </div>
                              {center.age_groups && center.age_groups.length > 0 && (
                                <div
                                  data-testid={`center-age-groups-${center.center_id}`}
                                  style={{
                                    display: 'flex', alignItems: 'center', flexWrap: 'wrap',
                                    gap: '0.35rem',
                                    fontSize: '0.75rem', marginTop: '0.5rem',
                                  }}
                                >
                                  <span style={{ color: 'hsl(var(--muted-foreground))' }}>Age groups:</span>
                                  {center.age_groups.map((ag) => (
                                    <span
                                      key={ag}
                                      className="badge"
                                      style={{
                                        display: 'inline-flex', alignItems: 'center',
                                        padding: '2px 8px', borderRadius: 999,
                                        fontSize: '0.72rem', fontWeight: 600,
                                        background: 'hsl(var(--muted))',
                                        color: 'hsl(var(--foreground))',
                                        border: '1px solid hsl(var(--border))',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >{formatAgeGroupLabel(ag)}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <span className={`badge ${center.is_active ? 'badge-success' : 'badge-warning'}`}>
                                {center.is_active ? 'Active' : 'Archived'}
                              </span>
                              <RowActions
                                testIdPrefix={`center-${center.center_id}`}
                                onEdit={() => {
                                  setEditingCenter(center);
                                  setEditCenterAgeGroups(Array.isArray(center.age_groups) ? center.age_groups : []);
                                  setShowCenterEditModal(true);
                                }}
                                onDelete={(force) => handleDeleteCenter(center, force)}
                                confirmTitle={`Archive center "${center.name}"?`}
                                confirmText="Centers with children, bookings, or staff are archived (hidden from active lists). To permanently remove, use 'Force delete' after moving dependents."
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state">No centers yet. Add your first location.</div>
                  )}
                </div>
                )}
              </>
            ) : (
              <div className="empty-state">Failed to load organization details.</div>
            )}
          </>
        )}

        {/* STAFF MANAGEMENT TAB */}
        {activeTab === 'messages' && (
          <MessagesInbox
            user={user}
            onCompose={() => {
              // Open the composer in admin mode with no preset recipient,
              // forcing the parent picker.
              setComposerCtx({});
            }}
          />
        )}

        {activeTab === 'staff' && (
          <>
            <div className="page-header">
              <h1>Staff Operations</h1>
              <p>Manage staff, rooms, shifts, and live ratios</p>
            </div>

            {/* Sub-tabs for Staff */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', borderBottom: '1px solid hsl(var(--border))' }}>
              <button 
                className={`sub-tab ${staffSubTab === 'profiles' ? 'active' : ''}`}
                onClick={() => setStaffSubTab('profiles')}
                style={{ 
                  padding: '0.75rem 1.5rem', 
                  background: staffSubTab === 'profiles' ? 'hsl(var(--primary))' : 'transparent',
                  color: staffSubTab === 'profiles' ? 'hsl(var(--primary-foreground))' : 'hsl(var(--foreground))',
                  border: 'none',
                  borderRadius: '6px 6px 0 0',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
                data-testid="subtab-profiles"
              >
                👥 Staff Profiles
              </button>
              <button 
                className={`sub-tab ${staffSubTab === 'rooms' ? 'active' : ''}`}
                onClick={() => setStaffSubTab('rooms')}
                style={{ 
                  padding: '0.75rem 1.5rem', 
                  background: staffSubTab === 'rooms' ? 'hsl(var(--primary))' : 'transparent',
                  color: staffSubTab === 'rooms' ? 'hsl(var(--primary-foreground))' : 'hsl(var(--foreground))',
                  border: 'none',
                  borderRadius: '6px 6px 0 0',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
                data-testid="subtab-rooms"
              >
                🏠 Rooms
              </button>
              <button 
                className={`sub-tab ${staffSubTab === 'shifts' ? 'active' : ''}`}
                onClick={() => setStaffSubTab('shifts')}
                style={{ 
                  padding: '0.75rem 1.5rem', 
                  background: staffSubTab === 'shifts' ? 'hsl(var(--primary))' : 'transparent',
                  color: staffSubTab === 'shifts' ? 'hsl(var(--primary-foreground))' : 'hsl(var(--foreground))',
                  border: 'none',
                  borderRadius: '6px 6px 0 0',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
                data-testid="subtab-shifts"
              >
                📅 Shifts
              </button>
              <button 
                className={`sub-tab ${staffSubTab === 'ratios' ? 'active' : ''}`}
                onClick={() => setStaffSubTab('ratios')}
                style={{ 
                  padding: '0.75rem 1.5rem', 
                  background: staffSubTab === 'ratios' ? 'hsl(var(--primary))' : 'transparent',
                  color: staffSubTab === 'ratios' ? 'hsl(var(--primary-foreground))' : 'hsl(var(--foreground))',
                  border: 'none',
                  borderRadius: '6px 6px 0 0',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
                data-testid="subtab-ratios"
              >
                🚦 Live Ratios
              </button>
              <button
                className={`sub-tab ${staffSubTab === 'timesheets' ? 'active' : ''}`}
                onClick={() => setStaffSubTab('timesheets')}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: staffSubTab === 'timesheets' ? 'hsl(var(--primary))' : 'transparent',
                  color: staffSubTab === 'timesheets' ? 'hsl(var(--primary-foreground))' : 'hsl(var(--foreground))',
                  border: 'none',
                  borderRadius: '6px 6px 0 0',
                  cursor: 'pointer',
                  fontWeight: '600',
                }}
                data-testid="subtab-timesheets"
              >
                🕒 Timesheets
              </button>
              <button
                className={`sub-tab ${staffSubTab === 'leave' ? 'active' : ''}`}
                onClick={() => setStaffSubTab('leave')}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: staffSubTab === 'leave' ? 'hsl(var(--primary))' : 'transparent',
                  color: staffSubTab === 'leave' ? 'hsl(var(--primary-foreground))' : 'hsl(var(--foreground))',
                  border: 'none',
                  borderRadius: '6px 6px 0 0',
                  cursor: 'pointer',
                  fontWeight: '600',
                }}
                data-testid="subtab-leave"
              >
                🏖️ Leave
              </button>
              <button
                className={`sub-tab ${staffSubTab === 'requests' ? 'active' : ''}`}
                onClick={() => setStaffSubTab('requests')}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: staffSubTab === 'requests' ? 'hsl(var(--primary))' : 'transparent',
                  color: staffSubTab === 'requests' ? 'hsl(var(--primary-foreground))' : 'hsl(var(--foreground))',
                  border: 'none',
                  borderRadius: '6px 6px 0 0',
                  cursor: 'pointer',
                  fontWeight: '600',
                }}
                data-testid="subtab-requests"
              >
                🔁 Shift Requests
              </button>
              <button
                className={`sub-tab ${staffSubTab === 'compliance' ? 'active' : ''}`}
                onClick={() => setStaffSubTab('compliance')}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: staffSubTab === 'compliance' ? 'hsl(var(--primary))' : 'transparent',
                  color: staffSubTab === 'compliance' ? 'hsl(var(--primary-foreground))' : 'hsl(var(--foreground))',
                  border: 'none',
                  borderRadius: '6px 6px 0 0',
                  cursor: 'pointer',
                  fontWeight: '600',
                }}
                data-testid="subtab-compliance"
              >
                🛡️ Compliance
              </button>
            </div>

            {/* STAFF PROFILES */}
            {staffSubTab === 'profiles' && (
              <>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem' }}>
                  <button
                    className="btn btn-primary"
                    onClick={() => { setEditingStaff(null); setStaffModalEmpType('employee'); setShowStaffModal(true); }}
                    data-testid="create-staff-btn"
                  >
                    + Add Staff Profile
                  </button>

                  {/* Repair tools for archived staff. Two modes:
                       1) "Fix missing status" — only touches legacy profiles
                          where is_active was never persisted.
                       2) "Reactivate inconsistent" — also restores profiles
                          explicitly set to is_active=false IF the underlying
                          user account is still active (a real data
                          inconsistency, e.g. invite stub never approved). */}
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={async () => {
                      try {
                        const r = await api.repairStaffProfileActiveStatus('missing');
                        if (r.modified > 0) {
                          toast.success(`Reactivated ${r.modified} legacy profile(s)`, { description: 'Refreshing list…' });
                          await loadData();
                        } else {
                          toast.info('Nothing to repair', { description: 'All profiles already have an explicit active status.' });
                        }
                      } catch (e) {
                        toast.error('Repair failed', { description: e.message });
                      }
                    }}
                    title="Reactivates legacy profiles whose 'active' status was never set. Won't touch profiles you intentionally archived."
                    data-testid="repair-active-status-btn"
                    style={{ fontSize: '0.78rem', padding: '0.25rem 0.6rem' }}
                  >
                    🔧 Fix missing status
                  </button>

                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={async () => {
                      const confirmed = window.confirm(
                        'Reactivate every archived staff profile whose user account is still active?\n\n' +
                        'This is meant to fix cases where a working staff member shows as Archived (e.g. an invite stub never got approved). ' +
                        'Profiles where the underlying account is inactive will NOT be touched.'
                      );
                      if (!confirmed) return;
                      try {
                        const r = await api.repairStaffProfileActiveStatus('inconsistent');
                        const total = (r.missing_modified || 0) + (r.inconsistent_modified || 0);
                        if (total > 0) {
                          toast.success(`Restored ${total} staff profile(s)`, {
                            description: `Legacy: ${r.missing_modified || 0} • Inconsistent: ${r.inconsistent_modified || 0}`,
                          });
                          await loadData();
                        } else {
                          toast.info('Nothing to repair', { description: 'No archived profile has an active underlying account.' });
                        }
                      } catch (e) {
                        toast.error('Repair failed', { description: e.message });
                      }
                    }}
                    title="Reactivate archived profiles where the underlying user account is still active (data inconsistency repair)."
                    data-testid="repair-inconsistent-status-btn"
                    style={{ fontSize: '0.78rem', padding: '0.25rem 0.6rem' }}
                  >
                    🩹 Restore inconsistent archived
                  </button>

                  {/* Role filter chips — keep drivers discoverable. */}
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginLeft: 'auto', alignItems: 'center' }} data-testid="staff-role-filter">
                    <span style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Role</span>
                    {[
                      { v: '',               label: 'All' },
                      { v: 'staff',          label: 'Staff' },
                      { v: 'driver',         label: '🚗 Drivers' },
                      { v: 'room_leader',    label: 'Room Leaders' },
                      { v: 'deputy_manager', label: 'Deputies' },
                      { v: 'org_admin',      label: 'Admins' },
                    ].map((opt) => (
                      <button
                        key={opt.v || 'all'}
                        type="button"
                        onClick={() => setStaffRoleFilter(opt.v)}
                        className={staffRoleFilter === opt.v ? 'btn btn-primary' : 'btn btn-ghost'}
                        style={{ fontSize: '0.78rem', padding: '0.2rem 0.6rem' }}
                        data-testid={`staff-role-chip-${opt.v || 'all'}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Employment-type chips — sit on their own row so role
                    filters above stay readable. Volunteer is orthogonal
                    to role: a Volunteer can be a Staff or a Driver. */}
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }} data-testid="staff-emp-type-filter">
                  <span style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Type</span>
                  {[
                    { v: '',           label: 'All' },
                    { v: 'employee',   label: '👨‍🏫 Employees' },
                    { v: 'contractor', label: '🧑‍💼 Contractors' },
                    { v: 'volunteer',  label: '🤝 Volunteers' },
                  ].map((opt) => (
                    <button
                      key={opt.v || 'all'}
                      type="button"
                      onClick={() => setStaffEmpTypeFilter(opt.v)}
                      className={staffEmpTypeFilter === opt.v ? 'btn btn-primary' : 'btn btn-ghost'}
                      style={{ fontSize: '0.78rem', padding: '0.2rem 0.6rem' }}
                      data-testid={`staff-emp-type-chip-${opt.v || 'all'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {(() => {
                  // Compute counts for the "needs attention" banner + status chips.
                  const roleAndTypeFiltered = staffProfiles.filter((p) => {
                    if (staffRoleFilter && (p.user_role || 'staff') !== staffRoleFilter) return false;
                    if (staffEmpTypeFilter && (p.employment_type || 'employee') !== staffEmpTypeFilter) return false;
                    return true;
                  });
                  const statusCounts = roleAndTypeFiltered.reduce(
                    (acc, p) => {
                      const isActive = p.is_active !== false;
                      const isPending = !isActive && (p.approval_status === 'pending');
                      if (isActive) acc.active += 1;
                      else if (isPending) acc.pending += 1;
                      else acc.archived += 1;
                      return acc;
                    },
                    { active: 0, pending: 0, archived: 0 },
                  );
                  const attentionCount = statusCounts.pending + statusCounts.archived;

                  const filteredStaffProfiles = roleAndTypeFiltered.filter((p) => {
                    if (!staffStatusFilter) return true; // 'All'
                    const isActive = p.is_active !== false;
                    const isPending = !isActive && (p.approval_status === 'pending');
                    if (staffStatusFilter === 'active') return isActive;
                    if (staffStatusFilter === 'pending') return isPending;
                    if (staffStatusFilter === 'archived') return !isActive && !isPending;
                    if (staffStatusFilter === 'attention') return !isActive; // pending OR archived
                    return true;
                  });
                  return (
                    <>
                      {/* "Needs attention" banner — jumps to the pending+archived filter. */}
                      {attentionCount > 0 && staffStatusFilter === 'active' && (
                        <div
                          data-testid="staff-attention-banner"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '0.75rem',
                            padding: '0.65rem 0.9rem',
                            marginBottom: '0.9rem',
                            background: 'hsl(38 95% 96%)',
                            border: '1px solid hsl(38 90% 78%)',
                            borderRadius: 8,
                            color: 'hsl(30 75% 25%)',
                            fontSize: '0.85rem',
                          }}
                        >
                          <span>
                            <strong>⚠️ {attentionCount} staff</strong>
                            {statusCounts.pending > 0 && statusCounts.archived > 0
                              ? ` (${statusCounts.pending} pending, ${statusCounts.archived} archived)`
                              : statusCounts.pending > 0
                                ? ` pending activation`
                                : ` archived`}
                            {' — '}review and activate to unblock schedules and logins.
                          </span>
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => setStaffStatusFilter('attention')}
                            style={{ fontSize: '0.78rem', padding: '0.25rem 0.7rem' }}
                            data-testid="staff-attention-review-btn"
                          >
                            Review {attentionCount}
                          </button>
                        </div>
                      )}

                      {/* Status filter chip row. */}
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }} data-testid="staff-status-filter">
                        <span style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</span>
                        {[
                          { v: '',         label: `All (${roleAndTypeFiltered.length})` },
                          { v: 'active',   label: `✅ Active (${statusCounts.active})` },
                          { v: 'pending',  label: `⏳ Pending (${statusCounts.pending})` },
                          { v: 'archived', label: `📦 Archived (${statusCounts.archived})` },
                        ].map((opt) => (
                          <button
                            key={opt.v || 'all'}
                            type="button"
                            onClick={() => setStaffStatusFilter(opt.v)}
                            className={staffStatusFilter === opt.v ? 'btn btn-primary' : 'btn btn-ghost'}
                            style={{ fontSize: '0.78rem', padding: '0.2rem 0.6rem' }}
                            data-testid={`staff-status-chip-${opt.v || 'all'}`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>

                      {loading ? (
                        <div>Loading...</div>
                      ) : filteredStaffProfiles.length > 0 ? (
                  <div className="card">
                    <table>
                      <thead>
                        <tr>
                          <th style={{ width: 60 }}>Photo</th>
                          <th>Name</th>
                          <th>Role</th>
                          <th>Email</th>
                          <th>Competencies</th>
                          <th>Hourly Rate</th>
                          <th>Contract Hours</th>
                          <th>Type</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStaffProfiles.map(profile => {
                          const userRole = profile.user_role || 'staff';
                          const ROLE_TONES = {
                            driver:         { bg: 'hsl(35 95% 90%)',  fg: 'hsl(35 80% 30%)',  label: '🚗 Driver' },
                            staff:          { bg: 'hsl(190 65% 92%)', fg: 'hsl(190 65% 25%)', label: '👨‍🏫 Staff' },
                            room_leader:    { bg: 'hsl(260 60% 92%)', fg: 'hsl(260 60% 30%)', label: '🎯 Room Leader' },
                            deputy_manager: { bg: 'hsl(220 80% 92%)', fg: 'hsl(220 70% 30%)', label: '🛡️ Deputy' },
                            org_admin:      { bg: 'hsl(0 70% 94%)',   fg: 'hsl(0 60% 35%)',   label: '👑 Admin' },
                            org_owner:      { bg: 'hsl(140 50% 92%)', fg: 'hsl(140 50% 30%)', label: '👑 Owner' },
                          };
                          const tone = ROLE_TONES[userRole] || ROLE_TONES.staff;
                          // Find the underlying user object for the Role-tier modal.
                          const userRow = users.find((u) => u._id === profile.user_id);
                          return (
                          <tr key={profile._id} style={profile.is_active === false ? { opacity: 0.55 } : undefined}>
                            <td style={{ padding: '0.5rem 0.35rem' }}>
                              <StaffAvatarCell profile={profile} onChanged={loadData} />
                            </td>
                            <td><strong>{profile.user_name}</strong></td>
                            <td>
                              <span
                                className="badge"
                                style={{ background: tone.bg, color: tone.fg, fontSize: '0.7rem', padding: '0.15rem 0.45rem', borderRadius: 999, fontWeight: 600 }}
                                data-testid={`staff-role-badge-${profile._id}`}
                              >
                                {tone.label}
                              </span>
                            </td>
                            <td>{profile.user_email}</td>
                            <td>
                              <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                                {profile.competencies.map((comp, i) => (
                                  <span key={i} className="badge badge-blue" style={{ fontSize: '0.75rem' }}>
                                    {comp === 'first_aider' ? '🩹 First Aider' :
                                     comp === 'unqualified' ? '🧑‍🤝‍🧑 Unqualified' :
                                     comp === 'level_2_qualified' ? '🎓 Level 2' :
                                     comp === 'level_3_qualified' ? '🎓 Level 3' :
                                     comp === 'level_5_qualified' ? '🎓 Level 5' :
                                     comp === 'food_hygiene_l2_l3' ? '🍽️ Food Hygiene L2-3' :
                                     comp === 'driver' ? '🚗 Driver' :
                                     comp === 'senco' ? '👨‍🏫 SENCO' :
                                     comp === 'safeguarding_lead' ? '🛡️ Safeguarding' : comp}
                                  </span>
                                ))}
                                {profile.competencies.length === 0 && <span className="badge badge-gray">No competencies</span>}
                              </div>
                            </td>
                            <td>£{profile.hourly_rate.toFixed(2)}</td>
                            <td>{profile.contract_hours}h/week</td>
                            <td>
                              {(() => {
                                const ET_TONES = {
                                  employee:   { bg: 'hsl(190 65% 92%)', fg: 'hsl(190 65% 25%)', label: '👨‍🏫 Employee' },
                                  contractor: { bg: 'hsl(45 95% 90%)',  fg: 'hsl(35 80% 30%)',  label: '🧑‍💼 Contractor' },
                                  volunteer:  { bg: 'hsl(140 50% 90%)', fg: 'hsl(140 55% 28%)', label: '🤝 Volunteer' },
                                };
                                const et = ET_TONES[profile.employment_type] || ET_TONES.employee;
                                return (
                                  <span
                                    className="badge"
                                    style={{ background: et.bg, color: et.fg, fontSize: '0.75rem' }}
                                    data-testid={`staff-emp-type-${profile._id}`}
                                  >
                                    {et.label}
                                  </span>
                                );
                              })()}
                            </td>
                            <td>
                              {(() => {
                                const isActive = profile.is_active !== false;
                                const isPending = !isActive && profile.approval_status === 'pending';
                                let badge;
                                if (isActive) {
                                  badge = <span className="badge badge-green" data-testid={`staff-status-${profile._id}`}>Active</span>;
                                } else if (isPending) {
                                  badge = (
                                    <span
                                      className="badge"
                                      data-testid={`staff-status-${profile._id}`}
                                      style={{ background: 'hsl(38 95% 90%)', color: 'hsl(30 80% 30%)', fontWeight: 600 }}
                                    >
                                      ⏳ Pending
                                    </span>
                                  );
                                } else {
                                  badge = <span className="badge badge-gray" data-testid={`staff-status-${profile._id}`}>📦 Archived</span>;
                                }
                                // Onboarding progress micro-badge — helps admins spot
                                // staff who haven't finished their profile yet. Hidden
                                // when either the field isn't populated (older records)
                                // or the staff is fully done (avoids noise).
                                const onbDone = profile.onboarding_completed;
                                const onbTotal = profile.onboarding_total;
                                const showOnb = typeof onbDone === 'number' && typeof onbTotal === 'number' && onbTotal > 0 && onbDone < onbTotal;
                                let onbColor = 'hsl(210 20% 45%)';
                                let onbBg = 'hsl(210 20% 94%)';
                                if (showOnb) {
                                  const pct = onbDone / onbTotal;
                                  if (pct < 0.5) { onbColor = 'hsl(0 60% 35%)'; onbBg = 'hsl(0 80% 95%)'; }
                                  else if (pct < 1) { onbColor = 'hsl(30 80% 30%)'; onbBg = 'hsl(38 95% 92%)'; }
                                }
                                return (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                                    {badge}
                                    {showOnb && (
                                      <span
                                        title={`This staff has completed ${onbDone} of ${onbTotal} onboarding items.`}
                                        data-testid={`staff-onboarding-badge-${profile._id}`}
                                        style={{
                                          fontSize: '0.68rem',
                                          background: onbBg,
                                          color: onbColor,
                                          padding: '0.1rem 0.4rem',
                                          borderRadius: 4,
                                          fontWeight: 600,
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        Onboarding {onbDone}/{onbTotal}
                                      </span>
                                    )}
                                  </div>
                                );
                              })()}
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                                {userRole === 'driver' && (
                                  <button
                                    type="button"
                                    className="btn btn-ghost"
                                    onClick={() => setDriverDrawer({ driverId: profile.user_id, driverName: profile.user_name })}
                                    style={{ fontSize: '0.72rem', padding: '0.18rem 0.45rem' }}
                                    data-testid={`staff-view-transport-${profile._id}`}
                                    title="View today's manifest and assigned children"
                                  >
                                    🚐 Transport
                                  </button>
                                )}
                                {userRow && userRole !== 'org_owner' && (
                                  <button
                                    type="button"
                                    className="btn btn-ghost"
                                    onClick={() => setRoleTierUser(userRow)}
                                    style={{ fontSize: '0.72rem', padding: '0.18rem 0.45rem' }}
                                    data-testid={`staff-role-${profile._id}`}
                                    title="Change role (Driver, Staff, Room Leader, Deputy, Admin)"
                                  >
                                    🎚️ Role
                                  </button>
                                )}
                                <RowActions
                                  testIdPrefix={`staff-${profile._id}`}
                                  onEdit={() => { setEditingStaff(profile); setStaffModalEmpType(profile.employment_type || 'employee'); setShowStaffModal(true); }}
                                  onDelete={(force) => handleDeleteStaff(profile, force)}
                                  confirmTitle={`Archive ${profile.user_name}?`}
                                  confirmText="Staff with active shifts will be archived (soft-deleted) so historical hours and shift records remain. Use 'Force delete' only after reassigning their shifts."
                                />
                                {profile.is_active === false && (
                                  <button
                                    type="button"
                                    className="btn btn-ghost"
                                    onClick={async () => {
                                      try {
                                        await api.restoreStaffProfile(profile._id);
                                        toast.success(`${profile.user_name} activated`, {
                                          description: 'They can now be scheduled and log in as normal.',
                                        });
                                        await loadData();
                                      } catch (e) {
                                        toast.error(e.message || 'Failed to activate');
                                      }
                                    }}
                                    style={{
                                      fontSize: '0.72rem',
                                      padding: '0.18rem 0.45rem',
                                      color: 'hsl(140 60% 30%)',
                                      borderColor: 'hsl(140 60% 60% / 0.4)',
                                    }}
                                    data-testid={`staff-restore-${profile._id}`}
                                    title={profile.approval_status === 'pending'
                                      ? 'Activate this newly-joined staff member so they can log in and be scheduled'
                                      : 'Restore this archived staff member to Active'}
                                  >
                                    {profile.approval_status === 'pending' ? '✅ Activate' : '🔄 Activate'}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>{staffRoleFilter ? `No ${staffRoleFilter.replace('_', ' ')} profiles match this filter.` : (staffStatusFilter && staffStatusFilter !== 'active') ? `No ${staffStatusFilter === 'attention' ? 'pending or archived' : staffStatusFilter} staff to show.` : 'No staff profiles yet'}</p>
                    {!staffRoleFilter && !staffStatusFilter && (
                      <button className="btn btn-primary" onClick={() => { setEditingStaff(null); setStaffModalEmpType('employee'); setShowStaffModal(true); }}>Add First Staff Profile</button>
                    )}
                  </div>
                )}
                    </>
                  );
                })()}
              </>
            )}

            {/* ROOMS */}
            {staffSubTab === 'rooms' && (
              <>
                <button 
                  className="btn btn-primary" 
                  onClick={() => setShowRoomModal(true)}
                  style={{ marginBottom: '1.5rem' }}
                  data-testid="create-room-btn"
                >
                  + Create Room
                </button>

                {loading ? (
                  <div>Loading...</div>
                ) : rooms.length > 0 ? (
                  <div className="card-grid">
                    {rooms.map(room => (
                      <div key={room._id} className="card" style={room.is_active === false ? { opacity: 0.6 } : undefined}>
                        <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem' }}>
                          {room.name}
                          {room.is_active === false && (
                            <span className="ml-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded">Archived</span>
                          )}
                        </h3>
                        <p style={{ margin: '0 0 1rem 0', color: 'hsl(var(--muted-foreground))' }}>
                          {room.age_group.charAt(0).toUpperCase() + room.age_group.slice(1)}
                        </p>
                        <div style={{ display: 'grid', gap: '0.75rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: 'hsl(var(--muted-foreground))' }}>Capacity:</span>
                            <strong>{room.capacity} children</strong>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: 'hsl(var(--muted-foreground))' }}>Required Ratio:</span>
                            <strong>1:{Math.round(1/room.required_ratio)}</strong>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: 'hsl(var(--muted-foreground))' }}>First Aider:</span>
                            <span className={`badge ${room.required_first_aider ? 'badge-red' : 'badge-gray'}`}>
                              {room.required_first_aider ? 'Required' : 'Not Required'}
                            </span>
                          </div>
                        </div>
                        <div className="mt-3 flex justify-end gap-2">
                          <button
                            type="button"
                            className="btn btn-ghost"
                            style={{ fontSize: '0.85rem' }}
                            onClick={async () => {
                              // Lazy-load the room's children when opening
                              // the panel so we always show the current
                              // assignment list (children data may have
                              // changed since this tab last loaded).
                              try {
                                const all = children.length ? children : await api.getChildren();
                                if (!children.length) setChildren(all);
                                setRoomDetail({
                                  room,
                                  children: all.filter((c) => c.room_id === room._id),
                                });
                              } catch (err) {
                                alert('Failed to load children: ' + err.message);
                              }
                            }}
                            data-testid={`room-children-btn-${room._id}`}
                            title="See which children are currently in this room"
                          >
                            🧒 View children
                          </button>
                          <RowActions
                            testIdPrefix={`room-${room._id}`}
                            onDelete={(force) => handleDeleteRoom(room, force)}
                            confirmTitle={`Archive room "${room.name}"?`}
                            confirmText="Rooms used in shifts will be archived (soft-deleted). To permanently delete, first remove or reassign existing shifts."
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>No rooms configured</p>
                    <button className="btn btn-primary" onClick={() => setShowRoomModal(true)}>Create First Room</button>
                  </div>
                )}
              </>
            )}

            {/* SHIFTS */}
            {staffSubTab === 'shifts' && (
              <>
                <button 
                  className="btn btn-primary" 
                  onClick={() => setShowShiftModal(true)}
                  style={{ marginBottom: '1.5rem' }}
                  data-testid="create-shift-btn"
                >
                  + Schedule Shift
                </button>

                {loading ? (
                  <div>Loading...</div>
                ) : shifts.length > 0 ? (
                  <div className="card">
                    <table>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Staff</th>
                          <th>Room</th>
                          <th>Time</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shifts.slice(0, 50).map(shift => (
                          <tr key={shift._id}>
                            <td>{new Date(shift.date).toLocaleDateString()}</td>
                            <td><strong>{shift.staff_name || 'Unknown'}</strong></td>
                            <td>{shift.room_name || 'Unknown'}</td>
                            <td>{shift.start_time} - {shift.end_time}</td>
                            <td>
                              <span className={`badge ${
                                shift.status === 'published' ? 'badge-green' :
                                shift.status === 'completed' ? 'badge-blue' :
                                shift.status === 'cancelled' ? 'badge-gray' :
                                'badge-yellow'
                              }`}>
                                {shift.status}
                              </span>
                            </td>
                            <td>
                              <div className="inline-flex flex-wrap gap-2 items-center">
                                {shift.status === 'draft' && (
                                  <button 
                                    className="btn btn-sm btn-primary" 
                                    onClick={() => handlePublishShift(shift._id)}
                                    data-testid={`publish-shift-${shift._id}`}
                                  >
                                    Publish
                                  </button>
                                )}
                                {shift.status !== 'cancelled' && (
                                  <RowActions
                                    testIdPrefix={`shift-${shift._id}`}
                                    onDelete={() => handleDeleteShift(shift)}
                                    dangerLabel={shift.status === 'draft' ? 'Delete' : 'Cancel'}
                                    confirmTitle={shift.status === 'draft' ? 'Delete this shift?' : 'Cancel this shift?'}
                                    confirmText={shift.status === 'draft' ? 'Draft shifts are deleted permanently.' : 'Published shifts are marked as cancelled but kept for the audit trail.'}
                                  />
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>No shifts scheduled</p>
                    <button className="btn btn-primary" onClick={() => setShowShiftModal(true)}>Schedule First Shift</button>
                  </div>
                )}
              </>
            )}

            {/* LIVE RATIOS */}
            {staffSubTab === 'ratios' && (
              <>
                <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h2 style={{ margin: 0 }}>Real-Time Staff-to-Child Ratios</h2>
                  <button className="btn btn-secondary" onClick={loadData} data-testid="refresh-ratios">
                    🔄 Refresh
                  </button>
                </div>

                {loading ? (
                  <div>Loading...</div>
                ) : liveRatios && liveRatios.ratios ? (
                  <>
                    <div style={{ marginBottom: '2rem', padding: '1.5rem', background: 'hsl(var(--muted))', borderRadius: '8px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
                        <div>
                          <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.25rem' }}>Total Children</div>
                          <div style={{ fontSize: '2rem', fontWeight: '700' }}>{liveRatios.total_children}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.25rem' }}>Staff Clocked In</div>
                          <div style={{ fontSize: '2rem', fontWeight: '700' }}>{liveRatios.total_staff_clocked_in}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.25rem' }}>Last Updated</div>
                          <div style={{ fontSize: '1rem', fontWeight: '600' }}>{new Date(liveRatios.timestamp).toLocaleTimeString()}</div>
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gap: '1.5rem' }}>
                      {liveRatios.ratios.map((ratio, idx) => (
                        <div 
                          key={idx} 
                          className="card" 
                          style={{ 
                            borderLeft: `6px solid ${
                              ratio.status === 'green' ? '#22c55e' :
                              ratio.status === 'amber' ? '#f59e0b' :
                              ratio.status === 'red' ? '#ef4444' :
                              '#94a3b8'
                            }`
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem' }}>{ratio.room_name}</h3>
                              <p style={{ margin: '0 0 1rem 0', color: 'hsl(var(--muted-foreground))' }}>
                                {ratio.age_group.charAt(0).toUpperCase() + ratio.age_group.slice(1)} Room
                              </p>
                              
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                                <div>
                                  <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>Children Present</div>
                                  <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>{ratio.children_count}</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>Staff Present</div>
                                  <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>{ratio.staff_count}</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>Required Ratio</div>
                                  <div style={{ fontSize: '1.25rem', fontWeight: '600' }}>1:{Math.round(1/ratio.required_ratio)}</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>Current Ratio</div>
                                  <div style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                                    {ratio.staff_count > 0 ? `1:${Math.round(ratio.children_count / ratio.staff_count)}` : 'N/A'}
                                  </div>
                                </div>
                              </div>

                              {ratio.first_aider_present !== undefined && (
                                <div style={{ marginTop: '1rem', padding: '0.75rem', background: ratio.first_aider_present ? 'hsl(142 76% 95%)' : 'hsl(0 84% 95%)', borderRadius: '6px' }}>
                                  <span style={{ fontWeight: '600', color: ratio.first_aider_present ? 'hsl(142 71% 25%)' : 'hsl(0 72% 40%)' }}>
                                    {ratio.first_aider_present ? '✅ First Aider Present' : '⚠️ First Aider Required'}
                                  </span>
                                </div>
                              )}

                              {ratio.warnings && ratio.warnings.length > 0 && (
                                <div style={{ marginTop: '1rem' }}>
                                  {ratio.warnings.map((warning, i) => (
                                    <div key={i} style={{ padding: '0.75rem', background: 'hsl(48 96% 95%)', borderRadius: '6px', marginTop: '0.5rem' }}>
                                      <span style={{ color: 'hsl(25 95% 35%)' }}>⚠️ {warning}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            <div 
                              style={{ 
                                width: '80px', 
                                height: '80px', 
                                borderRadius: '50%', 
                                background: ratio.status === 'green' ? '#22c55e' :
                                           ratio.status === 'amber' ? '#f59e0b' :
                                           ratio.status === 'red' ? '#ef4444' :
                                           '#94a3b8',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '2rem',
                                flexShrink: 0
                              }}
                            >
                              {ratio.status === 'green' ? '✅' :
                               ratio.status === 'amber' ? '⚠️' :
                               ratio.status === 'red' ? '🚨' : '❓'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="empty-state">No ratio data available</div>
                )}
              </>
            )}

            {/* TIMESHEETS */}
            {staffSubTab === 'timesheets' && (
              <TimesheetView mode="admin" />
            )}

            {/* LEAVE */}
            {staffSubTab === 'leave' && (
              <LeaveManager
                mode="admin"
                staffOptions={(staffProfiles || []).map((p) => ({
                  id: p._id,
                  name: p.user_name || p.user_email || p._id,
                }))}
              />
            )}

            {/* SHIFT REQUESTS */}
            {staffSubTab === 'requests' && (
              <ShiftRequestsInbox mode="admin" />
            )}

            {/* COMPLIANCE — admin org-wide rollup + per-staff drilldown.
                Headline cards show counts by status; the table lets the
                admin click into any staff member to verify or reject
                their uploaded certificates. */}
            {staffSubTab === 'compliance' && (
              <ComplianceAdminPanel
                staffProfiles={staffProfiles}
              />
            )}
          </>
        )}

      </div>

      {/* MODALS */}
      
      {/* Create Template Modal */}
      {showTemplateModal && (
        <div className="modal-overlay" onClick={() => { setShowTemplateModal(false); setEditingTemplate(null); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingTemplate ? 'Edit Session Template' : 'Create Session Template'}</h2>
            <form onSubmit={handleCreateTemplate}>
              <div className="form-group">
                <label>Name</label>
                <input name="name" required defaultValue={editingTemplate?.name || ''} data-testid="template-name" />
              </div>
              <div className="form-group">
                <label>Type</label>
                <select name="type" required defaultValue={editingTemplate?.type || 'breakfast'} data-testid="template-type">
                  <option value="breakfast">Breakfast</option>
                  <option value="early_years">Early Years</option>
                  <option value="afterschool">Afterschool</option>
                  <option value="holiday_club">Holiday Club</option>
                </select>
              </div>
              <div className="form-group">
                <label>Start Time (HH:MM)</label>
                <input name="start_time" type="time" required defaultValue={editingTemplate?.start_time || ''} data-testid="template-start-time" />
              </div>
              <div className="form-group">
                <label>End Time (HH:MM)</label>
                <input name="end_time" type="time" required defaultValue={editingTemplate?.end_time || ''} data-testid="template-end-time" />
              </div>
              <div className="form-group">
                <label>Days of Week (0=Mon, comma-separated)</label>
                <input name="days_of_week" defaultValue={editingTemplate ? editingTemplate.days_of_week.join(',') : '0,1,2,3,4'} required data-testid="template-days" />
              </div>
              <div className="form-group">
                <label>Capacity</label>
                <input name="capacity" type="number" required defaultValue={editingTemplate?.capacity || ''} data-testid="template-capacity" />
              </div>
              <div className="form-group">
                <label>Base Price (£)</label>
                <input
                  name="base_price"
                  type="number"
                  step="0.01"
                  required
                  value={tplBasePrice}
                  onChange={(e) => setTplBasePrice(e.target.value)}
                  data-testid="template-price"
                />
              </div>

              {/* Pricing tiers — collapsible. Default base = £X, early-bird
                  for advance bookings, last-minute surcharge for late ones.
                  Setting either rate to 0 yields a flat price. */}
              <div className="form-group" style={{ border: '1px solid hsl(var(--border))', borderRadius: 8, padding: '0.6rem 0.75rem' }}>
                <button
                  type="button"
                  onClick={() => setTplShowPricingTiers((v) => !v)}
                  data-testid="toggle-pricing-tiers"
                  style={{
                    border: 'none', background: 'transparent',
                    padding: 0, cursor: 'pointer', width: '100%',
                    textAlign: 'left', fontWeight: 600, fontSize: '0.92rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}
                >
                  <span>💷 Pricing tiers (Early-bird & Last-minute)</span>
                  <span style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))' }}>
                    {tplShowPricingTiers ? '▾ Hide' : '▸ Show'}
                  </span>
                </button>

                {/* Live preview — always visible so admins see the impact. */}
                {(() => {
                  const base = parseFloat(tplBasePrice || '0') || 0;
                  const earlyPct = Math.max(0, Math.min(100, Number(tplEarlyDiscount) || 0));
                  const latePct = Math.max(0, Math.min(500, Number(tplLatePremium) || 0));
                  const earlyPrice = (base * (1 - earlyPct / 100)).toFixed(2);
                  const latePrice = (base * (1 + latePct / 100)).toFixed(2);
                  const isFlat = earlyPct === 0 && latePct === 0;
                  return (
                    <div
                      data-testid="pricing-tier-preview"
                      style={{
                        marginTop: 8, padding: '0.45rem 0.6rem',
                        background: 'hsl(var(--muted)/0.4)', borderRadius: 6,
                        fontSize: '0.82rem', display: 'flex', flexWrap: 'wrap', gap: 12,
                      }}
                    >
                      {isFlat ? (
                        <span>📌 <strong>Flat price:</strong> £{base.toFixed(2)} for every booking</span>
                      ) : (
                        <>
                          <span>🐦 <strong>Early-bird</strong> (≥ {tplEarlyCutoff || 0}d): <strong>£{earlyPrice}</strong></span>
                          <span>📅 <strong>Standard</strong>: <strong>£{base.toFixed(2)}</strong></span>
                          <span>⏰ <strong>Last-minute</strong> (&lt; {tplLateCutoff || 0}d): <strong>£{latePrice}</strong></span>
                        </>
                      )}
                    </div>
                  );
                })()}

                {tplShowPricingTiers && (
                  <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem' }}>
                        <input
                          type="checkbox"
                          checked={(Number(tplEarlyDiscount) || 0) === 0 && (Number(tplLatePremium) || 0) === 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setTplEarlyDiscount(0);
                              setTplLatePremium(0);
                            } else {
                              setTplEarlyDiscount(20);
                              setTplLatePremium(30);
                            }
                          }}
                          data-testid="pricing-flat-toggle"
                        />
                        📌 Flat price for everyone (no tiered pricing)
                      </label>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
                      <div>
                        <label style={{ fontSize: '0.78rem', display: 'block' }}>🐦 Early-bird discount (%)</label>
                        <input
                          type="number" min="0" max="100" step="1"
                          value={tplEarlyDiscount}
                          onChange={(e) => setTplEarlyDiscount(e.target.value)}
                          data-testid="tier-early-discount"
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '0.78rem', display: 'block' }}>… if booked ≥ X days ahead</label>
                        <input
                          type="number" min="0" max="365" step="1"
                          value={tplEarlyCutoff}
                          onChange={(e) => setTplEarlyCutoff(e.target.value)}
                          data-testid="tier-early-cutoff"
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '0.78rem', display: 'block' }}>⏰ Last-minute surcharge (%)</label>
                        <input
                          type="number" min="0" max="500" step="1"
                          value={tplLatePremium}
                          onChange={(e) => setTplLatePremium(e.target.value)}
                          data-testid="tier-late-premium"
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '0.78rem', display: 'block' }}>… if booked &lt; X days ahead</label>
                        <input
                          type="number" min="0" max="30" step="1"
                          value={tplLateCutoff}
                          onChange={(e) => setTplLateCutoff(e.target.value)}
                          data-testid="tier-late-cutoff"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => { setShowTemplateModal(false); setEditingTemplate(null); }}>Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-template">{editingTemplate ? 'Save changes' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Generate Instances Modal */}
      {showGenerateModal && (
        <div className="modal-overlay" onClick={() => setShowGenerateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <h2>Generate Session Instances</h2>
            <p style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))', marginTop: 0 }}>
              Pick how to generate session dates. Use <strong>Term-time</strong> for after-school / breakfast clubs that pause during half-term and inset days.
            </p>

            <div role="tablist" style={{ display: 'flex', gap: '0.4rem', borderBottom: '1px solid hsl(var(--border))', marginBottom: '0.85rem' }}>
              <button
                type="button"
                role="tab"
                aria-selected={generateMode === 'range'}
                onClick={() => { setGenerateMode('range'); setGeneratePreview(null); }}
                className="btn btn-ghost"
                data-testid="gen-mode-range"
                style={{
                  borderBottom: generateMode === 'range' ? '2px solid hsl(var(--primary))' : '2px solid transparent',
                  borderRadius: 0,
                  fontWeight: generateMode === 'range' ? 600 : 400,
                }}
              >
                📆 Date range
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={generateMode === 'term_time'}
                onClick={() => { setGenerateMode('term_time'); setGeneratePreview(null); }}
                className="btn btn-ghost"
                data-testid="gen-mode-term-time"
                style={{
                  borderBottom: generateMode === 'term_time' ? '2px solid hsl(var(--primary))' : '2px solid transparent',
                  borderRadius: 0,
                  fontWeight: generateMode === 'term_time' ? 600 : 400,
                }}
              >
                🎓 Term-time
              </button>
            </div>

            {generateMode === 'range' && (
              <form onSubmit={handleGenerateInstances}>
                <input type="hidden" name="template_id" value={showGenerateModal} />
                <div className="form-group">
                  <label>Start Date</label>
                  <input name="start_date" type="date" required data-testid="gen-start-date" />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input name="end_date" type="date" required data-testid="gen-end-date" />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-ghost" onClick={() => setShowGenerateModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" data-testid="submit-generate">Generate</button>
                </div>
              </form>
            )}

            {generateMode === 'term_time' && (
              <div data-testid="gen-term-time-form">
                {generateTerms.length === 0 ? (
                  <div className="empty-state" style={{ padding: '1rem 0' }}>
                    <p>No terms or holiday clubs found. Configure your term calendar first.</p>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => { setShowGenerateModal(false); setActiveTab('terms'); }}
                      data-testid="gen-go-to-terms"
                    >
                      Open Term Calendar
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="form-group">
                      <label>Pick term(s) and/or holiday club window(s)</label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 240, overflowY: 'auto', border: '1px solid hsl(var(--border))', borderRadius: 6, padding: 6 }}>
                        {['term', 'holiday_club'].map((groupKind) => {
                          const items = generateTerms.filter((t) => (t.kind || 'term') === groupKind);
                          if (items.length === 0) return null;
                          const label = groupKind === 'holiday_club' ? '🏖️ Holiday clubs' : '🎓 School terms';
                          return (
                            <div key={groupKind} data-testid={`gen-term-group-${groupKind}`}>
                              <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'hsl(var(--muted-foreground))', margin: '4px 0 2px' }}>{label}</div>
                              {items.map((t) => (
                                <label key={t._id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', cursor: 'pointer', padding: '2px 0' }}>
                                  <input
                                    type="checkbox"
                                    checked={generateSelectedTermIds.includes(t._id)}
                                    onChange={() => toggleGenerateTerm(t._id)}
                                    data-testid={`gen-term-${t._id}`}
                                  />
                                  <span><strong>{t.name}</strong> — {t.start_date} → {t.end_date}{(t.closed_dates || []).length > 0 ? ` · ${t.closed_dates.length} closure(s)` : ''}</span>
                                </label>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Weekdays</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {[
                          { iso: 1, label: 'Mon' },
                          { iso: 2, label: 'Tue' },
                          { iso: 3, label: 'Wed' },
                          { iso: 4, label: 'Thu' },
                          { iso: 5, label: 'Fri' },
                          { iso: 6, label: 'Sat' },
                          { iso: 7, label: 'Sun' },
                        ].map((d) => {
                          const on = generateWeekdaysIso.includes(d.iso);
                          return (
                            <button
                              key={d.iso}
                              type="button"
                              onClick={() => toggleGenerateWeekday(d.iso)}
                              data-testid={`gen-weekday-${d.iso}`}
                              className={on ? 'btn btn-primary' : 'btn btn-ghost'}
                              style={{ fontSize: '0.78rem', padding: '0.25rem 0.6rem' }}
                            >
                              {d.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        Optional: clip the date window
                        <span style={{ fontSize: '0.7rem', fontWeight: 'normal', color: 'hsl(var(--muted-foreground))' }}>
                          (skip days before / after these dates within the picked windows)
                        </span>
                      </label>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                        <input
                          type="date"
                          value={generateDateFrom}
                          onChange={(e) => { setGenerateDateFrom(e.target.value); setGeneratePreview(null); }}
                          placeholder="Start sessions from"
                          data-testid="gen-date-from"
                          aria-label="Start sessions from"
                        />
                        <input
                          type="date"
                          value={generateDateTo}
                          onChange={(e) => { setGenerateDateTo(e.target.value); setGeneratePreview(null); }}
                          placeholder="Until"
                          data-testid="gen-date-to"
                          aria-label="Until"
                        />
                      </div>
                    </div>

                    {generatePreview && (
                      <div className="card" style={{ background: 'hsl(var(--muted)/0.4)', padding: '0.75rem', marginTop: '0.5rem' }} data-testid="gen-preview-result">
                        <strong>Preview</strong>
                        <div style={{ fontSize: '0.85rem', marginTop: 4 }}>
                          ✅ {generatePreview.would_create} sessions will be created
                          {generatePreview.skipped?.length ? ` · ⏭️ ${generatePreview.skipped.length} day(s) skipped (closures)` : ''}
                          {generatePreview.clip?.from || generatePreview.clip?.to
                            ? ` · clipped ${generatePreview.clip?.from || '…'} → ${generatePreview.clip?.to || '…'}`
                            : ''}
                        </div>
                        {generatePreview.skipped?.length > 0 && (
                          <details style={{ marginTop: 6, fontSize: '0.78rem' }}>
                            <summary>Show skipped dates</summary>
                            <ul style={{ margin: '4px 0 0 1rem' }}>
                              {generatePreview.skipped.slice(0, 30).map((s, i) => (
                                <li key={i}>{s.date} — {s.reason}</li>
                              ))}
                              {generatePreview.skipped.length > 30 && <li>…and {generatePreview.skipped.length - 30} more</li>}
                            </ul>
                          </details>
                        )}
                      </div>
                    )}

                    <div className="modal-actions">
                      <button type="button" className="btn btn-ghost" onClick={() => setShowGenerateModal(false)} disabled={generateBusy}>Cancel</button>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => handleGenerateTermTime({ previewOnly: true })}
                        disabled={generateBusy}
                        data-testid="gen-term-time-preview"
                      >
                        {generateBusy ? '…' : '👀 Preview'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => handleGenerateTermTime({ previewOnly: false })}
                        disabled={generateBusy}
                        data-testid="gen-term-time-submit"
                      >
                        {generateBusy ? 'Working…' : 'Generate'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create/Edit Activity Modal */}
      {showActivityModal && (
        <div className="modal-overlay" onClick={() => { setShowActivityModal(false); setEditingActivity(null); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingActivity ? 'Edit Activity' : 'Create Activity'}</h2>
            <form onSubmit={handleCreateActivity}>
              <div className="form-group">
                <label>Name</label>
                <input name="name" required defaultValue={editingActivity?.name || ''} data-testid="activity-name" placeholder="e.g., Jungle Day" />
              </div>
              <div className="form-group">
                <label>Description</label>
                <input name="description" required defaultValue={editingActivity?.description || ''} data-testid="activity-desc" placeholder="e.g., Explore the wild jungle" />
              </div>
              <div className="form-group">
                <label>Icon (emoji)</label>
                <input
                  name="icon"
                  required
                  value={activityIcon}
                  onChange={(e) => setActivityIcon(firstGrapheme(e.target.value))}
                  data-testid="activity-icon"
                  placeholder="Type or paste an emoji, or pick one below"
                  style={{ fontSize: '1.4rem' }}
                />
                <div
                  data-testid="activity-icon-picker"
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.3rem',
                    marginTop: '0.5rem',
                    maxHeight: 130,
                    overflowY: 'auto',
                    padding: '0.4rem',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: 6,
                  }}
                >
                  {['🦁','🐻','🐼','🐨','🦊','🐶','🐱','🐰','🦋','🐝','🐞',
                    '🌳','🌸','🌻','🌈','☀️','⭐','🌙','❄️','🔥','💧',
                    '🎨','🖌️','✂️','📚','✏️','🧩','🎭','🎪','🎁','🎈',
                    '🎵','🎶','🥁','🎺','🎸','🎤',
                    '⚽','🏀','🎾','🏊','🤸','🏃','🤾',
                    '🍎','🥕','🍓','🥪','🍞','🥛',
                    '🚂','🚀','🚌','✈️','🛼','🛝',
                    '🏰','🧸','🪀','🪁','👑','🦖','🦕','🐙'].map((em, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setActivityIcon(em)}
                      data-testid={`emoji-pick-${em}`}
                      aria-label={`Pick ${em}`}
                      style={{
                        fontSize: '1.4rem',
                        background: activityIcon === em ? 'hsl(var(--primary) / 0.15)' : 'transparent',
                        border: activityIcon === em ? '1px solid hsl(var(--primary))' : '1px solid transparent',
                        borderRadius: 6,
                        cursor: 'pointer',
                        padding: '0.15rem 0.35rem',
                        lineHeight: 1,
                      }}
                    >
                      {em}
                    </button>
                  ))}
                </div>
                <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                  Tip: any emoji works — flags, skin-tone variants and multi-character emojis are all supported. Only the first one is kept.
                </small>
              </div>
              <div className="form-group">
                <label>Color (hex)</label>
                <input name="color" type="color" required defaultValue={editingActivity?.color || '#00BCD4'} data-testid="activity-color" />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => { setShowActivityModal(false); setEditingActivity(null); }}>Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-activity">{editingActivity ? 'Save changes' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create/Edit Child Modal */}
      {showChildModal && (
        <div className="modal-overlay" onClick={() => { setShowChildModal(false); setEditingChild(null); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingChild ? 'Edit Child' : 'Add Child'}</h2>

            {/* Parent / Guardian contact (read-only summary) — surfaces
                the contact information captured during enrolment so the
                admin can see at a glance who to contact about this child.
                Falls back to the parent's user record for legacy children. */}
            {editingChild?.parent && (
              <div
                style={{
                  marginBottom: '1rem',
                  padding: '0.85rem 1rem',
                  background: 'hsl(var(--accent) / 0.4)',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 8,
                  fontSize: '0.88rem',
                }}
                data-testid="child-parent-contact-card"
              >
                <div style={{ fontWeight: 600, marginBottom: '0.35rem' }}>
                  👨‍👩‍👧 Parent / Guardian on file
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: '0.75rem', rowGap: '0.2rem' }}>
                  {editingChild.parent.full_name && (<>
                    <span style={{ color: 'hsl(var(--muted-foreground))' }}>Name</span>
                    <span data-testid="child-parent-name"><strong>{editingChild.parent.full_name}</strong> · {editingChild.parent.relationship_to_child || 'parent'}</span>
                  </>)}
                  {editingChild.parent.email && (<>
                    <span style={{ color: 'hsl(var(--muted-foreground))' }}>Email</span>
                    <span data-testid="child-parent-email">
                      <a href={`mailto:${editingChild.parent.email}`}>{editingChild.parent.email}</a>
                    </span>
                  </>)}
                  {editingChild.parent.phone && (<>
                    <span style={{ color: 'hsl(var(--muted-foreground))' }}>Phone</span>
                    <span data-testid="child-parent-phone">
                      <a href={`tel:${editingChild.parent.phone}`}>{editingChild.parent.phone}</a>
                    </span>
                  </>)}
                  {editingChild.parent.address && editingChild.parent.address.line1 && (<>
                    <span style={{ color: 'hsl(var(--muted-foreground))' }}>Address</span>
                    <span data-testid="child-parent-address">
                      {[editingChild.parent.address.line1, editingChild.parent.address.line2, editingChild.parent.address.city, editingChild.parent.address.postcode].filter(Boolean).join(', ')}
                    </span>
                  </>)}
                  {editingChild.parent.occupation && (<>
                    <span style={{ color: 'hsl(var(--muted-foreground))' }}>Occupation</span>
                    <span>{editingChild.parent.occupation}</span>
                  </>)}
                  {editingChild.parent.emergency_contact?.name && (<>
                    <span style={{ color: 'hsl(var(--muted-foreground))' }}>Emergency</span>
                    <span data-testid="child-parent-emergency">
                      <strong>{editingChild.parent.emergency_contact.name}</strong>
                      {editingChild.parent.emergency_contact.relationship ? ` (${editingChild.parent.emergency_contact.relationship})` : ''}
                      {editingChild.parent.emergency_contact.phone ? ` · ${editingChild.parent.emergency_contact.phone}` : ''}
                    </span>
                  </>)}
                </div>
              </div>
            )}

            {editingChild && (
              <PaymentPlanEditor
                child={editingChild}
                onSaved={(updated) => {
                  // Merge the new fields into the editing state so the UI
                  // reflects them without re-opening the modal, and refresh
                  // the wider children list so the row's badge updates too.
                  setEditingChild(prev => ({ ...prev, ...updated }));
                  loadData();
                }}
              />
            )}

            <form onSubmit={handleSaveChild}>
              <div className="form-group">
                <label>First Name</label>
                <input name="first_name" required defaultValue={editingChild?.first_name || ''} data-testid="child-first-name" />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input name="last_name" required defaultValue={editingChild?.last_name || ''} data-testid="child-last-name" />
              </div>
              <div className="form-group">
                <label>Date of Birth</label>
                <input name="date_of_birth" type="date" required
                  defaultValue={editingChild?.date_of_birth ? String(editingChild.date_of_birth).slice(0, 10) : ''}
                  data-testid="child-dob" />
              </div>
              <div className="form-group">
                <label>Age Group</label>
                <select name="age_group" required defaultValue={editingChild?.age_group || '3-5'} data-testid="child-age-group">
                  <option value="0-2">0-2</option>
                  <option value="2-3">2-3</option>
                  <option value="3-5">3-5</option>
                  <option value="5-11">5-11</option>
                </select>
              </div>
              <div className="form-group">
                <label>Room</label>
                <select
                  name="room_id"
                  defaultValue={editingChild?.room_id || ''}
                  data-testid="child-room-id"
                >
                  <option value="">— Unassigned —</option>
                  {rooms.map((r) => (
                    <option key={r._id} value={r._id}>
                      {r.name}{r.age_group ? ` (${r.age_group})` : ''}
                    </option>
                  ))}
                </select>
                {rooms.length === 0 && (
                  <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                    No rooms yet — create one in Staff Ops → Rooms first.
                  </small>
                )}
              </div>
              <div className="form-group">
                <label>Allergies (comma separated)</label>
                <input name="allergies" defaultValue={(editingChild?.allergies || []).join(', ')} data-testid="child-allergies" />
              </div>
              <div className="form-group">
                <label>Medical Notes</label>
                <textarea name="medical_notes" rows={3} defaultValue={editingChild?.medical_notes || ''} data-testid="child-medical-notes" />
              </div>

              {/* School information — used by Daily Roster, Transport
                  manifests and per-child collection-location flow. Free-form;
                  required-ness is enforced upstream during enrollment for
                  Breakfast / After-School Club. */}
              <details
                open={!!editingChild?.school_info?.school_name}
                style={{ marginBottom: '1rem', padding: '0.85rem', background: 'hsl(var(--muted))', borderRadius: 6 }}
              >
                <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                  🏫 School Information
                  {editingChild?.school_info?.school_name && (
                    <span style={{ marginLeft: 8, fontWeight: 400, color: 'hsl(var(--muted-foreground))', fontSize: '0.8rem' }}>
                      — {editingChild.school_info.school_name}
                      {editingChild.school_info.school_class ? ` · ${editingChild.school_info.school_class}` : ''}
                    </span>
                  )}
                </summary>
                <div style={{ marginTop: '0.75rem', display: 'grid', gap: '0.6rem' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>School Name</label>
                    <input name="school_name" defaultValue={editingChild?.school_info?.school_name || ''}
                      placeholder="St. Mary's Primary School" data-testid="edit-child-school-name" />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>School Address</label>
                    <input name="school_address" defaultValue={editingChild?.school_info?.school_address || ''}
                      placeholder="123 High Street" data-testid="edit-child-school-address" />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Postcode</label>
                      <input name="school_postcode" defaultValue={editingChild?.school_info?.school_postcode || ''}
                        placeholder="SW1A 1AA" data-testid="edit-child-school-postcode" />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Class / Year</label>
                      <input name="school_class" defaultValue={editingChild?.school_info?.school_class || ''}
                        placeholder="Year 2" data-testid="edit-child-school-class" />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Class Teacher</label>
                      <input name="teacher_name" defaultValue={editingChild?.school_info?.teacher_name || ''}
                        placeholder="Mrs. Johnson" data-testid="edit-child-teacher-name" />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>School Phone</label>
                      <input name="school_contact_number" type="tel" defaultValue={editingChild?.school_info?.school_contact_number || ''}
                        placeholder="020 1234 5678" data-testid="edit-child-school-phone" />
                    </div>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Collection Location (driver pickup)</label>
                    <input name="school_meeting_point" defaultValue={editingChild?.school_info?.school_meeting_point || ''}
                      placeholder="Main gate / Year 2 playground / Reception desk" data-testid="edit-child-school-meeting-point" />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Notes for school pickup</label>
                    <textarea name="school_notes" rows={2}
                      defaultValue={editingChild?.school_info?.school_notes || ''}
                      placeholder="Any special collection instructions"
                      data-testid="edit-child-school-notes" />
                  </div>
                </div>
              </details>

              {/* Authorised Collectors — Chain of custody */}
              <div className="form-group">
                <label>
                  Authorised collectors{' '}
                  <span style={{ fontWeight: 400, color: 'hsl(var(--muted-foreground))' }}>
                    (people other than the parent who may collect this child)
                  </span>
                </label>
                <div data-testid="authorised-collectors-list" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {(editingCollectors || []).map((p, i) => (
                    <div
                      key={i}
                      data-testid={`collector-row-${i}`}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1.4fr) minmax(0, 1.4fr) auto',
                        gap: '0.4rem',
                        alignItems: 'center',
                      }}
                    >
                      <input
                        type="text"
                        placeholder="Full name"
                        value={p.full_name}
                        onChange={(e) => {
                          const v = [...editingCollectors];
                          v[i] = { ...v[i], full_name: e.target.value };
                          setEditingCollectors(v);
                        }}
                        data-testid={`collector-name-${i}`}
                      />
                      <select
                        value={p.relationship}
                        onChange={(e) => {
                          const v = [...editingCollectors];
                          v[i] = { ...v[i], relationship: e.target.value };
                          setEditingCollectors(v);
                        }}
                        data-testid={`collector-relationship-${i}`}
                      >
                        <option value="guardian">Guardian</option>
                        <option value="grandparent">Grandparent</option>
                        <option value="parent_other">Other parent</option>
                        <option value="aunt_uncle">Aunt / Uncle</option>
                        <option value="sibling">Sibling (18+)</option>
                        <option value="friend">Family friend</option>
                        <option value="driver">Driver / minibus</option>
                        <option value="other">Other</option>
                      </select>
                      <input
                        type="tel"
                        placeholder="Phone (optional)"
                        value={p.phone}
                        onChange={(e) => {
                          const v = [...editingCollectors];
                          v[i] = { ...v[i], phone: e.target.value };
                          setEditingCollectors(v);
                        }}
                        data-testid={`collector-phone-${i}`}
                      />
                      <button
                        type="button"
                        className="btn btn-ghost"
                        style={{ padding: '0.35rem 0.5rem' }}
                        onClick={() => {
                          const v = [...editingCollectors];
                          v.splice(i, 1);
                          setEditingCollectors(v);
                        }}
                        data-testid={`collector-remove-${i}`}
                        aria-label="Remove collector"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-ghost"
                    style={{ alignSelf: 'flex-start', fontSize: '0.85rem' }}
                    onClick={() => setEditingCollectors([
                      ...(editingCollectors || []),
                      { full_name: '', relationship: 'guardian', phone: '' },
                    ])}
                    data-testid="add-collector-btn"
                  >
                    + Add authorised collector
                  </button>
                </div>
                <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                  These names are surfaced as quick-pick options on the daily roster check-out flow.
                </small>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => { setShowChildModal(false); setEditingChild(null); }}>Cancel</button>
                {editingChild?._id && editingChild?.parent_id && (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => {
                      setComposerCtx({
                        parentUserId: editingChild.parent_id,
                        childId: editingChild._id,
                        childName: `${editingChild.first_name || ''} ${editingChild.last_name || ''}`.trim(),
                      });
                    }}
                    data-testid="child-modal-message-btn"
                    title="Message this child's parent"
                  >
                    ✉️ Message parent
                  </button>
                )}
                <button type="submit" className="btn btn-primary" data-testid="submit-child">{editingChild ? 'Save changes' : 'Add child'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Booking on behalf of a parent modal — Slice A of Booking-v2 */}
      {showCreateBookingModal && (
        <div
          className="modal-overlay"
          onClick={() => !bookingFlow.submitting && setShowCreateBookingModal(false)}
          data-testid="create-booking-overlay"
        >
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 640 }}
            data-testid="create-booking-modal"
          >
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}>
              <h2 style={{ margin: 0 }}>Book on behalf of a parent</h2>
              {/* Bulk-mode toggle (Phase L). Keeps the single-session
                  flow intact while allowing an admin to book many
                  session dates for the selected child(ren) in one go. */}
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  userSelect: 'none',
                  padding: '0.3rem 0.6rem',
                  borderRadius: 999,
                  border: '1px solid hsl(var(--border))',
                  background: bookingFlow.bulkMode ? 'hsl(var(--accent))' : 'transparent',
                }}
                data-testid="booking-bulk-mode-toggle-label"
              >
                <input
                  type="checkbox"
                  checked={!!bookingFlow.bulkMode}
                  onChange={(e) => setBookingFlow((bf) => ({
                    ...bf,
                    bulkMode: e.target.checked,
                    bulkStep: 'select',
                    bulkPreview: null,
                    error: '',
                  }))}
                  data-testid="booking-bulk-mode-toggle"
                />
                <span>Bulk mode <small style={{ color: 'hsl(var(--muted-foreground))' }}>(multi-session)</small></span>
              </label>
            </div>
            <form
              onSubmit={(e) => {
                if (bookingFlow.bulkMode) {
                  e.preventDefault();
                  if (bookingFlow.bulkStep === 'select') runBulkPreview();
                  else submitBulkBooking();
                  return;
                }
                submitOnBehalfBooking(e);
              }}
              autoComplete="off"
            >
              {/* Step 1: Parent picker */}
              <div className="form-group">
                <label>1. Choose parent</label>
                {!bookingFlow.parent ? (
                  <>
                    <input
                      type="text"
                      placeholder="Search by name or email…"
                      value={bookingFlow.parentSearch}
                      onChange={(e) => setBookingFlow({ ...bookingFlow, parentSearch: e.target.value })}
                      data-testid="booking-parent-search"
                    />
                    <div
                      data-testid="booking-parent-list"
                      style={{
                        maxHeight: 180,
                        overflowY: 'auto',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: 6,
                        marginTop: '0.4rem',
                      }}
                    >
                      {(users || [])
                        .filter((u) => u.role === 'parent')
                        .filter((u) => u.approval_status !== 'pending' && u.approval_status !== 'rejected')
                        .filter((u) => {
                          const q = bookingFlow.parentSearch.trim().toLowerCase();
                          if (!q) return true;
                          return (
                            (u.full_name || '').toLowerCase().includes(q) ||
                            (u.email || '').toLowerCase().includes(q)
                          );
                        })
                        .slice(0, 30)
                        .map((u) => (
                          <button
                            key={u._id}
                            type="button"
                            className="btn btn-ghost"
                            style={{
                              display: 'block',
                              width: '100%',
                              textAlign: 'left',
                              padding: '0.4rem 0.6rem',
                              borderRadius: 0,
                              borderBottom: '1px solid hsl(var(--border))',
                            }}
                            onClick={() => selectBookingParent(u)}
                            data-testid={`booking-parent-option-${u._id}`}
                          >
                            <strong>{u.full_name || '—'}</strong>{' '}
                            <span style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.8rem' }}>
                              {u.email}
                            </span>
                          </button>
                        ))}
                      {(users || []).filter((u) => u.role === 'parent').length === 0 && (
                        <div style={{ padding: '0.6rem', color: 'hsl(var(--muted-foreground))' }}>
                          No active parents in this organisation yet.
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span data-testid="booking-parent-selected">
                      <strong>{bookingFlow.parent.full_name || '—'}</strong>{' '}
                      <span style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.85rem' }}>
                        {bookingFlow.parent.email}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      style={{ fontSize: '0.8rem' }}
                      onClick={() => setBookingFlow({ ...bookingFlow, parent: null, parentChildren: [], childIds: [] })}
                      data-testid="booking-change-parent"
                    >
                      Change
                    </button>
                  </div>
                )}
              </div>

              {/* Step 2: Multi-child picker (filtered to that parent's active children) */}
              {bookingFlow.parent && (
                <div className="form-group">
                  <label>
                    2. Choose child
                    {(bookingFlow.parentChildren || []).filter((c) => !c.is_archived).length > 1
                      ? 'ren (tick one or more)'
                      : ''}
                  </label>
                  {(bookingFlow.parentChildren || []).filter((c) => !c.is_archived).length === 0 ? (
                    <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                      This parent has no children on file. Add a child via the Children tab first.
                    </small>
                  ) : (
                    <div
                      data-testid="booking-child-multi"
                      style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}
                    >
                      {(bookingFlow.parentChildren || [])
                        .filter((c) => !c.is_archived)
                        .map((c) => {
                          const checked = (bookingFlow.childIds || []).includes(c._id);
                          return (
                            <label
                              key={c._id}
                              data-testid={`booking-child-checkbox-label-${c._id}`}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.4rem',
                                padding: '0.35rem 0.65rem',
                                border: '1px solid hsl(var(--border))',
                                borderRadius: '999px',
                                cursor: 'pointer',
                                userSelect: 'none',
                                background: checked ? 'hsl(var(--accent))' : 'hsl(var(--background))',
                                fontWeight: checked ? 600 : 400,
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  setBookingFlow((bf) => {
                                    const next = e.target.checked
                                      ? [...(bf.childIds || []), c._id]
                                      : (bf.childIds || []).filter((id) => id !== c._id);
                                    return { ...bf, childIds: next };
                                  });
                                }}
                                data-testid={`booking-child-checkbox-${c._id}`}
                              />
                              <span>
                                {c.first_name} {c.last_name}
                                {c.age_group ? ` (age group ${c.age_group})` : ''}
                              </span>
                            </label>
                          );
                        })}
                      {(bookingFlow.parentChildren || []).filter((c) => !c.is_archived).length > 1 && (
                        <div style={{ width: '100%', marginTop: '0.4rem', display: 'flex', gap: '0.5rem' }}>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ fontSize: '0.75rem', padding: '0.2rem 0.55rem' }}
                            onClick={() => setBookingFlow((bf) => ({
                              ...bf,
                              childIds: (bf.parentChildren || []).filter((c) => !c.is_archived).map((c) => c._id),
                            }))}
                            data-testid="booking-select-all-children"
                          >
                            Select all
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ fontSize: '0.75rem', padding: '0.2rem 0.55rem' }}
                            onClick={() => setBookingFlow((bf) => ({ ...bf, childIds: [] }))}
                            data-testid="booking-clear-all-children"
                          >
                            Clear
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {(bookingFlow.childIds || []).length > 1 && (
                    <small style={{ display: 'block', marginTop: 4, color: 'hsl(var(--muted-foreground))' }}>
                      A separate booking will be created for each selected child against this session.
                    </small>
                  )}
                </div>
              )}

              {/* Step 3: Session picker (single-session mode) */}
              {!bookingFlow.bulkMode && bookingFlow.parent && (bookingFlow.childIds || []).length > 0 && (
                <div className="form-group">
                  <label>3. Choose session (next 60 days)</label>
                  <select
                    value={bookingFlow.sessionInstanceId}
                    onChange={(e) => setBookingFlow({ ...bookingFlow, sessionInstanceId: e.target.value })}
                    data-testid="booking-session-select"
                    required
                  >
                    <option value="">— Select a session —</option>
                    {bookingFlow.upcomingSessions.map((s) => {
                      const date = new Date(s.date);
                      const remaining = (s.capacity ?? 0) - (s.booked_count ?? 0);
                      const isFull = remaining <= 0;
                      return (
                        <option key={s._id} value={s._id} disabled={isFull}>
                          {date.toLocaleDateString()} · {s.template_name || 'Session'}
                          {' '}({s.start_time}–{s.end_time}) ·{' '}
                          {isFull ? 'FULL — will go to waitlist' : `${remaining} spot${remaining === 1 ? '' : 's'} left`}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}

              {/* Step 3 (bulk mode): Service + multi-session picker */}
              {bookingFlow.bulkMode
                && bookingFlow.bulkStep === 'select'
                && bookingFlow.parent
                && (bookingFlow.childIds || []).length > 0 && (
                <>
                  <div className="form-group">
                    <label>3. Service</label>
                    <select
                      value={bookingFlow.bulkServiceType}
                      onChange={(e) => setBookingFlow((bf) => ({ ...bf, bulkServiceType: e.target.value }))}
                      data-testid="booking-bulk-service-select"
                    >
                      <option value="nursery_all_day">Nursery — all day</option>
                      <option value="nursery_morning">Nursery — morning</option>
                      <option value="nursery_afternoon">Nursery — afternoon</option>
                      <option value="breakfast_club">Breakfast Club</option>
                      <option value="afterschool_club">Afterschool Club</option>
                      <option value="afterschool_with_pickup">Afterschool + pickup</option>
                      <option value="holiday_club">Holiday Club</option>
                    </select>
                    <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                      One service per batch — pick a session type first, then tick the dates.
                    </small>
                  </div>

                  <div className="form-group">
                    <label>
                      4. Choose sessions (tick multiple)
                      <small style={{ color: 'hsl(var(--muted-foreground))', marginLeft: 6 }}>
                        {bookingFlow.bulkSessionIds.length} selected
                      </small>
                    </label>
                    <input
                      type="text"
                      placeholder="Filter by name (e.g. Afterschool)…"
                      value={bookingFlow.bulkTemplateFilter}
                      onChange={(e) => setBookingFlow((bf) => ({ ...bf, bulkTemplateFilter: e.target.value }))}
                      data-testid="booking-bulk-filter"
                      style={{ marginBottom: '0.4rem' }}
                    />
                    <div
                      data-testid="booking-bulk-session-list"
                      style={{
                        maxHeight: 260,
                        overflowY: 'auto',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: 6,
                      }}
                    >
                      {(() => {
                        const q = (bookingFlow.bulkTemplateFilter || '').trim().toLowerCase();
                        const filtered = (bookingFlow.upcomingSessions || []).filter((s) => {
                          if (!q) return true;
                          return (s.template_name || '').toLowerCase().includes(q);
                        });
                        if (!filtered.length) {
                          return (
                            <div style={{ padding: '0.6rem', color: 'hsl(var(--muted-foreground))' }}>
                              No upcoming sessions match your filter.
                            </div>
                          );
                        }
                        return filtered.map((s) => {
                          const date = new Date(s.date);
                          const remaining = (s.capacity ?? 0) - (s.booked_count ?? 0);
                          const isFull = remaining <= 0;
                          const checked = bookingFlow.bulkSessionIds.includes(s._id);
                          return (
                            <label
                              key={s._id}
                              data-testid={`booking-bulk-session-row-${s._id}`}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.4rem 0.6rem',
                                borderBottom: '1px solid hsl(var(--border))',
                                cursor: 'pointer',
                                background: checked ? 'hsl(var(--accent))' : 'hsl(var(--background))',
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  setBookingFlow((bf) => {
                                    const next = e.target.checked
                                      ? [...bf.bulkSessionIds, s._id]
                                      : bf.bulkSessionIds.filter((id) => id !== s._id);
                                    return { ...bf, bulkSessionIds: next };
                                  });
                                }}
                                data-testid={`booking-bulk-session-checkbox-${s._id}`}
                              />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600 }}>
                                  {date.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short' })}
                                  {' · '}{s.template_name || 'Session'}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                                  {s.start_time}–{s.end_time}
                                  {' · '}
                                  {isFull ? 'FULL — will waitlist' : `${remaining} spot${remaining === 1 ? '' : 's'} left`}
                                </div>
                              </div>
                            </label>
                          );
                        });
                      })()}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.4rem' }}>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        style={{ fontSize: '0.75rem', padding: '0.2rem 0.55rem' }}
                        onClick={() => {
                          const q = (bookingFlow.bulkTemplateFilter || '').trim().toLowerCase();
                          const ids = (bookingFlow.upcomingSessions || [])
                            .filter((s) => !q || (s.template_name || '').toLowerCase().includes(q))
                            .map((s) => s._id);
                          setBookingFlow((bf) => ({ ...bf, bulkSessionIds: ids }));
                        }}
                        data-testid="booking-bulk-select-all"
                      >
                        Select all shown
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        style={{ fontSize: '0.75rem', padding: '0.2rem 0.55rem' }}
                        onClick={() => setBookingFlow((bf) => ({ ...bf, bulkSessionIds: [] }))}
                        data-testid="booking-bulk-clear"
                      >
                        Clear
                      </button>
                    </div>
                    {bookingFlow.bulkSessionIds.length > 0 && (
                      <small style={{ display: 'block', marginTop: 6, color: 'hsl(var(--muted-foreground))' }}>
                        Will create up to{' '}
                        <strong>{bookingFlow.bulkSessionIds.length * (bookingFlow.childIds?.length || 0)}</strong>{' '}
                        booking{bookingFlow.bulkSessionIds.length * (bookingFlow.childIds?.length || 0) === 1 ? '' : 's'}{' '}
                        ({bookingFlow.bulkSessionIds.length} session
                        {bookingFlow.bulkSessionIds.length === 1 ? '' : 's'} × {bookingFlow.childIds?.length || 0} child
                        {(bookingFlow.childIds?.length || 0) === 1 ? '' : 'ren'}). You will see a preview before anything is booked.
                      </small>
                    )}
                  </div>
                </>
              )}

              {/* Step (bulk mode): Preview screen */}
              {bookingFlow.bulkMode && bookingFlow.bulkStep === 'preview' && bookingFlow.bulkPreview && (
                <div className="form-group" data-testid="booking-bulk-preview">
                  <label>Preview</label>
                  <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
                    <span
                      data-testid="booking-bulk-preview-ok-count"
                      style={{
                        padding: '0.25rem 0.6rem',
                        borderRadius: 999,
                        background: 'hsl(142 70% 92%)',
                        color: 'hsl(142 55% 25%)',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                      }}
                    >
                      ✓ {(bookingFlow.bulkPreview.confirmed || []).length} will book
                    </span>
                    <span
                      data-testid="booking-bulk-preview-wl-count"
                      style={{
                        padding: '0.25rem 0.6rem',
                        borderRadius: 999,
                        background: 'hsl(38 90% 92%)',
                        color: 'hsl(30 60% 30%)',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                      }}
                    >
                      ⏳ {(bookingFlow.bulkPreview.waitlisted || []).length} will waitlist
                    </span>
                    <span
                      data-testid="booking-bulk-preview-err-count"
                      style={{
                        padding: '0.25rem 0.6rem',
                        borderRadius: 999,
                        background: 'hsl(0 70% 94%)',
                        color: 'hsl(0 60% 35%)',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                      }}
                    >
                      ⚠️ {(bookingFlow.bulkPreview.errors || []).length} cannot book
                    </span>
                  </div>

                  <div
                    style={{
                      maxHeight: 260,
                      overflowY: 'auto',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 6,
                    }}
                  >
                    {Object.entries(bookingFlow.bulkPreview.by_session || {}).map(([sid, bucket]) => {
                      const ok = bucket.confirmed?.length || 0;
                      const wl = bucket.waitlisted?.length || 0;
                      const er = bucket.errors?.length || 0;
                      const dateLbl = bucket.session_date
                        ? new Date(bucket.session_date).toLocaleDateString(undefined, {
                            weekday: 'short',
                            day: '2-digit',
                            month: 'short',
                          })
                        : '—';
                      return (
                        <div
                          key={sid}
                          data-testid={`booking-bulk-preview-row-${sid}`}
                          style={{
                            padding: '0.5rem 0.6rem',
                            borderBottom: '1px solid hsl(var(--border))',
                          }}
                        >
                          <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                            {dateLbl} · {bucket.session_label || 'Unknown session'}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                            {ok > 0 && <span style={{ color: 'hsl(142 55% 25%)', marginRight: 8 }}>✓ {ok} book</span>}
                            {wl > 0 && <span style={{ color: 'hsl(30 60% 30%)', marginRight: 8 }}>⏳ {wl} waitlist</span>}
                            {er > 0 && (
                              <span style={{ color: 'hsl(0 60% 35%)' }}>
                                ⚠️ {er} error{er === 1 ? '' : 's'}
                                {bucket.errors?.[0]?.message ? ` — ${bucket.errors[0].message}` : ''}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {Object.keys(bookingFlow.bulkPreview.by_session || {}).length === 0 && (
                      <div style={{ padding: '0.6rem', color: 'hsl(var(--muted-foreground))' }}>
                        Nothing to preview.
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    className="btn btn-ghost"
                    style={{ marginTop: '0.5rem', fontSize: '0.8rem' }}
                    onClick={() => setBookingFlow((bf) => ({ ...bf, bulkStep: 'select', bulkPreview: null }))}
                    data-testid="booking-bulk-back"
                  >
                    ← Back to selection
                  </button>
                </div>
              )}

              {bookingFlow.error && (
                <div
                  className="alert alert-error"
                  style={{
                    color: 'hsl(0 70% 35%)',
                    background: 'hsl(0 70% 95%)',
                    padding: '0.5rem 0.75rem',
                    borderRadius: 6,
                    marginBottom: '0.75rem',
                  }}
                  data-testid="booking-error"
                >
                  {bookingFlow.error}
                </div>
              )}

              {/* Payment method choice — mirrors the parent-facing
                  payment options so admins can't accidentally mark a
                  booking as confirmed without proof of payment. The
                  default is "Send payment link to parent" (safest). */}
              <div
                className="form-group"
                data-testid="admin-payment-choice"
                style={{
                  marginTop: '0.5rem',
                  padding: '0.85rem 1rem',
                  background: 'hsl(210 40% 97%)',
                  border: '1px solid hsl(210 30% 88%)',
                  borderRadius: 8,
                }}
              >
                <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.55rem' }}>
                  {bookingFlow.bulkMode
                    ? '3. How is the parent paying? (applied to every booking created)'
                    : '3. How is the parent paying?'}
                </label>
                {[
                  { v: 'send_link',       icon: '📧', title: 'Send payment link to parent',    hint: 'Parent gets an email with a "Pay now" link — Mollie card or bank transfer. Booking stays pending until they pay.' },
                  { v: 'bank_transfer',   icon: '🏦', title: 'Bank transfer',                   hint: 'We generate a reference and email parent the bank details. You confirm receipt once funds land.' },
                  { v: 'mark_paid',       icon: '💵', title: 'Mark as already paid (cash / card at desk)', hint: 'Records payment now. Requires a reference + note — the owner sees these in the weekly review.' },
                  { v: 'add_to_invoice',  icon: '🧾', title: 'Add to next invoice / statement', hint: 'Charge goes on the parent\'s next statement. No upfront payment required.' },
                ].map((opt) => (
                  <label
                    key={opt.v}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.55rem',
                      padding: '0.5rem 0.65rem',
                      marginBottom: '0.35rem',
                      background: (bookingFlow.paymentMethod || 'send_link') === opt.v ? 'white' : 'transparent',
                      border: '1px solid ' + ((bookingFlow.paymentMethod || 'send_link') === opt.v ? 'hsl(190 60% 55%)' : 'hsl(210 25% 88%)'),
                      borderRadius: 6,
                      cursor: 'pointer',
                    }}
                    data-testid={`admin-payment-choice-${opt.v}`}
                  >
                    <input
                      type="radio"
                      name="admin_payment_choice"
                      value={opt.v}
                      checked={(bookingFlow.paymentMethod || 'send_link') === opt.v}
                      onChange={() => setBookingFlow((bf) => ({ ...bf, paymentMethod: opt.v }))}
                      style={{ marginTop: '0.15rem' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                        <span aria-hidden="true">{opt.icon}</span> {opt.title}
                        {opt.v === 'send_link' && (
                          <span style={{ marginLeft: 6, fontSize: '0.65rem', background: 'hsl(140 60% 90%)', color: 'hsl(140 40% 25%)', padding: '0.05rem 0.4rem', borderRadius: 4, fontWeight: 700, textTransform: 'uppercase' }}>
                            Recommended
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'hsl(210 15% 40%)' }}>{opt.hint}</div>
                    </div>
                  </label>
                ))}

                {/* Extra fields required for "mark_paid" — reference + note + method. */}
                {(bookingFlow.paymentMethod || 'send_link') === 'mark_paid' && (
                  <div
                    style={{
                      marginTop: '0.6rem',
                      padding: '0.7rem 0.8rem',
                      background: 'hsl(38 95% 96%)',
                      border: '1px solid hsl(38 90% 82%)',
                      borderRadius: 6,
                    }}
                    data-testid="mark-paid-extra-fields"
                  >
                    <div style={{ fontSize: '0.75rem', color: 'hsl(30 75% 25%)', marginBottom: '0.55rem' }}>
                      ⚠️ This bypasses proof-of-transfer. Both fields below are required and will be logged for the weekly owner review.
                    </div>
                    <div style={{ display: 'grid', gap: '0.5rem', gridTemplateColumns: '1fr 1fr' }}>
                      <div>
                        <label style={{ fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: 2 }}>Payment method</label>
                        <select
                          value={bookingFlow.paidMethod || 'cash'}
                          onChange={(e) => setBookingFlow((bf) => ({ ...bf, paidMethod: e.target.value }))}
                          data-testid="mark-paid-method"
                          style={{ width: '100%', padding: '0.35rem 0.5rem', fontSize: '0.85rem' }}
                        >
                          <option value="cash">Cash</option>
                          <option value="card_in_person">Card in person</option>
                          <option value="cheque">Cheque</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: 2 }}>Reference *</label>
                        <input
                          type="text"
                          placeholder="e.g. Receipt #147 or txn id"
                          value={bookingFlow.paidReference || ''}
                          onChange={(e) => setBookingFlow((bf) => ({ ...bf, paidReference: e.target.value }))}
                          data-testid="mark-paid-reference"
                          style={{ width: '100%', padding: '0.35rem 0.5rem', fontSize: '0.85rem' }}
                        />
                      </div>
                    </div>
                    <div style={{ marginTop: '0.5rem' }}>
                      <label style={{ fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: 2 }}>Note * <small style={{ color: 'hsl(210 15% 45%)', fontWeight: 400 }}>(at least 5 characters)</small></label>
                      <input
                        type="text"
                        placeholder="Short explanation — e.g. Received cash at reception on 5th"
                        value={bookingFlow.paidNote || ''}
                        onChange={(e) => setBookingFlow((bf) => ({ ...bf, paidNote: e.target.value }))}
                        data-testid="mark-paid-note"
                        style={{ width: '100%', padding: '0.35rem 0.5rem', fontSize: '0.85rem' }}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={bookingFlow.submitting || bookingFlow.bulkPreviewLoading}
                  onClick={() => setShowCreateBookingModal(false)}
                  data-testid="booking-cancel"
                >
                  Cancel
                </button>
                {!bookingFlow.bulkMode ? (
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={
                      bookingFlow.submitting ||
                      !bookingFlow.parent ||
                      !(bookingFlow.childIds && bookingFlow.childIds.length > 0) ||
                      !bookingFlow.sessionInstanceId
                    }
                    data-testid="booking-submit"
                  >
                    {bookingFlow.submitting
                      ? 'Booking…'
                      : ((bookingFlow.childIds || []).length > 1
                          ? `Confirm ${bookingFlow.childIds.length} bookings`
                          : 'Confirm booking')}
                  </button>
                ) : bookingFlow.bulkStep === 'select' ? (
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={
                      bookingFlow.bulkPreviewLoading ||
                      !bookingFlow.parent ||
                      !(bookingFlow.childIds && bookingFlow.childIds.length > 0) ||
                      !(bookingFlow.bulkSessionIds && bookingFlow.bulkSessionIds.length > 0)
                    }
                    data-testid="booking-bulk-preview-btn"
                  >
                    {bookingFlow.bulkPreviewLoading
                      ? 'Checking…'
                      : `Preview ${bookingFlow.bulkSessionIds.length * (bookingFlow.childIds?.length || 0)} booking${
                          bookingFlow.bulkSessionIds.length * (bookingFlow.childIds?.length || 0) === 1 ? '' : 's'
                        }`}
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={
                      bookingFlow.submitting ||
                      !bookingFlow.bulkPreview ||
                      ((bookingFlow.bulkPreview.confirmed || []).length + (bookingFlow.bulkPreview.waitlisted || []).length === 0)
                    }
                    data-testid="booking-bulk-confirm-btn"
                  >
                    {bookingFlow.submitting
                      ? 'Booking…'
                      : `Confirm ${
                          (bookingFlow.bulkPreview?.confirmed || []).length +
                          (bookingFlow.bulkPreview?.waitlisted || []).length
                        } booking${
                          ((bookingFlow.bulkPreview?.confirmed || []).length +
                            (bookingFlow.bulkPreview?.waitlisted || []).length) === 1 ? '' : 's'
                        }`}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Center Modal (separate from Add Center) */}
      {showCenterEditModal && editingCenter && (
        <div className="modal-overlay" onClick={() => { setShowCenterEditModal(false); setEditingCenter(null); setEditCenterAgeGroups([]); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Center</h2>
            <form onSubmit={handleSaveCenter}>
              <div className="form-group">
                <label>Center Name</label>
                <input name="name" required defaultValue={editingCenter.name || ''} data-testid="center-name" />
              </div>
              <div className="form-group">
                <label>Address</label>
                <input name="address" defaultValue={editingCenter.address || ''} data-testid="center-address" />
              </div>
              <div className="form-group" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label>City</label>
                  <input name="city" defaultValue={editingCenter.city || ''} data-testid="center-city" />
                </div>
                <div>
                  <label>Postcode</label>
                  <input name="postcode" defaultValue={editingCenter.postcode || ''} data-testid="center-postcode" />
                </div>
              </div>
              <div className="form-group" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label>Phone</label>
                  <input name="phone" defaultValue={editingCenter.phone || ''} data-testid="center-phone" />
                </div>
                <div>
                  <label>Email</label>
                  <input name="email" type="email" defaultValue={editingCenter.email || ''} data-testid="center-email" />
                </div>
              </div>
              <div className="form-group" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label>Capacity</label>
                  <input name="capacity" type="number" min="1" defaultValue={editingCenter.capacity || 50} data-testid="center-capacity" />
                </div>
                <div>
                  <label>OFSTED Number</label>
                  <input name="ofsted_number" defaultValue={editingCenter.ofsted_number || ''} data-testid="center-ofsted" />
                </div>
              </div>
              <div className="form-group">
                <label>Ofsted Registered Manager</label>
                <input
                  name="ofsted_registered_manager"
                  defaultValue={editingCenter.ofsted_registered_manager || ''}
                  placeholder="Full name of the person named on this centre's Ofsted URN"
                  data-testid="center-registered-manager"
                />
                <small style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.75rem' }}>
                  Recorded on each policy that references the Registered Manager. Overrides the org-level value for this centre.
                </small>
              </div>
              <div className="form-group">
                <label>Age Groups Accepted</label>
                <div
                  style={{
                    display: 'flex', gap: '1rem', flexWrap: 'wrap',
                    padding: '0.5rem',
                    background: 'hsl(var(--background))',
                    borderRadius: 'var(--radius)',
                    border: '1px solid hsl(var(--border))',
                  }}
                  data-testid="center-age-groups"
                >
                  {AGE_GROUP_OPTIONS.map((ag) => (
                    <label key={ag} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer', fontSize: '0.875rem' }}>
                      <input
                        type="checkbox"
                        checked={editCenterAgeGroups.includes(ag)}
                        onChange={(e) => {
                          const set = new Set(editCenterAgeGroups);
                          if (e.target.checked) set.add(ag); else set.delete(ag);
                          setEditCenterAgeGroups(Array.from(set));
                        }}
                        data-testid={`center-age-group-${ag}`}
                      />
                      {formatAgeGroupLabel(ag)}
                    </label>
                  ))}
                </div>
                <small style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.75rem' }}>
                  Per-centre age ranges override org-level defaults on the Welcome Page and booking eligibility.
                </small>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => { setShowCenterEditModal(false); setEditingCenter(null); setEditCenterAgeGroups([]); }}>Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-center">Save changes</button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Create User Modal */}
      {showUserModal && (
        <div className="modal-overlay" onClick={() => { setShowUserModal(false); setEditingUser(null); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingUser ? `Edit User — ${editingUser.full_name || ''}` : 'Create User'}</h2>
            <form key={editingUser?._id || 'new-user'} onSubmit={handleCreateUser}>
              <div className="form-group">
                <label>Full Name</label>
                <input
                  name="full_name"
                  defaultValue={editingUser?.full_name || ''}
                  required
                  data-testid="user-name"
                />
              </div>
              <div className="form-group">
                <label>Email {editingUser && <small style={{ color: 'hsl(var(--muted-foreground))' }}>(read-only)</small>}</label>
                <input
                  name="email"
                  type="email"
                  defaultValue={editingUser?.email || ''}
                  required
                  readOnly={!!editingUser}
                  style={editingUser ? { background: 'hsl(var(--muted))', cursor: 'not-allowed' } : undefined}
                  data-testid="user-email"
                />
              </div>
              <div className="form-group">
                <label>
                  {editingUser
                    ? <>New Password <small style={{ color: 'hsl(var(--muted-foreground))' }}>(leave blank to keep existing)</small></>
                    : 'Password'}
                </label>
                <input
                  name="password"
                  type="password"
                  required={!editingUser}
                  placeholder={editingUser ? '— unchanged —' : ''}
                  data-testid="user-password"
                />
              </div>
              <div className="form-group">
                <label>Role</label>
                <select
                  name="role"
                  defaultValue={editingUser?.role || 'parent'}
                  required
                  data-testid="user-role"
                >
                  <option value="parent">Parent</option>
                  <option value="staff">Staff</option>
                  <option value="room_leader">Room Leader</option>
                  <option value="deputy_manager">Deputy Manager</option>
                  <option value="driver">Driver</option>
                  <option value="org_admin">Org Admin</option>
                  <option value="admin">Admin (legacy)</option>
                </select>
                <small style={{ display: 'block', marginTop: 4, fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                  Tip: use the 🎚️ Role button on the Users list to promote / demote an existing user without recreating the account.
                </small>
              </div>
              <div className="form-group">
                <label>Phone (optional)</label>
                <input
                  name="phone"
                  type="tel"
                  defaultValue={editingUser?.phone || ''}
                  data-testid="user-phone"
                />
              </div>
              {/* Capability flag — orthogonal to the user's primary role.
                  Lets staff who do the school-run rota show up in the
                  Transport driver dropdown alongside primary drivers. */}
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', userSelect: 'none' }}>
                  <input
                    type="checkbox"
                    name="can_do_school_runs"
                    defaultChecked={!!editingUser?.can_do_school_runs}
                    data-testid="user-can-do-school-runs"
                  />
                  <span>🚐 Can do school runs (rota eligible)</span>
                </label>
                <small style={{ display: 'block', marginTop: 4, fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                  Tick this for staff (or any non-driver) who is on the centre's school-run rota.
                  Drivers (role = Driver) are automatically eligible.
                </small>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => { setShowUserModal(false); setEditingUser(null); }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" data-testid="submit-user">
                  {editingUser ? 'Save Changes' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Moment Modal */}
      {showMomentModal && (
        <div className="modal-overlay" onClick={() => setShowMomentModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Post Moment</h2>
            <form onSubmit={handleCreateMoment}>
              <div className="form-group">
                <label>Child</label>
                <select name="child_id" required data-testid="moment-child">
                  <option value="">Select child...</option>
                  {children.map(child => (
                    <option key={child._id} value={child._id}>
                      {child.first_name} {child.last_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Caption</label>
                <textarea name="caption" required data-testid="moment-caption" rows="4" />
              </div>
              <div className="form-group">
                <label>Photo or Video (optional)</label>
                <div style={{
                  display: 'flex', gap: '0.75rem', alignItems: 'flex-start',
                  padding: '0.75rem',
                  border: '1px dashed hsl(var(--border))',
                  borderRadius: 'var(--radius)',
                  background: 'hsl(var(--muted) / 0.3)',
                }}>
                  <div style={{
                    width: 80, height: 80, borderRadius: 'var(--radius)',
                    background: '#fff', border: '1px solid hsl(var(--border))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden', flexShrink: 0,
                  }}>
                    {adminMomentPhoto?.url && adminMomentPhoto.content_type?.startsWith('image/') ? (
                      <img src={adminMomentPhoto.url} alt="preview"
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }}
                        data-testid="admin-moment-photo-preview" />
                    ) : adminMomentPhoto?.url ? (
                      <span style={{ fontSize: '0.7rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))', padding: '0.25rem' }}>📹 Video</span>
                    ) : (
                      <span style={{ fontSize: '0.7rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))', padding: '0.25rem' }}>No media</span>
                    )}
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <label className="btn btn-primary" style={{ cursor: adminMomentUploading ? 'wait' : 'pointer', margin: 0 }}>
                        {adminMomentUploading ? 'Uploading…' : (adminMomentPhoto ? '🔄 Replace' : '⬆️ Upload')}
                        <input type="file" accept="image/*,video/mp4,video/quicktime,video/webm"
                          style={{ display: 'none' }}
                          disabled={adminMomentUploading}
                          onChange={(e) => { handleAdminMomentPhotoPick(e.target.files?.[0]); e.target.value = ''; }}
                          data-testid="admin-moment-file-input" />
                      </label>
                      {adminMomentPhoto && (
                        <button type="button" className="btn btn-ghost"
                          onClick={handleAdminClearMomentPhoto}
                          disabled={adminMomentUploading}>🗑️ Remove</button>
                      )}
                    </div>
                    <small style={{ color: 'hsl(var(--muted-foreground))' }}>Image or video, max 10 MB.</small>
                    {adminMomentError && (
                      <div style={{
                        fontSize: '0.8rem', color: '#9f1239',
                        background: '#fff1f2', border: '1px solid #fecdd3',
                        borderRadius: '0.375rem', padding: '0.4rem 0.6rem',
                      }}>{adminMomentError}</div>
                    )}
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label>Tags (comma-separated)</label>
                <input name="tags" data-testid="moment-tags" placeholder="e.g., painting, outdoor, creative" />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowMomentModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-moment">Post</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Moment Detail modal — opens when a card in the Moments tab is
          clicked. Shows the full media, full caption and offers a
          secondary link to the child's full profile (which was the old
          click behaviour, kept reachable). Editable in-place for senior
          staff and the original poster (see role rules in server
          crud_extensions.py::_can_edit_moment). */}
      {viewingMoment && (() => {
        const m = viewingMoment;
        const contentType = m.photo_content_type || '';
        const isVideo = contentType.startsWith('video/');
        // Client-side gate — mirrors the backend role checks so the
        // buttons don't appear for roles that would just get a 403.
        // (Backend is still authoritative — this is UX polish only.)
        const editAdminRoles = ['admin', 'org_owner', 'org_admin', 'deputy_manager', 'room_leader'];
        const deleteAdminRoles = ['admin', 'org_owner', 'org_admin', 'deputy_manager'];
        const isPoster = m.posted_by && user && m.posted_by === user._id;
        const canEdit = editAdminRoles.includes(user?.role) || isPoster;
        // Author "oops" window matches backend: 30 minutes.
        const posterWithinWindow = (() => {
          if (!isPoster || !m.created_at) return false;
          const ageMs = Date.now() - new Date(m.created_at).getTime();
          return ageMs <= 30 * 60 * 1000;
        })();
        const canDelete = deleteAdminRoles.includes(user?.role) || posterWithinWindow;
        return (
          <div
            className="modal-overlay"
            onClick={() => { if (!editingMoment) { setViewingMoment(null); setMomentActionError(''); } }}
            data-testid="moment-detail-modal"
          >
            <div
              className="modal"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: 720, width: '100%' }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <div>
                  <h2 style={{ margin: 0 }}>📸 Moment</h2>
                  <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                    {m.child_name || 'Unknown child'} • posted by {m.posted_by_name || 'unknown'} •{' '}
                    {new Date(m.created_at).toLocaleString()}
                    {m.edited_at && (
                      <>
                        {' • '}
                        <span style={{ fontStyle: 'italic' }} data-testid="moment-detail-edited-badge">
                          edited {new Date(m.edited_at).toLocaleString()}
                        </span>
                      </>
                    )}
                  </small>
                </div>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => { setViewingMoment(null); setEditingMoment(null); setMomentActionError(''); }}
                  data-testid="moment-detail-close"
                  aria-label="Close moment detail"
                >
                  ✕
                </button>
              </div>

              {/* Full-size media. Falls back to a hint when a moment
                  has no attached photo/video (legacy caption-only rows). */}
              {m.photo_url ? (
                <div style={{ margin: '0.75rem 0', background: '#000', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                  {isVideo ? (
                    <video
                      src={m.photo_url}
                      controls
                      autoPlay={false}
                      preload="metadata"
                      data-testid="moment-detail-video"
                      style={{ width: '100%', maxHeight: '60vh', display: 'block', background: '#000' }}
                    />
                  ) : (
                    <img
                      src={m.photo_url}
                      alt={m.caption || 'Moment media'}
                      data-testid="moment-detail-image"
                      style={{ width: '100%', maxHeight: '60vh', objectFit: 'contain', background: '#000', display: 'block' }}
                    />
                  )}
                </div>
              ) : (
                <div
                  style={{
                    margin: '0.75rem 0',
                    padding: '1rem',
                    border: '1px dashed hsl(var(--border))',
                    borderRadius: 'var(--radius)',
                    color: 'hsl(var(--muted-foreground))',
                    fontSize: '0.85rem',
                    textAlign: 'center',
                  }}
                  data-testid="moment-detail-no-media"
                >
                  No photo or video attached to this moment.
                </div>
              )}

              {/* Full caption — never clamped in the detail view.
                  Switches to an inline textarea when the admin/poster
                  toggles Edit mode. */}
              {!editingMoment ? (
                m.caption && (
                  <p
                    style={{ margin: '0.5rem 0', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}
                    data-testid="moment-detail-caption"
                  >
                    {m.caption}
                  </p>
                )
              ) : (
                <div className="form-group" style={{ margin: '0.75rem 0' }}>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600 }}>Caption</label>
                  <textarea
                    value={editingMoment.caption}
                    onChange={(e) =>
                      setEditingMoment((em) => ({ ...em, caption: e.target.value }))
                    }
                    rows={4}
                    maxLength={2000}
                    data-testid="moment-edit-caption"
                    style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
                  />
                </div>
              )}

              {!editingMoment ? (
                m.tags && m.tags.length > 0 && (
                  <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', margin: '0.5rem 0 0.75rem' }}>
                    {m.tags.map((tag, i) => (
                      <span
                        key={i}
                        className="badge badge-blue"
                        data-testid={`moment-detail-tag-${i}`}
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )
              ) : (
                <div className="form-group" style={{ margin: '0.5rem 0 0.75rem' }}>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                    Tags <small style={{ color: 'hsl(var(--muted-foreground))' }}>(comma-separated)</small>
                  </label>
                  <input
                    type="text"
                    value={editingMoment.tagsInput}
                    onChange={(e) =>
                      setEditingMoment((em) => ({ ...em, tagsInput: e.target.value }))
                    }
                    data-testid="moment-edit-tags"
                    placeholder="e.g. play, outdoors, art"
                    style={{ width: '100%' }}
                  />
                </div>
              )}

              {/* Media editor (edit mode only) — lets senior staff/poster
                  add, replace, or remove the photo/video attached to a
                  moment. Uploads go through the same /api/files/upload
                  endpoint used by the create-moment flow. */}
              {editingMoment && (
                <div
                  className="form-group"
                  style={{
                    margin: '0.5rem 0 0.75rem',
                    padding: '0.75rem',
                    border: '1px dashed hsl(var(--border))',
                    borderRadius: 'var(--radius)',
                    background: 'hsl(var(--muted) / 0.3)',
                  }}
                  data-testid="moment-edit-media"
                >
                  <label style={{ fontSize: '0.8rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                    Photo / Video
                  </label>

                  {/* Preview of pending state */}
                  {editingMoment.photo ? (
                    <div style={{ marginBottom: '0.5rem' }}>
                      <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.35rem' }}>
                        ✅ New media ready — save to apply
                      </div>
                      {editingMoment.photo.content_type?.startsWith('video/') ? (
                        <video
                          src={editingMoment.photo.url}
                          controls
                          preload="metadata"
                          data-testid="moment-edit-media-new-video"
                          style={{ width: '100%', maxHeight: 200, borderRadius: 6, background: '#000' }}
                        />
                      ) : (
                        <img
                          src={editingMoment.photo.url}
                          alt="New media preview"
                          data-testid="moment-edit-media-new-image"
                          style={{ width: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 6, background: '#000' }}
                        />
                      )}
                    </div>
                  ) : editingMoment.removeMedia ? (
                    <div
                      style={{
                        padding: '0.5rem',
                        background: 'hsl(0 70% 95%)',
                        color: 'hsl(0 70% 35%)',
                        borderRadius: 6,
                        fontSize: '0.85rem',
                        marginBottom: '0.5rem',
                      }}
                      data-testid="moment-edit-media-remove-flag"
                    >
                      🗑️ Existing media will be removed on save
                    </div>
                  ) : m.photo_url ? (
                    <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.5rem' }}>
                      Current media will be kept unless replaced or removed below.
                    </div>
                  ) : (
                    <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.5rem' }}>
                      No media attached — add one below (optional).
                    </div>
                  )}

                  {/* Upload / remove controls */}
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    <input
                      type="file"
                      accept="image/*,video/*"
                      disabled={editingMoment.mediaUploading || momentActionSubmitting}
                      data-testid="moment-edit-media-input"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setEditingMoment((em) => ({ ...em, mediaError: null }));
                        if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
                          setEditingMoment((em) => ({ ...em, mediaError: 'Please choose an image or short video.' }));
                          return;
                        }
                        if (file.size > 10 * 1024 * 1024) {
                          setEditingMoment((em) => ({ ...em, mediaError: 'File too large (max 10 MB).' }));
                          return;
                        }
                        setEditingMoment((em) => ({ ...em, mediaUploading: true }));
                        try {
                          const uploaded = await api.uploadFile(file, { category: 'moments' });
                          // Track the newly uploaded file. `content_type`
                          // isn't always echoed by the server, so fall
                          // back to the file object.
                          const contentType = uploaded.content_type || file.type;
                          setEditingMoment((em) => ({
                            ...em,
                            photo: { ...uploaded, content_type: contentType },
                            removeMedia: false,
                            mediaUploading: false,
                            mediaError: null,
                          }));
                        } catch (err) {
                          setEditingMoment((em) => ({
                            ...em,
                            mediaUploading: false,
                            mediaError: err.message || 'Upload failed',
                          }));
                        } finally {
                          // Reset the input so re-selecting the same file re-triggers onChange.
                          try { e.target.value = ''; } catch { /* noop */ }
                        }
                      }}
                    />
                    {editingMoment.photo && (
                      <button
                        type="button"
                        className="btn btn-ghost"
                        style={{ fontSize: '0.8rem' }}
                        disabled={momentActionSubmitting}
                        onClick={async () => {
                          // Best-effort cleanup of the just-uploaded temp file.
                          if (editingMoment.photo?.file_id) {
                            try { await api.deleteFile(editingMoment.photo.file_id); } catch { /* noop */ }
                          }
                          setEditingMoment((em) => ({ ...em, photo: null, mediaError: null }));
                        }}
                        data-testid="moment-edit-media-cancel-upload"
                      >
                        Discard new upload
                      </button>
                    )}
                    {m.photo_url && !editingMoment.photo && !editingMoment.removeMedia && (
                      <button
                        type="button"
                        className="btn btn-ghost"
                        style={{ fontSize: '0.8rem', color: 'hsl(0 60% 40%)' }}
                        disabled={momentActionSubmitting}
                        onClick={() => setEditingMoment((em) => ({ ...em, removeMedia: true, mediaError: null }))}
                        data-testid="moment-edit-media-remove"
                      >
                        🗑️ Remove current media
                      </button>
                    )}
                    {editingMoment.removeMedia && (
                      <button
                        type="button"
                        className="btn btn-ghost"
                        style={{ fontSize: '0.8rem' }}
                        disabled={momentActionSubmitting}
                        onClick={() => setEditingMoment((em) => ({ ...em, removeMedia: false }))}
                        data-testid="moment-edit-media-remove-undo"
                      >
                        Undo remove
                      </button>
                    )}
                  </div>

                  {editingMoment.mediaUploading && (
                    <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.35rem' }} data-testid="moment-edit-media-uploading">
                      Uploading…
                    </div>
                  )}
                  {editingMoment.mediaError && (
                    <div
                      style={{
                        fontSize: '0.8rem',
                        color: 'hsl(0 70% 35%)',
                        marginTop: '0.35rem',
                      }}
                      data-testid="moment-edit-media-error"
                    >
                      {editingMoment.mediaError}
                    </div>
                  )}
                </div>
              )}

              {momentActionError && (
                <div
                  className="alert alert-error"
                  data-testid="moment-detail-error"
                  style={{
                    color: 'hsl(0 70% 35%)',
                    background: 'hsl(0 70% 95%)',
                    padding: '0.5rem 0.75rem',
                    borderRadius: 6,
                    marginBottom: '0.5rem',
                    fontSize: '0.85rem',
                  }}
                >
                  {momentActionError}
                </div>
              )}

              {/* Comment thread — parents and staff can post replies.
                  Hidden while an admin is inline-editing the moment so
                  the modal stays focused on a single task. */}
              {!editingMoment && (
                <MomentComments
                  momentId={m._id}
                  currentUser={user}
                  onCountChange={(n) => {
                    // Keep the card badge in sync so users don't need to
                    // re-fetch the whole moments list after replying.
                    setMoments((prev) =>
                      (prev || []).map((x) => (x._id === m._id ? { ...x, comment_count: n } : x))
                    );
                    setViewingMoment((vm) => (vm && vm._id === m._id ? { ...vm, comment_count: n } : vm));
                  }}
                />
              )}

              <div className="modal-actions" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                {/* Left cluster: secondary + edit/delete admin actions */}
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {!editingMoment && m.child_id && (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => {
                        setViewingMoment(null);
                        setViewingChildId(m.child_id);
                      }}
                      data-testid="moment-detail-open-child"
                    >
                      👶 View {m.child_name?.split(' ')[0] || 'child'}&apos;s full profile →
                    </button>
                  )}
                  {!editingMoment && canEdit && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setMomentActionError('');
                        setEditingMoment({
                          caption: m.caption || '',
                          tagsInput: Array.isArray(m.tags) ? m.tags.join(', ') : '',
                          // Media editor state:
                          //   photo        → object {file_id,url,content_type} for a NEW upload
                          //   removeMedia  → true when user asked to clear existing media
                          //   mediaUploading / mediaError → upload UX state
                          photo: null,
                          removeMedia: false,
                          mediaUploading: false,
                          mediaError: null,
                        });
                      }}
                      data-testid="moment-detail-edit"
                    >
                      ✏️ Edit
                    </button>
                  )}
                  {!editingMoment && canDelete && (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      style={{ color: 'hsl(0 60% 40%)' }}
                      disabled={momentActionSubmitting}
                      onClick={async () => {
                        const label = m.child_name ? `moment for ${m.child_name}` : 'this moment';
                        if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
                        setMomentActionError('');
                        setMomentActionSubmitting(true);
                        try {
                          await api.deleteMoment(m._id);
                          setViewingMoment(null);
                          setEditingMoment(null);
                          toast.success('Moment deleted.');
                          // Optimistic list update so the card disappears immediately.
                          setMoments((prev) => (prev || []).filter((x) => x._id !== m._id));
                          // Refresh from server as source of truth.
                          try {
                            const fresh = await api.getMoments();
                            setMoments(fresh);
                          } catch (_e) {
                            /* non-fatal */
                          }
                        } catch (err) {
                          setMomentActionError(err.message || 'Failed to delete moment');
                        } finally {
                          setMomentActionSubmitting(false);
                        }
                      }}
                      data-testid="moment-detail-delete"
                    >
                      🗑️ Delete
                    </button>
                  )}
                </div>

                {/* Right cluster: Save/Cancel (edit mode) or Done (view mode) */}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {editingMoment ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        disabled={momentActionSubmitting}
                        onClick={() => {
                          setEditingMoment(null);
                          setMomentActionError('');
                        }}
                        data-testid="moment-detail-edit-cancel"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={momentActionSubmitting || editingMoment.mediaUploading}
                        onClick={async () => {
                          setMomentActionError('');
                          setMomentActionSubmitting(true);
                          try {
                            const tags = editingMoment.tagsInput
                              .split(',')
                              .map((t) => t.trim())
                              .filter(Boolean);
                            const payload = {
                              caption: editingMoment.caption,
                              tags,
                            };
                            // Media handling:
                            //   • New upload → replace with new photo_url/file_id/content_type
                            //   • Remove flag → send null to clear all media fields on server
                            //   • Otherwise  → omit media fields (keep existing)
                            if (editingMoment.photo) {
                              payload.photo_url = editingMoment.photo.url || null;
                              payload.media_file_id = editingMoment.photo.file_id || null;
                              payload.photo_content_type =
                                editingMoment.photo.content_type || null;
                            } else if (editingMoment.removeMedia) {
                              payload.photo_url = null;
                              payload.media_file_id = null;
                              payload.photo_content_type = null;
                            }
                            const updated = await api.updateMoment(m._id, payload);
                            // Prefer the server's returned object; fall
                            // back to a local merge if it's just an ack.
                            const merged = updated && updated._id
                              ? {
                                  ...m,
                                  ...updated,
                                  // If the caller replaced media, prefer
                                  // the freshly-uploaded URL (which is a
                                  // presigned link the browser can render
                                  // right away) over whatever the server
                                  // echoes back — the server-side
                                  // presigned URL is refreshed on the
                                  // next GET /api/moments anyway.
                                  photo_url:
                                    (editingMoment.photo && editingMoment.photo.url) ||
                                    (editingMoment.removeMedia ? null : (updated.photo_url || m.photo_url)),
                                  photo_content_type:
                                    (editingMoment.photo && editingMoment.photo.content_type) ||
                                    (editingMoment.removeMedia ? null : (updated.photo_content_type || m.photo_content_type)),
                                }
                              : {
                                  ...m,
                                  caption: editingMoment.caption,
                                  tags,
                                  photo_url: editingMoment.photo?.url
                                    || (editingMoment.removeMedia ? null : m.photo_url),
                                  photo_content_type: editingMoment.photo?.content_type
                                    || (editingMoment.removeMedia ? null : m.photo_content_type),
                                };
                            setViewingMoment(merged);
                            setMoments((prev) =>
                              (prev || []).map((x) => (x._id === m._id ? merged : x))
                            );
                            setEditingMoment(null);
                            toast.success('Moment updated.');
                          } catch (err) {
                            setMomentActionError(err.message || 'Failed to update moment');
                          } finally {
                            setMomentActionSubmitting(false);
                          }
                        }}
                        data-testid="moment-detail-edit-save"
                      >
                        {momentActionSubmitting ? 'Saving…' : 'Save changes'}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => { setViewingMoment(null); setEditingMoment(null); setMomentActionError(''); }}
                      data-testid="moment-detail-done"
                    >
                      Done
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Create Staff Profile Modal */}
      {showStaffModal && (
        <div className="modal-overlay" onClick={() => { setShowStaffModal(false); setEditingStaff(null); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingStaff ? `Edit Staff Profile — ${editingStaff.user_name || ''}` : 'Add Staff Profile'}</h2>
            <form key={editingStaff?._id || 'new'} onSubmit={handleCreateStaffProfile}>
              <div className="form-group">
                <label>{editingStaff ? 'Linked User' : 'Select User'}</label>
                {editingStaff ? (
                  // In edit mode the user link is immutable — show a read-only
                  // summary and submit the existing user_id via a hidden input.
                  <>
                    <input
                      type="hidden"
                      name="user_id"
                      value={editingStaff.user_id || ''}
                      data-testid="staff-user-id"
                    />
                    <div
                      style={{
                        padding: '0.6rem 0.85rem',
                        background: 'hsl(var(--muted))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: 'var(--radius)',
                        fontSize: '0.9rem',
                      }}
                      data-testid="staff-user-readonly"
                    >
                      {editingStaff.user_name || '—'}{editingStaff.user_email ? ` (${editingStaff.user_email})` : ''}
                    </div>
                    {/* Quick access to the staff ID card + share link
                        without navigating away from the profile drawer. */}
                    {editingStaff.user_id && (
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => setViewingStaffIdFor({
                          userId: editingStaff.user_id,
                          label: editingStaff.user_name || '',
                        })}
                        data-testid={`staff-profile-view-id-${editingStaff.user_id}`}
                        style={{ marginTop: 8 }}
                      >
                        🎫 View Staff ID card
                      </button>
                    )}
                  </>
                ) : (
                  <select name="user_id" required data-testid="staff-user-id">
                    <option value="">-- Select user --</option>
                    {users.filter(u => u.role === 'staff' || u.role === 'driver').map(u => (
                      <option key={u._id} value={u._id}>{u.full_name} ({u.email})</option>
                    ))}
                  </select>
                )}
              </div>
              
              <div className="form-group">
                <label>Competencies (check all that apply)</label>
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input type="checkbox" name="first_aider" defaultChecked={editingStaff?.competencies?.includes('first_aider')} data-testid="comp-first-aider" />
                    <span>🩹 First Aider</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input type="checkbox" name="unqualified" defaultChecked={editingStaff?.competencies?.includes('unqualified')} data-testid="comp-unqualified" />
                    <span>🧑‍🤝‍🧑 Unqualified <small style={{ color: 'hsl(var(--muted-foreground))' }}>(support staff — does not count toward qualified-headcount ratios)</small></span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input type="checkbox" name="level_2" defaultChecked={editingStaff?.competencies?.includes('level_2_qualified')} data-testid="comp-level-2" />
                    <span>🎓 Level 2 Qualified</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input type="checkbox" name="level_3" defaultChecked={editingStaff?.competencies?.includes('level_3_qualified')} data-testid="comp-level-3" />
                    <span>🎓 Level 3 Qualified</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input type="checkbox" name="level_5" defaultChecked={editingStaff?.competencies?.includes('level_5_qualified')} data-testid="comp-level-5" />
                    <span>🎓 Level 5 Qualified</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input type="checkbox" name="food_hygiene" defaultChecked={editingStaff?.competencies?.includes('food_hygiene_l2_l3')} data-testid="comp-food-hygiene" />
                    <span>🍽️ Food &amp; Hygiene Level 2-3</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input type="checkbox" name="driver" defaultChecked={editingStaff?.competencies?.includes('driver')} data-testid="comp-driver" />
                    <span>🚗 Driver</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input type="checkbox" name="senco" defaultChecked={editingStaff?.competencies?.includes('senco')} data-testid="comp-senco" />
                    <span>👨‍🏫 SENCO</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input type="checkbox" name="safeguarding" defaultChecked={editingStaff?.competencies?.includes('safeguarding_lead')} data-testid="comp-safeguarding" />
                    <span>🛡️ Safeguarding Lead</span>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>Employment Type</label>
                <select
                  name="employment_type"
                  value={staffModalEmpType}
                  onChange={(e) => setStaffModalEmpType(e.target.value)}
                  data-testid="staff-employment-type"
                >
                  <option value="employee">👨‍🏫 Employee (payroll)</option>
                  <option value="contractor">🧑‍💼 Contractor (invoice)</option>
                  <option value="volunteer">🤝 Volunteer (unpaid)</option>
                </select>
              </div>

              {staffModalEmpType !== 'volunteer' ? (
                <div className="form-group">
                  <label>Hourly Rate (£)</label>
                  <input
                    name="hourly_rate"
                    type="number"
                    step="0.01"
                    defaultValue={editingStaff ? (editingStaff.hourly_rate ?? 0) : '15.00'}
                    required
                    data-testid="staff-hourly-rate"
                  />
                </div>
              ) : (
                <div className="form-group">
                  <label>Hourly Rate</label>
                  <div style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))', padding: '0.5rem 0.75rem', border: '1px dashed hsl(var(--border))', borderRadius: 6 }}>
                    🤝 Volunteers are unpaid — pay totals will display "—" on timesheets and they're excluded from payroll CSV by default.
                  </div>
                  <input type="hidden" name="hourly_rate" value="0" />
                </div>
              )}

              <div className="form-group">
                <label>Contract Hours (per week)</label>
                <input
                  name="contract_hours"
                  type="number"
                  defaultValue={editingStaff ? (editingStaff.contract_hours ?? 0) : '40'}
                  required
                  data-testid="staff-contract-hours"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Annual leave (days)</label>
                  <input
                    name="annual_leave_entitlement"
                    type="number" min="0" max="60"
                    defaultValue={editingStaff ? (editingStaff.annual_leave_entitlement ?? 28) : 28}
                    required
                    data-testid="staff-annual-leave"
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Carried over (days)</label>
                  <input
                    name="carried_over_leave_days"
                    type="number" min="0" max="40"
                    defaultValue={editingStaff ? (editingStaff.carried_over_leave_days ?? 0) : 0}
                    data-testid="staff-carried-leave"
                  />
                </div>
              </div>

              {/* Admin-side availability viewer/editor — only meaningful in
                  edit mode (we need a saved staff_id). For new profiles
                  the staff member can self-declare availability after
                  their account is created. */}
              {editingStaff && editingStaff._id && (
                <div
                  style={{
                    marginTop: '1rem',
                    paddingTop: '1rem',
                    borderTop: '1px solid hsl(var(--border))',
                  }}
                >
                  <AvailabilityEditor
                    staffId={editingStaff._id}
                    canEdit={true}
                    title="📅 Weekly availability"
                  />
                </div>
              )}

              {/* Phase M — Extended HR sections (employment lifecycle,
                  job info, HMRC, emergency contact, custom competencies,
                  history timelines, HR documents, archive/restore). */}
              <StaffProfileExtras
                staff={editingStaff}
                users={users}
                rooms={rooms}
                onArchived={() => { setShowStaffModal(false); setEditingStaff(null); loadData(); }}
                onRestored={() => { setShowStaffModal(false); setEditingStaff(null); loadData(); }}
              />

              {/* Payment & HMRC details + payslip generator — admin
                  only. Renders inline inside the edit modal so admins
                  manage everything for one staff member in one place. */}
              {editingStaff && editingStaff._id && (
                <div
                  style={{
                    marginTop: '1rem',
                    paddingTop: '1rem',
                    borderTop: '1px solid hsl(var(--border))',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75rem',
                  }}
                >
                  <PaymentDetailsCard
                    staffId={editingStaff._id}
                    staffName={editingStaff.user_name}
                    employmentType={editingStaff.employment_type || staffModalEmpType}
                  />
                  {(editingStaff.employment_type || staffModalEmpType) !== 'volunteer' && (
                    <button
                      type="button"
                      onClick={() => setPayslipDialogFor(editingStaff)}
                      data-testid="open-payslip-dialog"
                      style={{
                        alignSelf: 'flex-start',
                        padding: '0.6rem 1rem',
                        background: '#10b981',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 700,
                      }}
                    >
                      💷 Generate payslip…
                    </button>
                  )}
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => { setShowStaffModal(false); setEditingStaff(null); }}>Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-staff-profile">
                  {editingStaff ? 'Save Changes' : 'Create Profile'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Room Modal */}
      {showRoomModal && (
        <div className="modal-overlay" onClick={() => setShowRoomModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Create Room</h2>
            <form onSubmit={handleCreateRoom}>
              <div className="form-group">
                <label>Room Name</label>
                <input name="name" required placeholder="e.g., Sunshine Room" data-testid="room-name" />
              </div>

              <div className="form-group">
                <label>Age Group</label>
                <select name="age_group" required data-testid="room-age-group">
                  <option value="infant">Infant (0-2 years)</option>
                  <option value="toddler">Toddler (2-3 years)</option>
                  <option value="preschool">Preschool (3-5 years)</option>
                  <option value="primary">Primary (5+ years)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Capacity</label>
                <input name="capacity" type="number" required defaultValue="20" data-testid="room-capacity" />
              </div>

              <div className="form-group">
                <label>Required Staff-to-Child Ratio</label>
                <select name="required_ratio" required data-testid="room-ratio">
                  <option value="0.333">1:3 (Infants under 2)</option>
                  <option value="0.25">1:4 (2 year olds)</option>
                  <option value="0.125">1:8 (3+ years)</option>
                  <option value="0.1">1:10 (School age)</option>
                  <option value="0.0667">1:15 (Out of school)</option>

                </select>
              </div>

              <div className="form-group">
                <label>First Aider Required?</label>
                <select name="required_first_aider" required data-testid="room-first-aider">
                  <option value="true">Yes - Required</option>
                  <option value="false">No - Not Required</option>
                </select>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowRoomModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-room">Create Room</button>
              </div>
            </form>
          </div>
        </div>
      )}


        {/* ENROLLMENTS TAB */}
        {activeTab === 'enrollments' && (
          <>
            <div className="page-header">
              <div>
                <h1>Enrollment Applications</h1>
                <p>Review and approve new child enrollments</p>
              </div>
              <button
                className="btn btn-primary"
                onClick={() => setShowWalkInHelper(true)}
                data-testid="create-walkin-enrolment-btn"
                title="Walk through what to do when a family arrives in person without using the online wizard"
              >
                ➕ Create Enrolment (walk-in)
              </button>
            </div>

            {loading ? (
              <div>Loading enrollments...</div>
            ) : enrollments.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {enrollments.map(enrollment => {
                  const statusColors = {
                    draft: '#999',
                    submitted: '#3b82f6',
                    under_review: '#f59e0b',
                    approved: '#10b981',
                    rejected: '#ef4444'
                  };

                  return (
                    <div 
                      key={enrollment._id || enrollment.id} 
                      className="card"
                      style={{ borderLeft: `4px solid ${statusColors[enrollment.status]}` }}
                      data-testid={`enrollment-card-${enrollment.status}`}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
                            <h3 style={{ margin: 0 }}>
                              {enrollment.child_first_name} {enrollment.child_last_name}
                            </h3>
                            <span 
                              className="badge"
                              style={{ 
                                background: statusColors[enrollment.status],
                                color: 'white',
                                textTransform: 'capitalize'
                              }}
                            >
                              {enrollment.status.replace('_', ' ')}
                            </span>
                          </div>

                          <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                            gap: '1rem',
                            marginBottom: '1rem'
                          }}>
                            <div>
                              <strong>Child Info:</strong><br />
                              DOB: {new Date(enrollment.child_date_of_birth).toLocaleDateString()}<br />
                              Age Group: {enrollment.child_age_group}
                            </div>
                            
                            {enrollment.parent_info && (
                              <div>
                                <strong>Parent/Guardian:</strong><br />
                                {enrollment.parent_info.phone}<br />
                                {enrollment.parent_info.city}, {enrollment.parent_info.postcode}
                              </div>
                            )}

                            {enrollment.medical_info && enrollment.medical_info.allergies && enrollment.medical_info.allergies.length > 0 && (
                              <div>
                                <strong style={{ color: '#ef4444' }}>⚠️ Allergies:</strong><br />
                                {enrollment.medical_info.allergies.join(', ')}
                              </div>
                            )}

                            {enrollment.submitted_at && (
                              <div>
                                <strong>Submitted:</strong><br />
                                {new Date(enrollment.submitted_at).toLocaleString()}
                              </div>
                            )}
                          </div>

                          {enrollment.preferred_days && enrollment.preferred_days.length > 0 && (
                            <div style={{ marginBottom: '0.5rem' }}>
                              <strong>Preferred Days:</strong> {enrollment.preferred_days.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')}
                            </div>
                          )}

                          {enrollment.admin_notes && (
                            <div style={{ 
                              marginTop: '1rem',
                              padding: '0.75rem',
                              background: 'hsl(var(--muted))',
                              borderRadius: '4px'
                            }}>
                              <strong>Admin Notes:</strong> {enrollment.admin_notes}
                            </div>
                          )}

                          {enrollment.status === 'rejected' && enrollment.rejection_reason && (
                            <div style={{ 
                              marginTop: '1rem',
                              padding: '0.75rem',
                              background: '#fee2e2',
                              borderRadius: '4px'
                            }}>
                              <strong>Rejection Reason:</strong> {enrollment.rejection_reason}
                            </div>
                          )}

                          {/* View buttons row — every enrolment gets a
                              "📋 View full details" toggle (in-page expand)
                              and a "📄 View signed application" link to the
                              printable PDF. The PDF link only renders for
                              submitted/approved status because earlier
                              statuses won't yet have the parent signature
                              attached. */}
                          <div
                            style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}
                            data-testid={`enrollment-actions-${enrollment._id || enrollment.id}`}
                          >
                            <button
                              type="button"
                              className="btn btn-ghost"
                              style={{ fontSize: '0.85rem' }}
                              onClick={() => setExpandedEnrollmentId(
                                expandedEnrollmentId === (enrollment._id || enrollment.id)
                                  ? null
                                  : (enrollment._id || enrollment.id)
                              )}
                              data-testid={`enrollment-expand-${enrollment._id || enrollment.id}`}
                            >
                              {expandedEnrollmentId === (enrollment._id || enrollment.id) ? '▲ Hide details' : '📋 View full details'}
                            </button>
                            {(enrollment.status === 'submitted' || enrollment.status === 'approved') && (
                              <a
                                href={`${process.env.REACT_APP_BACKEND_URL}/api/enrollments/${enrollment._id || enrollment.id}/printable?token=${encodeURIComponent(localStorage.getItem('token') || '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-ghost"
                                style={{ fontSize: '0.85rem' }}
                                data-testid={`enrollment-printable-${enrollment._id || enrollment.id}`}
                                title="Open the full signed enrolment in a new tab — printable / saveable as PDF from the browser print dialog"
                              >
                                📄 View signed application
                              </a>
                            )}
                          </div>

                          {/* Full details expansion. Renders only when
                              this card's id matches expandedEnrollmentId.
                              We surface every field the parent submitted
                              so the admin can review before approving —
                              previously these fields were collected but
                              never displayed. */}
                          {expandedEnrollmentId === (enrollment._id || enrollment.id) && (
                            <div
                              data-testid={`enrollment-details-${enrollment._id || enrollment.id}`}
                              style={{
                                marginTop: '1rem',
                                padding: '1rem',
                                background: 'hsl(var(--muted))',
                                borderRadius: 6,
                                border: '1px solid hsl(var(--border))',
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                                gap: '1rem',
                              }}
                            >
                              {/* Services chosen + preferred start.
                                  Previously missing from the review UI —
                                  the parent supplied these on the wizard
                                  (Step 1 services, Step 4 start date) but
                                  the admin never saw them. */}
                              <DetailBlock title="📚 Service & Start">
                                <DetailRow
                                  k="Services requested"
                                  v={(() => {
                                    const svcs = enrollment.services_required || [];
                                    if (!svcs.length) return <em>(none specified)</em>;
                                    const labels = {
                                      breakfast_club: 'Breakfast Club',
                                      after_school_club: 'After-school Club',
                                      holiday_club: 'Holiday Club',
                                      nursery_full_day: 'Nursery (full day)',
                                      other: 'Other',
                                    };
                                    return svcs.map(s => labels[s] || s).join(', ');
                                  })()}
                                />
                                <DetailRow
                                  k="Preferred start date"
                                  v={enrollment.preferred_start_date
                                    ? new Date(enrollment.preferred_start_date).toLocaleDateString()
                                    : <em>(not specified)</em>}
                                />
                                <DetailRow
                                  k="Preferred days"
                                  v={(enrollment.preferred_days || []).length
                                    ? enrollment.preferred_days.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')
                                    : <em>(not specified)</em>}
                                />
                                <DetailRow
                                  k="Preferred sessions"
                                  v={(enrollment.preferred_sessions || []).length
                                    ? enrollment.preferred_sessions.join(', ')
                                    : <em>(not specified)</em>}
                                />
                                <DetailRow
                                  k="Payment plan"
                                  v={(() => {
                                    const p = enrollment.payment_plan || 'pay_as_you_go';
                                    const labels = {
                                      pay_as_you_go: 'Pay as you go (per-session / block bookings)',
                                      monthly_term_time: 'Monthly — term-time only (11 payments, Sept–July)',
                                      monthly_full_year: 'Monthly — full year (12 payments, Sept–Aug)',
                                    };
                                    return labels[p] || p;
                                  })()}
                                />
                                <DetailRow
                                  k="Billing terms"
                                  v={(() => {
                                    const t = enrollment.billing_terms || 'standard';
                                    return t === 'invoice'
                                      ? 'Invoice / contractual (org invoices family, external payment)'
                                      : 'Standard (parent self-pays via app)';
                                  })()}
                                />
                              </DetailBlock>

                              {/* Child block */}
                              <DetailBlock title="👶 Child">
                                <DetailRow k="First name" v={enrollment.child_first_name} />
                                <DetailRow k="Last name" v={enrollment.child_last_name} />
                                <DetailRow k="Date of birth" v={enrollment.child_date_of_birth ? new Date(enrollment.child_date_of_birth).toLocaleDateString() : '—'} />
                                <DetailRow k="Age group" v={enrollment.child_age_group} />
                                <DetailRow k="Gender" v={enrollment.child_gender} />
                                <DetailRow k="Languages at home" v={(enrollment.child_languages || []).join(', ')} />
                              </DetailBlock>

                              {/* Parent / guardian block */}
                              <DetailBlock title="👤 Parent / Guardian">
                                {enrollment.parent_info ? (
                                  <>
                                    <DetailRow k="Full name" v={enrollment.parent_info.full_name} />
                                    <DetailRow k="Relationship" v={enrollment.parent_info.relationship} />
                                    <DetailRow k="Email" v={enrollment.parent_info.email} />
                                    <DetailRow k="Phone" v={enrollment.parent_info.phone} />
                                    <DetailRow k="Address" v={[
                                      enrollment.parent_info.address_line1,
                                      enrollment.parent_info.address_line2,
                                      enrollment.parent_info.city,
                                      enrollment.parent_info.postcode,
                                    ].filter(Boolean).join(', ')} />
                                  </>
                                ) : <em>(not provided)</em>}
                              </DetailBlock>

                              {/* Emergency contacts */}
                              <DetailBlock title="🚨 Emergency contacts">
                                {(enrollment.emergency_contacts || []).length === 0 ? <em>(none)</em> : (
                                  enrollment.emergency_contacts.map((c, i) => (
                                    <div key={i} style={{ marginBottom: '0.5rem' }}>
                                      <strong>{c.full_name || '—'}</strong> ({c.relationship || '—'})<br />
                                      <small>{c.phone || '—'}</small>
                                    </div>
                                  ))
                                )}
                              </DetailBlock>

                              {/* Medical & GP */}
                              <DetailBlock title="🏥 Medical">
                                {enrollment.medical_info ? (
                                  <>
                                    <DetailRow k="GP name" v={enrollment.medical_info.gp_name} />
                                    <DetailRow k="GP phone" v={enrollment.medical_info.gp_phone} />
                                    <DetailRow k="GP surgery" v={enrollment.medical_info.gp_surgery} />
                                    <DetailRow k="NHS number" v={enrollment.medical_info.nhs_number} />
                                    <DetailRow k="Allergies" v={(enrollment.medical_info.allergies || []).join(', ')} />
                                    <DetailRow k="Medications" v={enrollment.medical_info.medications} />
                                    <DetailRow k="Conditions" v={enrollment.medical_info.medical_conditions} />
                                    <DetailRow k="Immunisations up to date" v={enrollment.medical_info.immunizations_up_to_date === true ? 'Yes' : enrollment.medical_info.immunizations_up_to_date === false ? 'No' : '—'} />
                                  </>
                                ) : <em>(not provided)</em>}
                              </DetailBlock>

                              {/* Dietary */}
                              <DetailBlock title="🍽️ Dietary">
                                {(() => {
                                  const d = enrollment.child_dietary_requirements || enrollment.dietary_requirements;
                                  if (!d) return <em>(none)</em>;
                                  return (
                                    <>
                                      <DetailRow k="Diet" v={d.diet_type || (Array.isArray(d) ? d.join(', ') : null)} />
                                      <DetailRow k="Allergies" v={(d.allergies || []).join(', ')} />
                                      <DetailRow k="Notes" v={d.notes} />
                                    </>
                                  );
                                })()}
                              </DetailBlock>

                              {/* SEND */}
                              <DetailBlock title="🌟 Special Educational Needs">
                                {(() => {
                                  const s = enrollment.child_special_needs || enrollment.special_needs;
                                  if (!s) return <em>(none)</em>;
                                  return (
                                    <>
                                      <DetailRow k="Has SEND" v={s.has_sen === true ? 'Yes' : s.has_sen === false ? 'No' : '—'} />
                                      <DetailRow k="Categories" v={(s.categories || []).join(', ')} />
                                      <DetailRow k="EHCP" v={s.has_ehcp === true ? 'Yes' : s.has_ehcp === false ? 'No' : '—'} />
                                      <DetailRow k="Notes" v={s.notes} />
                                    </>
                                  );
                                })()}
                              </DetailBlock>

                              {/* Authorisations & T&C */}
                              <DetailBlock title="✍️ Authorisations & T&Cs">
                                <DetailRow k="Photo consent" v={enrollment.photo_consent === true ? 'Yes' : enrollment.photo_consent === false ? 'No' : '—'} />
                                <DetailRow k="Outing consent" v={enrollment.outing_consent === true ? 'Yes' : enrollment.outing_consent === false ? 'No' : '—'} />
                                <DetailRow k="Emergency medical consent" v={enrollment.emergency_medical_consent === true ? 'Yes' : enrollment.emergency_medical_consent === false ? 'No' : '—'} />
                                <DetailRow k="T&C accepted" v={enrollment.terms_accepted ? 'Yes' : 'No'} />
                                <DetailRow k="T&C version signed" v={enrollment.terms_snapshot_version || enrollment.terms_version} />
                                <DetailRow k="T&C accepted at" v={enrollment.terms_accepted_at ? new Date(enrollment.terms_accepted_at).toLocaleString() : '—'} />
                                <DetailRow k="Signature on file" v={enrollment.signature_data_url || enrollment.signature_image_url ? 'Yes — see printable PDF' : 'No'} />
                              </DetailBlock>
                            </div>
                          )}
                        </div>

                        {(enrollment.status === 'submitted' || enrollment.status === 'under_review') && (
                          <div style={{ display: 'flex', gap: '0.5rem', marginLeft: '1rem' }}>
                            <button
                              className="btn btn-primary"
                              onClick={async () => {
                                const notes = prompt('Admin notes (optional):');
                                try {
                                  await api.reviewEnrollment(enrollment._id || enrollment.id, {
                                    status: 'approved',
                                    admin_notes: notes || ''
                                  });
                                  alert('Enrollment approved! Child record created.');
                                  loadData();
                                } catch (err) {
                                  alert('Error: ' + err.message);
                                }
                              }}
                              data-testid="approve-enrollment-btn"
                            >
                              ✓ Approve
                            </button>
                            <button
                              className="btn btn-secondary"
                              onClick={async () => {
                                const reason = prompt('Rejection reason (required):');
                                if (!reason) return;
                                
                                try {
                                  await api.reviewEnrollment(enrollment._id || enrollment.id, {
                                    status: 'rejected',
                                    rejection_reason: reason
                                  });
                                  alert('Enrollment rejected.');
                                  loadData();
                                } catch (err) {
                                  alert('Error: ' + err.message);
                                }
                              }}
                              data-testid="reject-enrollment-btn"
                            >
                              ✗ Reject
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Expandable Details */}
                      <details style={{ marginTop: '1rem' }}>
                        <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>View Full Details</summary>
                        <div style={{ 
                          marginTop: '1rem', 
                          padding: '1rem', 
                          background: 'hsl(var(--muted))',
                          borderRadius: '6px'
                        }}>
                          {/* OFSTED Consents Section */}
                          {enrollment.ofsted_consents && (
                            <div style={{ marginBottom: '1.5rem' }}>
                              <h4 style={{ marginBottom: '1rem' }}>OFSTED Consents</h4>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '0.5rem' }}>
                                {Object.entries(enrollment.ofsted_consents).map(([key, consent]) => (
                                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <span>{consent.granted ? '✅' : '❌'}</span>
                                    <span style={{ fontSize: '0.875rem' }}>
                                      {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Parent Signature */}
                          {enrollment.parent_signature && (
                            <div style={{ 
                              marginBottom: '1.5rem',
                              padding: '1rem',
                              background: 'white',
                              border: '2px solid hsl(var(--border))',
                              borderRadius: '6px'
                            }}>
                              <h4 style={{ marginBottom: '0.5rem' }}>Parent Signature</h4>
                              <div style={{ fontFamily: 'cursive', fontSize: '1.5rem', marginBottom: '0.5rem' }}>
                                {enrollment.parent_signature.full_name}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                                Signed: {new Date(enrollment.parent_signature.signed_at).toLocaleString()}<br />
                                Type: {enrollment.parent_signature.signature_type}
                              </div>
                            </div>
                          )}

                          {enrollment.medical_info && (
                            <div style={{ marginBottom: '1rem' }}>
                              <h4>Medical Information</h4>
                              {enrollment.medical_info.allergies?.length > 0 && (
                                <p><strong>Allergies:</strong> {enrollment.medical_info.allergies.join(', ')}</p>
                              )}
                              {enrollment.medical_info.dietary_restrictions?.length > 0 && (
                                <p><strong>Dietary:</strong> {enrollment.medical_info.dietary_restrictions.join(', ')}</p>
                              )}
                              {enrollment.medical_info.medications?.length > 0 && (
                                <p><strong>Medications:</strong> {enrollment.medical_info.medications.join(', ')}</p>
                              )}
                              {enrollment.medical_info.medical_conditions?.length > 0 && (
                                <p><strong>Conditions:</strong> {enrollment.medical_info.medical_conditions.join(', ')}</p>
                              )}
                              {enrollment.medical_info.doctor_name && (
                                <p><strong>Doctor:</strong> {enrollment.medical_info.doctor_name} ({enrollment.medical_info.doctor_phone})</p>
                              )}
                              {enrollment.medical_info.notes && (
                                <p><strong>Notes:</strong> {enrollment.medical_info.notes}</p>
                              )}
                            </div>
                          )}

                          {enrollment.parent_info?.emergency_contact && (
                            <div style={{ marginBottom: '1rem' }}>
                              <h4>Emergency Contact</h4>
                              <p><strong>Name:</strong> {enrollment.parent_info.emergency_contact.name}</p>
                              <p><strong>Relationship:</strong> {enrollment.parent_info.emergency_contact.relationship}</p>
                              <p><strong>Phone:</strong> {enrollment.parent_info.emergency_contact.phone}</p>
                              {enrollment.parent_info.emergency_contact.can_pickup && (
                                <span className="badge badge-green">Authorized for Pickup</span>
                              )}
                            </div>
                          )}

                          {enrollment.authorized_pickups && enrollment.authorized_pickups.length > 0 && (
                            <div>
                              <h4>Authorized Pickup List</h4>
                              {enrollment.authorized_pickups.map((pickup, i) => (
                                <p key={i}>
                                  {pickup.name} ({pickup.relationship}) - {pickup.phone}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                      </details>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">
                <p>No enrollment applications yet</p>
              </div>
            )}
          </>
        )}

      {/* Create Shift Modal */}
      {showShiftModal && (
        <div className="modal-overlay" onClick={() => setShowShiftModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Schedule Shift</h2>
            <form onSubmit={handleCreateShift} autoComplete="off">
              <div className="form-group">
                <label>Staff Member</label>
                <select
                  required
                  data-testid="shift-staff-id"
                  value={shiftFormState.staff_id}
                  onChange={(e) => setShiftFormState((s) => ({ ...s, staff_id: e.target.value }))}
                >
                  <option value="">-- Select staff --</option>
                  {staffProfiles.map(profile => (
                    <option key={profile._id} value={profile._id}>
                      {profile.user_name}
                      {profile.competencies.includes('first_aider') ? ' 🩹' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Room</label>
                <select
                  required
                  data-testid="shift-room-id"
                  value={shiftFormState.room_id}
                  onChange={(e) => setShiftFormState((s) => ({ ...s, room_id: e.target.value }))}
                >
                  <option value="">-- Select room --</option>
                  {rooms.map(room => (
                    <option key={room._id} value={room._id}>
                      {room.name} ({room.age_group})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Date</label>
                <input
                  type="date" required data-testid="shift-date"
                  value={shiftFormState.date}
                  onChange={(e) => setShiftFormState((s) => ({ ...s, date: e.target.value }))}
                />
              </div>

              <div className="form-group">
                <label>Start Time</label>
                <input
                  type="time" required data-testid="shift-start-time"
                  autoComplete="off"
                  value={shiftFormState.start_time}
                  onChange={(e) => setShiftFormState((s) => ({ ...s, start_time: e.target.value }))}
                />
              </div>

              <div className="form-group">
                <label>End Time</label>
                <input
                  type="time" required data-testid="shift-end-time"
                  autoComplete="off"
                  value={shiftFormState.end_time}
                  onChange={(e) => setShiftFormState((s) => ({ ...s, end_time: e.target.value }))}
                />
              </div>

              <div className="form-group">
                <label>Notes (optional)</label>
                <textarea
                  rows="3" placeholder="Any special instructions..."
                  data-testid="shift-notes"
                  value={shiftFormState.notes}
                  onChange={(e) => setShiftFormState((s) => ({ ...s, notes: e.target.value }))}
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowShiftModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-shift">Schedule Shift</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Center Modal */}
      {showAddCenterModal && (
        <div className="modal-overlay" onClick={() => setShowAddCenterModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="add-center-modal">
            <div className="modal-header">
              <h2>Add New Center / Location</h2>
              <button
                className="close-btn"
                onClick={() => setShowAddCenterModal(false)}
                data-testid="close-center-modal"
              >
                ✕
              </button>
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setOrgSaving(true);
                try {
                  await api.addOrganizationCenter(newCenter);
                  const org = await api.getMyOrganization();
                  setOrganization(org);
                  setNewCenter({
                    name: '', address: '', city: '', postcode: '',
                    phone: '', email: '', capacity: 50,
                    ofsted_number: '', ofsted_registered_manager: '',
                    age_groups: ['0-2', '2-3', '3-5'],
                  });
                  setShowAddCenterModal(false);
                  alert('Center added successfully');
                } catch (err) {
                  alert(`Failed to add center: ${err.message}`);
                } finally {
                  setOrgSaving(false);
                }
              }}
            >
              <div className="form-group">
                <label>Center Name *</label>
                <input
                  type="text"
                  value={newCenter.name}
                  onChange={(e) => setNewCenter({ ...newCenter, name: e.target.value })}
                  required
                  data-testid="new-center-name"
                />
              </div>
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  value={newCenter.address}
                  onChange={(e) => setNewCenter({ ...newCenter, address: e.target.value })}
                  data-testid="new-center-address"
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={newCenter.city}
                    onChange={(e) => setNewCenter({ ...newCenter, city: e.target.value })}
                    data-testid="new-center-city"
                  />
                </div>
                <div className="form-group">
                  <label>Postcode</label>
                  <input
                    type="text"
                    value={newCenter.postcode}
                    onChange={(e) => setNewCenter({ ...newCenter, postcode: e.target.value })}
                    data-testid="new-center-postcode"
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={newCenter.phone}
                    onChange={(e) => setNewCenter({ ...newCenter, phone: e.target.value })}
                    data-testid="new-center-phone"
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={newCenter.email}
                    onChange={(e) => setNewCenter({ ...newCenter, email: e.target.value })}
                    data-testid="new-center-email"
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div className="form-group">
                  <label>Capacity</label>
                  <input
                    type="number"
                    min="1"
                    value={newCenter.capacity}
                    onChange={(e) => setNewCenter({ ...newCenter, capacity: parseInt(e.target.value || '1', 10) })}
                    data-testid="new-center-capacity"
                  />
                </div>
                <div className="form-group">
                  <label>OFSTED Number</label>
                  <input
                    type="text"
                    value={newCenter.ofsted_number}
                    onChange={(e) => setNewCenter({ ...newCenter, ofsted_number: e.target.value })}
                    placeholder="e.g. EY123456"
                    data-testid="new-center-ofsted"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Ofsted Registered Manager</label>
                <input
                  type="text"
                  value={newCenter.ofsted_registered_manager}
                  onChange={(e) => setNewCenter({ ...newCenter, ofsted_registered_manager: e.target.value })}
                  placeholder="Full name of the person named on this centre's Ofsted URN"
                  data-testid="new-center-registered-manager"
                />
                <small style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.75rem' }}>
                  Recorded on each policy that references the Registered Manager. Overrides the org-level value for this centre.
                </small>
              </div>
              <div className="form-group">
                <label>Age Groups Accepted</label>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', padding: '0.5rem', background: 'hsl(var(--background))', borderRadius: 'var(--radius)', border: '1px solid hsl(var(--border))' }}>
                  {AGE_GROUP_OPTIONS.map((ag) => (
                    <label key={ag} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer', fontSize: '0.875rem' }}>
                      <input
                        type="checkbox"
                        checked={newCenter.age_groups.includes(ag)}
                        onChange={(e) => {
                          const set = new Set(newCenter.age_groups);
                          if (e.target.checked) set.add(ag); else set.delete(ag);
                          setNewCenter({ ...newCenter, age_groups: Array.from(set) });
                        }}
                        data-testid={`new-center-age-group-${ag}`}
                      />
                      {formatAgeGroupLabel(ag)}
                    </label>
                  ))}
                </div>
                <small style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.75rem' }}>
                  Per-centre age ranges override org-level defaults on the Welcome Page and booking eligibility.
                </small>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowAddCenterModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={orgSaving} data-testid="submit-new-center">
                  {orgSaving ? 'Adding...' : 'Add Center'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Children-in-this-room panel — opens from Staff Ops → Rooms.
          Shows every child currently assigned to the selected room with
          a quick "Move to..." option per child so the admin can reassign
          without leaving the panel. */}
      {roomDetail && (
        <div className="modal-overlay" onClick={() => setRoomDetail(null)}>
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 560 }}
            data-testid="room-children-panel"
          >
            <div className="modal-header">
              <h2 style={{ margin: 0 }}>🧒 Children in {roomDetail.room.name}</h2>
            </div>
            <p style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.75rem' }}>
              Capacity {roomDetail.room.capacity}.&nbsp;
              {roomDetail.children.length} of {roomDetail.room.capacity} child{roomDetail.children.length === 1 ? '' : 'ren'} assigned.
              {roomDetail.children.length > roomDetail.room.capacity && (
                <strong style={{ color: 'hsl(0 70% 35%)' }}> ⚠️ Over capacity</strong>
              )}
            </p>
            {roomDetail.children.length === 0 ? (
              <div className="empty-state" style={{ padding: '1rem' }}>
                No children are in this room yet. Assign one from the Children tab.
              </div>
            ) : (
              <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                <table style={{ width: '100%', fontSize: '0.9rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid hsl(var(--border))' }}>
                      <th style={{ textAlign: 'left', padding: '0.4rem' }}>Name</th>
                      <th style={{ textAlign: 'left', padding: '0.4rem' }}>Age group</th>
                      <th style={{ textAlign: 'right', padding: '0.4rem' }}>Move to…</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roomDetail.children.map((c) => (
                      <tr key={c._id} style={{ borderBottom: '1px solid hsl(var(--border))' }} data-testid={`room-detail-child-${c._id}`}>
                        <td style={{ padding: '0.4rem' }}><strong>{c.first_name} {c.last_name}</strong></td>
                        <td style={{ padding: '0.4rem' }}>{c.age_group || '—'}</td>
                        <td style={{ padding: '0.4rem', textAlign: 'right' }}>
                          <select
                            value={c.room_id || ''}
                            onChange={async (e) => {
                              const newRoom = e.target.value || null;
                              try {
                                await api.updateChild(c._id, { room_id: newRoom });
                                setChildren((prev) => prev.map((x) =>
                                  x._id === c._id ? { ...x, room_id: newRoom } : x
                                ));
                                setRoomDetail((prev) => prev ? ({
                                  ...prev,
                                  children: prev.children.filter((x) => x._id !== c._id || newRoom === prev.room._id),
                                }) : prev);
                              } catch (err) {
                                alert('Failed to move child: ' + err.message);
                              }
                            }}
                            style={{ fontSize: '0.85rem', padding: '0.2rem 0.4rem' }}
                            data-testid={`room-detail-move-${c._id}`}
                          >
                            <option value="">— Unassigned —</option>
                            {rooms.map((r) => (
                              <option key={r._id} value={r._id}>{r.name}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setRoomDetail(null)}
                data-testid="room-children-panel-close"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Walk-in enrolment helper modal — explains the supported workflow
          for in-person enrolment to the operator and shortcuts them to
          the right next step. Avoids reimplementing the wizard flow on
          the admin side. */}
      {showWalkInHelper && (
        <div className="modal-overlay" onClick={() => setShowWalkInHelper(false)}>
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 540 }}
            data-testid="walkin-enrolment-helper-modal"
          >
            <div className="modal-header">
              <h2>➕ Create enrolment for a walk-in family</h2>
            </div>
            <p style={{ fontSize: '0.9rem' }}>
              Enrolments are signed by the <strong>parent</strong> (it's a legal record), so they
              must complete the wizard themselves — but you can set them up in two minutes:
            </p>
            <ol style={{ fontSize: '0.9rem', paddingLeft: '1.2rem', lineHeight: 1.6 }}>
              <li>
                Switch to the <strong>Users</strong> tab and click <em>+ Create User</em>.
                Set role to <em>Parent</em> and enter the family's email + a temporary
                password.
              </li>
              <li>
                Hand or text the parent the temporary password and your branded login URL
                (Workspace tab → Sharing card).
              </li>
              <li>
                The parent signs in, sees the Enrolment Wizard, fills in child details,
                signs the Terms &amp; Conditions, and submits. The application appears
                here for you to approve.
              </li>
            </ol>
            <p style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>
              Tip: if the parent is sitting with you, log them in on a tablet and complete
              the wizard together — the signature at the end is theirs to give.
            </p>
            <div className="modal-actions">
              <button
                className="btn btn-ghost"
                onClick={() => setShowWalkInHelper(false)}
                data-testid="walkin-helper-cancel-btn"
              >
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowWalkInHelper(false);
                  setActiveTab('users');
                  setEditingUser(null);
                  setShowUserModal(true);
                }}
                data-testid="walkin-helper-create-parent-btn"
              >
                👤 Create parent account now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin "View parent details" drawer — shows children, enrolments
          and recent bookings for the selected user. Triggered from any
          Users-tab row, the Pending-parents card, or any "Parent" column
          link on Children/Bookings tables. */}
      {viewingUser && (
        <UserDetailDrawer
          user={viewingUser}
          currentUser={user}
          onClose={() => setViewingUser(null)}
        />
      )}

      {/* Admin "View child profile" drawer — opened from any row on the
          Children list, Moments cards, or future entry points. Reuses
          the same aggregated profile endpoint the Staff Dashboard uses. */}
      {viewingChildId && (
        <ChildProfileDrawer
          open={!!viewingChildId}
          childId={viewingChildId}
          onClose={() => setViewingChildId(null)}
        />
      )}

      {/* Upgrade modal — shown when any create-resource API call returns
          a 402 PLAN_LIMIT_REACHED, or when an Upgrade button is clicked. */}
      {upgradeDetail && (
        <UpgradeModal
          detail={upgradeDetail}
          onClose={() => setUpgradeDetail(null)}
        />
      )}

      {/* Staff ID badge modal — opens from sidebar "🪪 My ID Card". */}
      <StaffIDCardModal
        open={showStaffIdModal}
        onClose={() => setShowStaffIdModal(false)}
      />

      {/* Admin-view staff ID modal — opens from the Users tab "🎫 View ID"
          row action. Loads the target staff member's badge via the
          admin-scoped endpoints; photo upload UI is auto-hidden inside
          the modal when userId is set. */}
      <StaffIDCardModal
        open={!!viewingStaffIdFor}
        onClose={() => setViewingStaffIdFor(null)}
        userId={viewingStaffIdFor?.userId || null}
        targetLabel={viewingStaffIdFor?.label || null}
      />

      {/* Global MessageComposer — opened by row-level Message buttons.
          Slot ID lookup is keyed by composerCtx so the same dialog
          serves Daily Roster, Bookings, Child modal, and "compose" FAB. */}
      <MessageComposer
        open={!!composerCtx}
        onClose={() => setComposerCtx(null)}
        user={user}
        parentUserId={composerCtx?.parentUserId || null}
        childId={composerCtx?.childId || null}
        childName={composerCtx?.childName || null}
        parentName={composerCtx?.parentName || null}
        onSent={() => {
          setComposerCtx(null);
          // After sending, refresh the unread badge so any direct response
          // from a parent surfaces quickly.
          (async () => {
            try {
              const list = await api.getCommunications({ unread_only: true });
              setAdminUnreadMessages(Array.isArray(list)
                ? list.filter((m) => m.sent_by_user_id !== user?._id).length
                : 0);
            } catch (_) { /* non-fatal */ }
          })();
        }}
      />

      {/* Force-aware delete dialog for the Children tab — replaces the
          plain RowActions Delete to expose the cascade option safely. */}
      <DeleteChildDialog
        open={!!deletingChild}
        child={deletingChild}
        user={user}
        onClose={() => setDeletingChild(null)}
        onDeleted={async ({ child, mode, response }) => {
          // Reload first so the list reflects the new state immediately,
          // THEN surface the toast — this prevents the user from seeing
          // a stale row blink before the data refresh kicks in.
          try { await loadData(); } catch (_) { /* non-fatal */ }
          const childName = `${child?.first_name || ''} ${child?.last_name || ''}`.trim() || 'Child';
          if (mode === 'force' && response?.deleted) {
            const total = Object.values(response.deleted).reduce((a, b) => a + b, 0);
            toast.success(`${childName} permanently removed`, {
              description: `Wiped ${total} record${total === 1 ? '' : 's'} (including the child).`,
              duration: 6000,
            });
          } else if (mode === 'archive') {
            toast.success(`${childName} archived`, {
              description: 'Hidden from active lists. An admin can restore them at any time.',
              duration: 5000,
            });
          }
        }}
      />

      {/* Role tier promotion / demotion modal — accessible from any
          row's "🎚️ Role" button on the Users tab. */}
      <RoleTierModal
        open={!!roleTierUser}
        user={roleTierUser}
        currentUser={user}
        rooms={rooms}
        onClose={() => setRoleTierUser(null)}
        onSaved={() => loadData()}
      />

      {/* Password admin modal — opened from Users tab "🔑 Password"
          button. Two flows in one dialog: set a specific value (direct,
          no email) OR reset & email a temp password that must be
          rotated on next login. */}
      <PasswordAdminModal
        open={!!passwordAdminUser}
        target={passwordAdminUser}
        onClose={() => setPasswordAdminUser(null)}
      />

      {/* Driver transport assignments slide-in panel — opened from the
          Staff Profiles "🚐 Transport" button on driver rows. */}
      <DriverAssignmentsDrawer
        open={!!driverDrawer}
        driverId={driverDrawer?.driverId}
        driverName={driverDrawer?.driverName}
        onClose={() => setDriverDrawer(null)}
      />

      {/* Bulk-reprice tool — fixes prices set under stale tier rules. */}
      <RepriceBookingsDialog
        open={showRepriceDialog}
        onClose={() => { setShowRepriceDialog(false); if (activeTab === 'bookings') loadData(); }}
      />

      {/* Floating clock + register quick-access — visible across every
          admin page for staff who also work on the floor (deputy
          managers, room leaders, working owners). The FAB self-hides
          when the user has no staff_profile (e.g. desk-only org owners),
          so it never appears for people who don't need it.
          Uses /api/clock-events/my-status (48h window) so an overtime
          shift crossing midnight still shows the correct state. */}
      <ClockInOutFAB
        activeTab={activeTab}
        onNavigate={(tab) => {
          // Admin dashboard's daily-roster tab is keyed 'roster' to
          // match StaffDashboard, so the FAB's onNavigate routes
          // cleanly without any tab-name translation.
          if (tab === 'roster') setActiveTab('roster');
        }}
        showRegister={true}
      />

      {/* Payslip generator — mounted at the dashboard root so it
          survives the staff-edit modal closing. Opens via the
          "💷 Generate payslip…" button on the staff profile editor. */}
      <PayslipDialog
        open={!!payslipDialogFor}
        onClose={() => setPayslipDialogFor(null)}
        staffId={payslipDialogFor?._id}
        staffName={payslipDialogFor?.user_name}
        defaultRate={payslipDialogFor?.hourly_rate}
        employmentType={payslipDialogFor?.employment_type || 'employee'}
      />

    </div>
  );
}

/**
 * Small layout helpers used by the Enrolments tab "View full details"
 * expansion. Kept close to the dashboard so future field additions are
 * trivial — just add a <DetailRow /> inside the relevant <DetailBlock />.
 */
const DetailBlock = ({ title, children }) => (
  <div
    style={{
      padding: '0.75rem',
      background: 'hsl(var(--background))',
      border: '1px solid hsl(var(--border))',
      borderRadius: 6,
    }}
  >
    <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>{title}</div>
    {children}
  </div>
);

const DetailRow = ({ k, v }) => {
  if (v === undefined || v === null || v === '' || v === '—') return null;
  return (
    <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.85rem', padding: '0.15rem 0' }}>
      <div style={{ minWidth: 110, color: 'hsl(var(--muted-foreground))' }}>{k}</div>
      <div style={{ flex: 1, wordBreak: 'break-word' }}>{String(v)}</div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// PaymentPlanEditor — inline card inside the Edit Child modal.
// ---------------------------------------------------------------------------
// Lets an admin update `payment_plan` (Pay as you go / Monthly term-time /
// Monthly full year) and `billing_terms` (Standard / Invoice) on a child
// AFTER enrolment. Not part of the main "Edit Child" <form> so it can
// save independently (uses PUT /api/children/{id}/payment-plan which
// writes an audit row).
//
// Monthly plans are disabled when the child is enrolled ONLY for the
// Holiday Club service — matches the parent-facing wizard rule.
// ---------------------------------------------------------------------------
const PAYMENT_PLAN_OPTIONS = [
  { key: 'pay_as_you_go', label: 'Pay as you go', hint: 'Per-session / block bookings' },
  { key: 'monthly_term_time', label: 'Monthly — term-time (11 pmts)', hint: '39 weeks, Sept → July' },
  { key: 'monthly_full_year', label: 'Monthly — full year (12 pmts)', hint: '52 weeks, Sept → Aug' },
];
const BILLING_TERMS_OPTIONS = [
  { key: 'standard', label: 'Standard', hint: 'Parent self-pays via app' },
  { key: 'invoice', label: 'Invoice / contractual', hint: 'Org invoices family; paid externally' },
];

function PaymentPlanEditor({ child, onSaved }) {
  const [plan, setPlan] = useState(child.payment_plan || 'pay_as_you_go');
  const [terms, setTerms] = useState(child.billing_terms || 'standard');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Sync when parent switches which child is being edited.
  useEffect(() => {
    setPlan(child.payment_plan || 'pay_as_you_go');
    setTerms(child.billing_terms || 'standard');
    setNotes('');
    setError('');
    setSuccess(false);
  }, [child._id, child.payment_plan, child.billing_terms]);

  const services = child.services_required || [];
  const monthlyBlocked = services.length === 1 && services[0] === 'holiday_club';
  const dirty = plan !== (child.payment_plan || 'pay_as_you_go')
    || terms !== (child.billing_terms || 'standard')
    || notes.trim();

  const save = async () => {
    setSaving(true);
    setError('');
    setSuccess(false);
    try {
      const updated = await api.updateChildPaymentPlan(child._id, {
        payment_plan: plan,
        billing_terms: terms,
        notes: notes.trim() || undefined,
      });
      setSuccess(true);
      setNotes('');
      onSaved?.(updated);
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        marginBottom: '1rem',
        padding: '0.85rem 1rem',
        background: 'hsl(210 80% 96%)',
        border: '1px solid hsl(210 80% 82%)',
        borderRadius: 8,
        fontSize: '0.88rem',
      }}
      data-testid="child-payment-plan-editor"
    >
      <div style={{ fontWeight: 600, marginBottom: '0.55rem' }}>💷 Payment plan &amp; billing terms</div>

      {monthlyBlocked && (
        <div style={{ fontSize: '0.78rem', color: 'hsl(38 92% 30%)', marginBottom: '0.5rem' }}>
          ⚠️ This child is enrolled for Holiday Club only — monthly plans are not applicable, use Pay as you go.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', marginBottom: '0.5rem' }}>
        <div>
          <label style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))', fontWeight: 600 }}>Payment plan</label>
          <select
            value={plan}
            onChange={(e) => setPlan(e.target.value)}
            data-testid="edit-payment-plan-select"
            style={{ width: '100%', marginTop: 4 }}
          >
            {PAYMENT_PLAN_OPTIONS.map(opt => (
              <option
                key={opt.key}
                value={opt.key}
                disabled={monthlyBlocked && opt.key.startsWith('monthly_')}
              >
                {opt.label} — {opt.hint}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))', fontWeight: 600 }}>Billing terms</label>
          <select
            value={terms}
            onChange={(e) => setTerms(e.target.value)}
            data-testid="edit-billing-terms-select"
            style={{ width: '100%', marginTop: 4 }}
          >
            {BILLING_TERMS_OPTIONS.map(opt => (
              <option key={opt.key} value={opt.key}>
                {opt.label} — {opt.hint}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ marginBottom: '0.5rem' }}>
        <label style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))', fontWeight: 600 }}>
          Note (optional — kept in audit trail)
        </label>
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. switched to monthly after chat with parent"
          data-testid="edit-payment-plan-notes"
          style={{ width: '100%', marginTop: 4 }}
        />
      </div>

      {error && (
        <div style={{ padding: '0.4rem 0.6rem', background: '#fee2e2', color: '#991b1b', borderRadius: 4, marginBottom: '0.5rem', fontSize: '0.78rem' }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ padding: '0.4rem 0.6rem', background: '#d1fae5', color: '#065f46', borderRadius: 4, marginBottom: '0.5rem', fontSize: '0.78rem' }}>
          ✅ Payment plan updated.
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={save}
          disabled={saving || !dirty}
          data-testid="edit-payment-plan-save"
          style={{ fontSize: '0.85rem' }}
        >
          {saving ? 'Saving…' : 'Save payment plan'}
        </button>
        {child.payment_plan_updated_at && (
          <small style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.75rem' }}>
            Last changed {new Date(child.payment_plan_updated_at).toLocaleDateString()}
          </small>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
